import posthog from "posthog-js";
import type { BeforeSendFn } from "posthog-js";
import { getStoredUtm } from "./utm";
import { POSTHOG_PROXY_PATH, resolvePostHogHosts } from "./posthog/hosts";
import { stripInAppBrowserConsoleNoise } from "./observability/session-replay-console-noise";
import {
  applyErrorTracking,
  buildExceptionEnvelope,
  type ExceptionContext,
} from "./observability/posthog-error-tracking";
import { PANDA_SHELL_ORIGIN } from "./observability/error-origin";
import { projectIdFromPathname, routeTagFor } from "./observability/route-tag";
import { isErrorTrackingDrillRoute } from "./observability/error-tracking-drill";
import { VISUAL_EDITOR_VERSION } from "./canvas/version";
import type { VisualEditorActionType } from "./canvas/edit-action";
import type { IssueKind } from "@/lib/ai/active-issue";
import type { PreviewApplyFailure } from "@/lib/sandbox/apply-failure";
import {
  TRIAL_MARKER,
  type TrialSurface,
  type TrialSurveySource,
} from "@/lib/trial/config";
import {
  firstBuildOfferTimingProps,
  type FirstBuildOfferSuppression,
  type FirstBuildOfferTiming,
} from "@/lib/trial/first-build-offer";
import type {
  StripeHelpTopic,
  StripeSetupPath,
  StripeSetupSurface,
} from "@/lib/stripe-setup-guidance";
import {
  ONBOARDING_TOTAL_STEPS,
  onboardingAnalyticsValue,
  onboardingOptionLabel,
  type OnboardingQuestionKey,
} from "./onboarding/personalization";
import type { OnboardingBlockedReason } from "./onboarding/entry-point";
import type { InlinePublishCtaVariant } from "./publish/inline-cta-experiment";

let initialized = false;

/**
 * Grace period before we decide the session recorder failed to start. The
 * recorder only boots after the `/flags` response + the lazily-loaded recorder
 * bundle, so we wait well past a normal cold start before reporting.
 */
const RECORDER_START_GRACE_MS = 15_000;

/** One-shot guard so the recorder-start probe reports at most once per load. */
let recorderProbeArmed = false;

/**
 * Observability for the exact failure this fix targets. Because replay is now
 * ingested through the first-party `/ingest` proxy, a recorder that never loads
 * (wrong region/env baked at build time, a blocked asset, the proxy buffering
 * the first `/s/` snapshot batch) no longer throws — it silently truncates the
 * replay ("the first N minutes of this recording can't be shown"). After a
 * generous grace period we check whether recording actually started and, if
 * not, surface it to Sentry under a stable fingerprint so every occurrence
 * collapses into one monitorable issue instead of failing invisibly. It is
 * self-silencing: once recording starts reliably, this never fires.
 */
function reportIfRecorderNeverStarted() {
  if (recorderProbeArmed || typeof window === "undefined") return;
  recorderProbeArmed = true;
  window.setTimeout(() => {
    try {
      if (posthog.sessionRecordingStarted()) return; // recording is healthy
      const status = posthog.get_property("$recording_status");
      // Recording legitimately off for this session (server-side sampling /
      // disabled) is not a failure — don't report it.
      if (status === "sampled" || status === "disabled") return;
      void import("@sentry/nextjs")
        .then((Sentry) => {
          Sentry.captureMessage("PostHog session recording did not start", {
            level: "warning",
            fingerprint: ["posthog-session-recording-not-started"],
            extra: {
              recording_status: status ?? "unknown",
              session_id: posthog.get_session_id(),
              configured_host:
                process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "(default eu)",
            },
          });
        })
        .catch(() => {});
    } catch {
      // Observability must never break the page.
    }
  }, RECORDER_START_GRACE_MS);
}

/**
 * Initialize PostHog. Safe to call multiple times — only initializes once.
 * Gracefully no-ops if NEXT_PUBLIC_POSTHOG_KEY is not set.
 */
export function initAnalytics() {
  if (initialized) return;
  if (typeof window === "undefined") return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  // Ingest through the first-party `/ingest` proxy (rewritten to PostHog in
  // next.config.ts) so the rrweb recorder script and snapshot uploads are
  // same-origin and survive ad blockers — without this, session replays arrive
  // late and truncated ("first N minutes can't be shown"). `ui_host` still
  // points at the real PostHog app so replay deep-links / toolbar resolve.
  const { uiHost } = resolvePostHogHosts(process.env.NEXT_PUBLIC_POSTHOG_HOST);

  posthog.init(key, {
    api_host: POSTHOG_PROXY_PATH,
    ui_host: uiHost,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    session_recording: {
      recordCrossOriginIframes: true,
    },
    // Redact Facebook/Instagram in-app-browser performance-logger spam
    // (`FBNav*`, `iabjs://…`) from replay console capture so the inspector
    // isn't flooded and real product errors (413s, E2B timeouts) stay visible.
    // Fail-safe: leaves every non-$snapshot event and every real console line
    // untouched. See lib/observability/session-replay-console-noise.ts.
    // PostHog Error Tracking (PANDA-1707 follow-up). Unhandled errors and
    // unhandled promise rejections become grouped `$exception` issues, with the
    // triage envelope stamped by `before_send` below.
    //
    // `capture_console_errors` stays OFF deliberately. Panda's own preview
    // pipeline relays the GENERATED app's compile and runtime output through
    // `console.error` on the shell, so turning it on would file every user
    // app's build error as a Panda exception — thousands of issues with no
    // stack trace that can never be resolved, which is precisely the failure
    // mode Error Tracking dedup exists to prevent. Those already have a better
    // home as `pipeline_build_failed` / `preview_boot_timeout` product events.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    before_send: beforeSendAnalytics,
    loaded: reportIfRecorderNeverStarted,
  });

  initialized = true;
}

// ── PostHog Error Tracking ────────────────────────────────────────────────
//
// Sentry remains the symbolication authority (source maps + releases are
// uploaded there at build time). Error Tracking is the PostHog-side view of the
// same crashes: it is where an exception sits next to the person who hit it,
// the replay of them hitting it, and the `publish_failed` / `apply_failed`
// events from the same session — the joins Sentry cannot do.
// See docs/failure-monitoring.md for which signal belongs to which tool.

/**
 * Identity facts captured at identify time so an `$exception` can be scoped
 * without re-reading Supabase from a `before_send` hook (which must stay
 * synchronous and cheap). Null until the user is identified — an anonymous
 * crash is still reported, just without them.
 */
let identifiedEmail: string | null = null;
let identifiedIsInternal: boolean | null = null;

/**
 * The ambient triage envelope for an exception captured right now. Every field
 * is best-effort: this runs inside `before_send`, where throwing would break
 * ingestion, so each lookup is individually guarded by the helpers it calls.
 */
function exceptionContext(): ExceptionContext {
  const pathname =
    typeof window !== "undefined" ? (window.location?.pathname ?? null) : null;
  const session = getSessionContext();
  return {
    environment: analyticsEnvironment(),
    route: routeTagFor(pathname) ?? null,
    project_id: projectIdFromPathname(pathname),
    session_id: session.session_id ?? null,
    session_replay_url: session.session_replay_url ?? null,
    email: identifiedEmail,
    is_internal: identifiedIsInternal,
    // Vercel exposes both to the browser as system env vars, so an issue names
    // the exact deploy it came from — the difference between "this regressed"
    // and "this has always been broken".
    release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null,
    deployment_id: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID ?? null,
    is_qa_drill: isErrorTrackingDrillRoute(routeTagFor(pathname)),
  };
}

/**
 * The single `before_send` posthog-js runs. Two independent stages, in order:
 * scrub in-app-browser console noise out of session-replay snapshots, then
 * apply Error Tracking's noise gate + triage envelope to `$exception` events.
 * Each stage ignores the other's event type, so neither can affect the other.
 */
const beforeSendAnalytics: BeforeSendFn = (cr) =>
  applyErrorTracking(stripInAppBrowserConsoleNoise(cr), exceptionContext);

/**
 * Report a caught exception to PostHog Error Tracking from a known product
 * flow. Use this where the code already knows WHICH phase failed — the
 * `failurePhase` is what turns "TypeError: undefined is not a function" into
 * "publish broke", and it is the property triage groups the manual reports by.
 *
 * This is for exceptions — a thrown error nobody planned for. A handled failure
 * the product has a NAME for (a build that failed to parse, a publish rejected
 * for unresolved imports) stays a product event: it has a `failure_code`, a
 * rate to measure against attempts, and no stack trace. Filing those as issues
 * would create "issues" that can never be resolved. See docs/failure-monitoring.md.
 *
 * Never throws: observability must not be able to break the flow it observes.
 */
export function captureClientException(
  error: unknown,
  opts?: {
    /** The product phase that died — `publish`, `build`, `preview`, `apply`, … */
    failurePhase?: string;
    /** Overrides the project inferred from the URL (background work, modals). */
    projectId?: string | null;
    /** Anything else worth having in the issue. Never secrets. */
    extra?: Record<string, unknown>;
  },
) {
  if (!initialized) return;
  try {
    const envelope = buildExceptionEnvelope(
      exceptionContext(),
      PANDA_SHELL_ORIGIN,
      opts?.failurePhase ?? null,
    );
    posthog.captureException(error, {
      ...envelope,
      ...(opts?.projectId !== undefined
        ? { project_id: opts.projectId }
        : {}),
      ...opts?.extra,
    });
  } catch {
    // Swallowed by design — see the note above.
  }
}

/**
 * Identify the user in PostHog after login/signup.
 *
 * posthog-js auto-merges the anonymous browser distinct_id with the
 * new authenticated id, but only on the first identify call of a
 * session that's still anonymous — if anything (a stale localStorage
 * id, a prior identify) already moved the session off the anon id,
 * the merge silently no-ops and the pre-signup journey is orphaned.
 * We snapshot the distinct_id immediately before identify and follow
 * up with an explicit alias so the link survives those edge cases.
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  // Cache what an `$exception` envelope needs. `buildPostHogPersonProperties`
  // already resolved both, and re-deriving them inside `before_send` would mean
  // an async Supabase read on a synchronous hot path.
  identifiedEmail =
    typeof properties?.email === "string" ? properties.email : null;
  identifiedIsInternal =
    typeof properties?.is_internal === "boolean" ? properties.is_internal : null;
  const previousDistinctId = posthog.get_distinct_id();
  posthog.identify(userId, properties);
  if (previousDistinctId && previousDistinctId !== userId) {
    posthog.alias(userId, previousDistinctId);
  }
}

/**
 * Reset user identity on logout.
 */
export function resetAnalytics() {
  if (!initialized) return;
  // Clear the cached identity too, or the next anonymous session's exceptions
  // would be stamped with the logged-out user's email — and a QA logout would
  // leave `is_internal: true` filtering real traffic off the dashboard.
  identifiedEmail = null;
  identifiedIsInternal = null;
  posthog.reset();
}

/**
 * Logout funnel. Standard logout ends ONLY the current device's session
 * (scope "current_session"); a security action (password reset) or an explicit
 * "log out of all devices" ends every session (scope "all_sessions"). The
 * `scope` property is what tells a normal per-device logout apart from a global
 * one. These MUST be captured BEFORE resetAnalytics() — posthog.reset() wipes
 * the distinct id, so a later capture would be attributed to an anonymous user;
 * signOutCurrentDevice() (lib/auth/logout.ts) enforces that ordering.
 */
export function trackLogout(
  event:
    | "logout_clicked"
    | "logout_completed"
    | "session_invalidated"
    | "global_logout_completed",
  scope: "current_session" | "all_sessions",
  properties?: { error?: string },
) {
  if (!initialized) return;
  posthog.capture(event, { scope, ...properties });
}

// ── Daily credit habit-loop funnel (migration 131) ────────────────────────

const LAST_ACTIVE_LS_KEY = "panda:last-active-date";

function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * Fire when a user lands from a daily credit-reset notification — every CTA in
 * the email and the browser push carries ?src=daily_credit_reset&channel=…
 * Closes the daily_credit_reset_sent → _clicked loop.
 *
 * Mounted app-wide (components/posthog-provider), not per page, because the two
 * channels deep-link to two different destinations: the primary CTA lands on
 * /projects and the "upgrade for more" action on /settings/billing.
 *
 * The EMAIL channel is deliberately not counted here. Resend's click webhook
 * already fires daily_credit_reset_clicked server-side
 * (app/api/webhooks/resend), and counting it again on the landing would double
 * every email click. A push click has no server-side equivalent — it happens
 * inside the service worker, where PostHog does not exist — so this landing is
 * the only place the browser can report it.
 *
 * `daily_credit_email` is the pre-migration-177 src value, still accepted so a
 * click on an email already sitting in someone's inbox is not lost.
 */
export function trackDailyCreditResetClicked() {
  if (!initialized || typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const src = params.get("src");
  if (src !== "daily_credit_reset" && src !== "daily_credit_email") return;

  const channel = params.get("channel") ?? "email";
  if (channel !== "email") {
    posthog.capture("daily_credit_reset_clicked", {
      channel,
      ...getUtmProperties(),
    });
  }

  // Strip the params so a refresh or a back-navigation can't re-fire the event.
  const url = new URL(window.location.href);
  url.searchParams.delete("src");
  url.searchParams.delete("channel");
  window.history.replaceState({}, "", url.toString());
}

/**
 * Fire once per local calendar day when a returning user opens the app on a
 * later day than their last visit — the daily-return signal the credit habit
 * loop optimizes for.
 */
export function trackNextDayReturned() {
  if (!initialized || typeof window === "undefined") return;
  try {
    const today = localDateKey(new Date());
    const prev = window.localStorage.getItem(LAST_ACTIVE_LS_KEY);
    if (prev && prev < today) {
      const days = Math.round(
        (new Date(today).getTime() - new Date(prev).getTime()) / 86_400_000,
      );
      posthog.capture("next_day_returned", { days_since_last_visit: days });
    }
    if (prev !== today) window.localStorage.setItem(LAST_ACTIVE_LS_KEY, today);
  } catch {
    // localStorage unavailable (private mode, etc.) — skip silently.
  }
}

/**
 * Track: credits_reset_message_shown — the proactive inline Build-chat message
 * confirming the user's daily credit allowance has reset and is back
 * (PANDA-811). Fires once per reset window, when the message is injected into
 * the chat (gated so it never fires mid-build session).
 */
export function trackCreditsResetMessageShown(props: {
  projectId: string | null;
  plan: string;
  availableToday: number;
}) {
  if (!initialized) return;
  posthog.capture("credits_reset_message_shown", {
    project_id: props.projectId,
    plan: props.plan,
    available_today: props.availableToday,
    ...getDeviceContext(),
  });
}

/**
 * PANDA-30: the one-time "value realization" message shown in chat right after
 * the user's first successful build, before any publish/improve/pay ask.
 */
export function trackValueRealizationShown(props: {
  projectId: string | null;
  businessType: string;
}) {
  if (!initialized) return;
  posthog.capture("value_realization_message_shown", {
    project_id: props.projectId,
    business_type: props.businessType,
    ...getDeviceContext(),
  });
}

// ── $1 trial offer funnel (PANDA-1500) ──────────────────────────────────
// Distinct event family from the existing upgrade_* / exit_intent_* funnels so
// it doesn't corrupt them. `trigger` is the surface (t1_signup | t2_dismiss |
// t3_exit); getUtmProperties() ties T1 back to the ad click, getDeviceContext()
// records the desktop-only T3 gating. Money truth (started/converted) is
// server-side in lib/posthog/billing-events.ts — these are the intent events.

// Aliased to TrialSurface rather than re-declared. The two unions used to be
// maintained by hand and had already drifted (this one was missing "t4_email"),
// and components/trial/trial-dismiss-fallback.tsx casts a TrialSurface straight
// to a TrialTrigger — a cast that would never have flagged a missed addition.
// One union means a new surface is automatically a legal analytics trigger.
export type TrialTrigger = TrialSurface;

/** Why the credit gate did — or did not — show the $1 trial modal.
 *  Emitted once per credit-gate hit so we can tell from the funnel exactly
 *  which condition selected the surface (PANDA-1613 follow-up: "add logging so
 *  we can tell why the trial modal did or did not trigger"). */
export function trackTrialGateDecision(info: {
  action: string;
  reason?: string;
  plan?: string;
  freeBlocked: boolean;
  isDailyCap: boolean;
  flagEnabled: boolean;
  eligible: boolean;
  offerPresent: boolean;
  firstPreviewPending: boolean;
  /** The first build failed and no usable preview was ever delivered. Carried
   *  alongside firstPreviewPending so the funnel can separate "deferred, the app
   *  is coming" from "deferred, the app never arrived" — the two used to be the
   *  same boolean and were indistinguishable in the data. */
  firstPreviewFailed: boolean;
  decision:
    | "trial"
    | "paywall"
    | "deferred_first_preview"
    | "trial_exhausted"
    | "first_preview_recovery"
    /** The card-first wall (PANDA-2341) — no payment method on file. */
    | "card_first";
}) {
  if (!initialized) return;
  posthog.capture("trial_gate_evaluated", {
    // Paid marker on every trial_* funnel event so the generically-named
    // "trial_offer_*"/"trial_*" family can never be read as a free trial.
    ...TRIAL_MARKER,
    ...info,
    ...getDeviceContext(),
  });
}

/**
 * Extra properties every $1-trial funnel event carries so the copy A/B test is
 * analysable:
 *   • copy_variant — which arm rendered (lib/billing/upgrade-copy-experiment).
 *   • plan_before  — the QA test requires segmenting the funnel by the existing
 *     plan_before property so the trial-eligible and non-eligible cohorts are
 *     never blended into one conversion number. A trial-eligible user is always
 *     on Free, so it defaults to "free" rather than being left undefined —
 *     without it these events can't be joined to the upgrade-modal funnel,
 *     which has carried plan_before all along.
 */
export interface TrialFunnelProps {
  copy_variant?: string;
  plan_before?: string | null;
  /**
   * WHICH affordance produced this exposure (PANDA-1723).
   *
   * `trigger` alone cannot answer it. PANDA-1618 routed ~18 distinct in-app
   * buttons — header, credit chip, publish gate, Stripe button, every feature
   * gate — and every one of them reports `trigger: "t6_upgrade_entry"`. So
   * "which entry point produced the most $1 modal views / dismissals?" was
   * unanswerable no matter how the funnel was built. This is that answer.
   */
  entrypoint?: UpgradeEntryPoint | null;
  /** The broad screen the entry point sits in, mirroring UpgradeEventProps so
   *  the trial funnel and the upgrade funnel slice the same way. */
  entry_surface?: UpgradeSurface | null;
  /** What prompted it — user click vs a gate vs an auto-dispatched timer. */
  trigger_reason?: string | null;
  /** Always "eligible" on a shown/clicked event; carried anyway so the trial
   *  events join cleanly to trial_eligibility_evaluated on one key. */
  eligibility_status?: TrialEligibilityStatus | null;
  project_id?: string | null;
  credits_remaining?: number | null;
  source_ticket?: string | null;
  /**
   * WHICH survey produced a t8_survey exposure (lib/trial/config.ts).
   *
   * `trigger` says "t8_survey" for four genuinely different moments — the
   * build-quality modal, the return-to-chat feedback survey, the pre-publish
   * exit-intent thank-you step and a chat thumbs-up — and `entrypoint` told
   * them apart only on the upgrade family, never on this one. Absent on every
   * non-survey surface, which is correct: there was no survey.
   */
  survey_id?: TrialSurveySource | null;
  /**
   * WHAT the user answered immediately before the offer appeared ("yes",
   * "not-at-all", the exit reason, "thumbs_up"…).
   *
   * The single most useful thing about a survey-sourced trial and the one piece
   * that never survived the hop: a user who said "better than expected" and one
   * who said "not at all what I expected" were producing byte-identical trial
   * rows. Free-form by necessity (each survey has its own answer set), so break
   * funnels down by `survey_id` and read this alongside.
   */
  survey_answer?: string | null;
  /** Finer-grained origin than `entrypoint` — the variant/bucket of the
   *  affordance (e.g. "build_quality_negative"). Mirrors UpgradeEventProps.source
   *  so the trial funnel and the upgrade funnel slice the same way. */
  source?: string | null;
  /** Paid-feature key that prompted the offer, when a feature gate produced it
   *  (lib/features/paid-features.ts). Mirrors UpgradeEventProps.feature_requested. */
  feature_requested?: string | null;
  /**
   * WHEN this exposure was allowed to happen, for the post-first-build timer
   * (lib/trial/first-build-offer.ts).
   *
   * Carried as the whole object rather than five loose properties because it
   * travels through four hops before anything is captured — the project page,
   * openUpgradeEntry, the CustomEvent, then the modal — and a hop that forwards
   * four of five fields is a bug nobody would notice. Flattened once, at
   * capture, by firstBuildOfferTimingProps.
   *
   * Absent on every other trigger, and deliberately so: the rows written before
   * the delay existed carry no timing either, which makes "no variant" the
   * immediate-show cohort and lets the two be compared in one breakdown.
   */
  offer_timing?: FirstBuildOfferTiming | null;
}

function trialFunnelProps(props?: TrialFunnelProps): Record<string, unknown> {
  const merged: Record<string, unknown> = {
    plan_before: props?.plan_before ?? "free",
    ...(props?.copy_variant ? { copy_variant: props.copy_variant } : {}),
    entrypoint: props?.entrypoint ?? null,
    entry_surface: props?.entry_surface ?? null,
    trigger_reason: props?.trigger_reason ?? null,
    eligibility_status: props?.eligibility_status ?? null,
    project_id: props?.project_id ?? null,
    credits_remaining: props?.credits_remaining ?? null,
    source_ticket: props?.source_ticket ?? null,
    // Survey attribution. Null (and therefore deleted below) on every surface
    // that is not a survey — deliberately absent rather than an "n/a" sentinel,
    // matching how every other optional key on this event already behaves.
    survey_id: props?.survey_id ?? null,
    survey_answer: props?.survey_answer ?? null,
    source: props?.source ?? null,
    feature_requested: props?.feature_requested ?? null,
    ...firstBuildOfferTimingProps(props?.offer_timing),
    // The trial funnel was the ONLY billing funnel with no environment tag, so
    // dev/staging QA traffic was indistinguishable from production conversions
    // — which the ticket calls for explicitly ("environment - dev/staging/prod")
    // and which QA needs to verify their own three test accounts.
    environment: analyticsEnvironment(),
  };
  for (const key of Object.keys(merged)) {
    if (merged[key] === null || merged[key] === undefined) delete merged[key];
  }
  return merged;
}

export function trackTrialOfferShown(
  trigger: TrialTrigger,
  props?: TrialFunnelProps,
) {
  if (!initialized) return;
  posthog.capture("trial_offer_shown", {
    ...TRIAL_MARKER,
    trigger,
    ...trialFunnelProps(props),
    ...getUtmProperties(),
    ...getDeviceContext(),
  });
}

export function trackTrialOfferClicked(
  trigger: TrialTrigger,
  props?: TrialFunnelProps,
) {
  if (!initialized) return;
  posthog.capture("trial_offer_clicked", {
    ...TRIAL_MARKER,
    trigger,
    ...trialFunnelProps(props),
    ...getUtmProperties(),
    ...getDeviceContext(),
  });
}

export function trackTrialOfferDismissed(
  trigger: TrialTrigger,
  props?: TrialFunnelProps,
) {
  if (!initialized) return;
  posthog.capture("trial_offer_dismissed", {
    ...TRIAL_MARKER,
    trigger,
    ...trialFunnelProps(props),
    ...getDeviceContext(),
  });
}

/**
 * An offer this app had already decided to make, that the user never saw.
 *
 * The gap this closes: `trial_eligibility_evaluated` is written by the ROUTER,
 * at dispatch time, and its `trial_offer_shown` means "we asked for the modal"
 * — not "the modal opened". Everything downstream of the dispatch (the host's
 * dismissal latch, the post-first-build timer giving up after standing aside
 * for other modals) used to drop the offer with nothing but a console line, so
 * a whole population of users who were offered nothing looked, in the funnel,
 * exactly like a population that had been offered something.
 *
 * `reason` is a FirstBuildOfferSuppression (lib/trial/first-build-offer.ts).
 */
export function trackTrialOfferSuppressed(
  trigger: TrialTrigger,
  props: TrialFunnelProps & { reason: FirstBuildOfferSuppression },
) {
  if (!initialized) return;
  const { reason, ...rest } = props;
  posthog.capture("trial_offer_suppressed", {
    ...TRIAL_MARKER,
    trigger,
    suppression_reason: reason,
    // Said plainly, because this event exists precisely because the other
    // events' `trial_offer_shown` could not be trusted to mean this.
    trial_offer_shown: false,
    ...trialFunnelProps(rest),
    ...getDeviceContext(),
  });
}

export function trackTrialExhaustedShown(props?: {
  /** Trial-pool balance when the gate fired (0 for a genuinely-empty pool). */
  credits_remaining?: number | null;
  /** Credits the blocked action needed to start (the server `min_cost`). */
  required_credits?: number | null;
  /** Server gate reason: "pool_empty" | "insufficient_balance" (etc). */
  gating_reason?: string | null;
  /** Which copy the modal rendered — mirrors the credit state above. */
  variant?: "zero_balance" | "insufficient_balance";
}) {
  if (!initialized) return;
  posthog.capture("trial_exhausted_shown", {
    // Carry the credit state so the "trial says used-all but I had credits" bug
    // is diagnosable from the funnel: balance, the action's estimate, why the
    // gate fired, and which copy variant the user actually saw.
    ...(typeof props?.credits_remaining === "number"
      ? { credits_remaining: props.credits_remaining }
      : {}),
    ...(typeof props?.required_credits === "number"
      ? { required_credits: props.required_credits }
      : {}),
    ...(props?.gating_reason ? { gating_reason: props.gating_reason } : {}),
    ...(props?.variant ? { variant: props.variant } : {}),
    ...TRIAL_MARKER,
    ...getDeviceContext(),
  });
}

/**
 * The trial countdown and the trial's real calendar date disagreed.
 *
 * Fired at most once per status-card mount, and NOT because the card is about
 * to render something wrong — display is derived entirely client-side now
 * (lib/trial/countdown). This measures the residual gap: the server still ships
 * a UTC `Math.ceil(remaining / 1 day)` in `daysLeft`, which reads "1" for a
 * trial's whole final day and is off by one for anyone whose local date differs
 * from UTC's. Carrying the viewer's timezone makes the reported QA case ("Trial
 * ends in 1 day" above "on Aug 10, 2026", where Aug 10 was today) countable
 * rather than anecdotal, and proves the divergence is gone from the UI.
 */
export function trackTrialCountdownMismatch(props: {
  /** The trial end instant both sides were reading (user_billing.trial_ends_at). */
  ends_at: string | null;
  /** What the server's UTC day-count said. */
  server_days_left: number;
  /** Whole local calendar days the viewer's own device reports. */
  client_calendar_days_left: number;
}) {
  if (!initialized) return;
  let timezone: string | null = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    timezone = null;
  }
  posthog.capture("trial_countdown_mismatch", {
    ends_at: props.ends_at,
    server_days_left: props.server_days_left,
    client_calendar_days_left: props.client_calendar_days_left,
    delta_days: props.server_days_left - props.client_calendar_days_left,
    timezone,
    // Minutes the viewer is AHEAD of UTC — the sign convention people expect,
    // opposite to getTimezoneOffset's.
    utc_offset_minutes: -new Date().getTimezoneOffset(),
    ...TRIAL_MARKER,
    ...getDeviceContext(),
  });
}

export function trackTrialConvertClicked() {
  if (!initialized) return;
  posthog.capture("trial_convert_clicked", {
    ...TRIAL_MARKER,
    ...getDeviceContext(),
  });
}

// Fired when a $1-trial user clicks a buy/upgrade action that is blocked during
// the trial (billing-clarity ticket). The client half of the funnel; the guard
// fires the server-side trial_purchase_blocked when the request lands.
export function trackTrialAddCreditsClicked(props?: {
  entry_point?: string | null;
  surface?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("trial_add_credits_clicked", {
    ...TRIAL_MARKER,
    entry_point: props?.entry_point ?? null,
    surface: props?.surface ?? null,
    ...getDeviceContext(),
  });
}

// Early-convert (surface 4b) request funnel — the primary conversion path once
// trial credits are exhausted. clicked → started → succeeded | failed(reason),
// so a stuck/failing "Upgrade and keep building" CTA is observable end-to-end
// instead of vanishing into a silent button reset (the reported bug).
export function trackTrialConvertStarted() {
  if (!initialized) return;
  posthog.capture("trial_convert_started", {
    ...TRIAL_MARKER,
    ...getDeviceContext(),
  });
}

export function trackTrialConvertSucceeded() {
  if (!initialized) return;
  posthog.capture("trial_convert_succeeded", {
    ...TRIAL_MARKER,
    ...getDeviceContext(),
  });
}

/**
 * The user took the card-recovery path after a conversion charge failed — the
 * step between trial_convert_failed and a later trial_convert_succeeded. Without
 * it, a user who fixed their card and converted looks in the funnel like an
 * unrelated second attempt, and the recovery rate for declines/SCA is
 * unmeasurable.
 */
export function trackTrialConvertCardUpdate() {
  if (!initialized) return;
  posthog.capture("trial_convert_card_update", {
    ...TRIAL_MARKER,
    ...getDeviceContext(),
  });
}

export function trackTrialConvertFailed(reason: string, status?: number) {
  if (!initialized) return;
  posthog.capture("trial_convert_failed", {
    // The failure reason (server error code, or "network_error") + HTTP status,
    // so a config gap (e.g. not_configured / http_503) is diagnosable in the
    // funnel without a repro.
    reason,
    ...(typeof status === "number" ? { status } : {}),
    ...TRIAL_MARKER,
    ...getDeviceContext(),
  });
}

/**
 * The user pressed "Start my $1 trial" and did NOT reach Stripe (PANDA-1723).
 *
 * The ticket asks for "errors or blocked checkout attempts". Without this, both
 * failure modes are invisible: the client swallowed every error to keep the
 * modal open for a retry, and a refusal simply returned no redirect URL. So a
 * user who clicked and was refused looked EXACTLY like a user who clicked and
 * changed their mind — `trial_offer_clicked` with no follow-up — and a broken
 * checkout would read as a copy problem, not an outage.
 *
 * Client-side half. The server fires `trial_checkout_blocked` with the
 * authoritative reason; this one also catches what the server never sees (the
 * request never arrived, or the response never came back).
 */
export function trackTrialCheckoutFailed(
  trigger: TrialTrigger,
  props: TrialFunnelProps & {
    /** "network_error" | "no_url" | a server `code` | HTTP status text. */
    reason: string;
    status?: number | null;
  },
) {
  if (!initialized) return;
  const { reason, status, ...funnel } = props;
  posthog.capture("trial_checkout_failed", {
    ...TRIAL_MARKER,
    trigger,
    reason,
    ...(typeof status === "number" ? { status } : {}),
    ...trialFunnelProps(funnel),
    ...getDeviceContext(),
  });
}

/**
 * The $1 checkout PAGE was reached and rendered its payment form.
 *
 * The step between `trial_offer_clicked` and the money, and the one step of the
 * funnel with no event at all on the custom Payment Element transport
 * (isCustomCheckoutEnabled). On the hosted transport the page is Stripe's, so
 * the server's `stripe_checkout_created` stands in for it; on the custom one the
 * page is OURS and emitted nothing, so a click was followed by silence until —
 * and only if — `billing_trial_started` landed minutes later. A user who reached
 * the card form and abandoned it was indistinguishable from one whose checkout
 * never minted at all.
 *
 * `trigger` and the funnel props are recovered from the /checkout URL, so this
 * row breaks down by the same source keys as the click that produced it.
 */
export function trackTrialCheckoutViewed(
  trigger: TrialTrigger,
  props?: TrialFunnelProps & {
    /** Which transport rendered it — "payment_element" today; named so the
     *  hosted leg can be added later without splitting the event. */
    checkout_mode?: string;
  },
) {
  if (!initialized) return;
  const { checkout_mode, ...funnel } = props ?? {};
  posthog.capture("trial_checkout_viewed", {
    ...TRIAL_MARKER,
    trigger,
    ...(checkout_mode ? { checkout_mode } : {}),
    ...trialFunnelProps(funnel),
    ...getUtmProperties(),
    ...getDeviceContext(),
  });
}

// ── Canonical upgrade funnel (shared helper) ───────────────────────────────
//
// One standardized event vocabulary for EVERY previously-untracked upgrade /
// paywall / trial / billing entry point, so Product can build a complete
// impression → click → checkout → activation funnel sliced by `entry_point` /
// `surface` instead of chasing ~150 one-off event names. Only used at the gap
// sites — surfaces that already fire their own events keep those names, so
// existing dashboards are untouched and nothing double-counts.
//
// `is_internal` is NOT set here: it rides along as a PostHog Person property
// (buildPostHogPersonProperties → identifyUser), so any of these events is
// filterable by internal-vs-real traffic without threading the email through
// every call site.

/** Where the upgrade CTA lives — the specific button/affordance. */
export type UpgradeEntryPoint =
  /** A tier CTA on the /pricing page. */
  | "pricing_page_cta"
  | "workspace_header_upgrade"
  | "workspace_mobile_upgrade"
  | "credit_dropdown_request_more"
  | "rated_credits_modal"
  | "low_credits_modal"
  | "domain_upgrade"
  | "publish_app_inactive_upgrade"
  | "publish_remove_branding_upgrade"
  | "post_publish_upgrade"
  | "chat_upgrade_link"
  | "build_feedback_upgrade"
  | "settings_billing_upgrade"
  | "settings_change_plan"
  | "settings_credits_usage_upgrade"
  | "home_business_url_limit_upgrade"
  | "impatience_banner"
  | "annual_upgrade_prompt"
  | "student_discount_banner"
  | "paused_banner"
  // Finish-line credit reframe — the low-credit banner + exhaustion modal CTAs.
  | "low_credit_banner"
  | "credit_exhaustion_modal"
  // Post-build model-tier badge (free user's "Standard — upgrade to Advanced").
  | "model_tier_badge"
  // ── PANDA-1618 routed entry points (PANDA-1723 audit) ───────────────────
  //
  // PANDA-1618 put a trial-eligibility decision in front of ~22 upgrade
  // affordances, but only three of them reported that decision. Everything
  // below was previously untracked or tracked without an entry point at all,
  // so "which button showed the $1 offer" was unanswerable. Each name is the
  // EXACT affordance, per the ticket's `entrypoint` requirement — deliberately
  // not pooled into one "workspace_upgrade" bucket, because the whole question
  // is which of them converts.
  /** The card-first trial wall shown before the first gated action (PANDA-2341). */
  | "card_first_gate"
  /** The preview pane's "Build paused — subscription required" state: a first
   *  build that is prepared, saved and waiting on a payment (PANDA-2398). Its
   *  own name rather than card_first_gate's, because this one is AMBIENT — it
   *  sits on screen after the wall was dismissed, and pooling the two would
   *  make "did dismissing the wall cost us the sale?" unanswerable. */
  | "build_payment_required"
  /** The "Start free trial" control on the in-chat trial-activation notice —
   *  the row a build or edit refused for a missing payment method leaves behind
   *  (PANDA — "trial notice repeats after every prompt"). Its own name rather
   *  than card_first_gate's for the same reason build_payment_required has one:
   *  this is what the user comes back to after dismissing the wall, and pooling
   *  the two would hide whether the notice recovers the dismissals. */
  | "chat_activation_notice"
  /** The credits chip / coin counter (dashboard, workspace and Settings). */
  | "credit_chip"
  /** In-chat credit notice card — "Buy credits" / "Upgrade" / "Come back". */
  | "credit_notice_card"
  /** Free project-count cap reached. */
  | "project_limit_gate"
  /** "Connect Stripe" on a plan that does not include it. */
  | "stripe_connect_gate"
  /** Build-canvas Project Settings upsells (custom domain, remove badge). */
  | "project_settings_upgrade"
  /** Publish dialog's non-domain Pro upsell. */
  | "publish_gate_upgrade"
  /** The X on the "Remix with Panda" preview badge (free user). */
  | "preview_badge_remove"
  /** A gated readiness chip Panda proactively offered mid-build. */
  | "readiness_chip_gate"
  /** The demo-limits pill layered over the preview — the upgrade path at the
   *  point of limitation, where the owner can SEE the demo behaviour. Distinct
   *  from every other gate because it is ambient rather than triggered: it is
   *  viewed far more often than it is clicked, so pooling it with a click-driven
   *  gate would drag that gate's conversion rate down for no reason. */
  | "preview_demo_limit_gate"
  /** The pre-build question gate — fires BEFORE any credit is spent, so its
   *  conversion rate is not comparable to a mid-build chip's. */
  | "prebuild_gate"
  /** Share dialog's "Upgrade to go private". */
  | "share_go_private_upgrade"
  /** Composer model-tier upsell routed through the free credit wall. */
  | "composer_model_tier"
  /** Low-credits modal "Top up". */
  | "low_credits_modal_topup"
  /** The first-build recovery card's billing control ("Upgrade plan" / "Add
   *  credits" / the trial), in chat or the preview pane (PANDA-2504). Its own
   *  name because it is a RETURN visit: the build was stopped earlier, and this
   *  is whether the card brings the owner back into it. */
  | "build_recovery_card"
  // ── Auto-dispatched (no click) — the two surfaces PANDA-1618 introduced ──
  // Separated from every click entry point above because their denominator is
  // impressions the user never asked for; pooling them would flatter or wreck
  // the click-through numbers depending on volume.
  /** Chat message thumbs-up → positive-survey $1 offer (t8_survey). */
  | "chat_message_thumbs_up"
  /** Timer after a project's first successful build (t7_first_build). */
  | "post_first_build_auto"
  /** Post-positive-survey CTA raised by the workspace (t8_survey). */
  | "post_survey_auto"
  /** The /start $1 ad landing (t1_signup / t3_exit). Not a PANDA-1618 route,
   *  but named so the trial funnel's entrypoint breakdown has no "(none)"
   *  bucket quietly holding a real, high-volume surface. */
  | "trial_ad_landing"
  /**
   * The T4 backstop email's CTA, landing on /start?src=trial_email_<step>.
   *
   * Distinct from `trial_ad_landing` even though it lands on the same page:
   * one is paid acquisition and the other is lifecycle re-engagement of someone
   * who already signed up, and pooling them makes the email's own performance
   * unreadable. `t4_email` had been a declared TrialSurface with no code path
   * able to emit it — /start read no query string at all — so the entire email
   * funnel was filed as ad traffic.
   */
  | "trial_backstop_email"
  /**
   * The $1 recovery offer on the pre-publish feedback survey's thank-you step
   * (PANDA-1119's modal). Its own entry point, NOT pooled with
   * `build_feedback_upgrade`: that one follows "did Panda build what you
   * expected?" a few seconds after a build renders, while this one follows a
   * user who was walking away from an unpublished app and typed out what was
   * stopping them. Same trial, opposite ends of the funnel — pooling them would
   * make the only interesting question ("does catching them on the way out
   * convert?") unanswerable.
   */
  | "feedback_survey_trial";

/** The broad screen/context the entry point sits in. */
export type UpgradeSurface =
  | "workspace"
  | "workspace_mobile"
  | "billing_settings"
  | "publish_dialog"
  | "generated_app_preview"
  | "chat"
  | "home"
  /** Dashboard / project list — the credit chip and Settings live here too. */
  | "dashboard"
  /** The /pricing plan-comparison page. Named because four out-of-credits
   *  toasts navigate there, so it is a real upgrade surface and not only a
   *  marketing page. */
  | "pricing";

/**
 * What a nested "Upgrade" affordance tells its parent about itself.
 *
 * PANDA-1618 funnelled a dozen leaf buttons — the chat upgrade link, the
 * model-tier badge, the project-limit CTA, the survey CTAs — into ONE
 * `onUpgradeClick` prop several components up the tree, where the trial routing
 * lives. That erased which button was pressed by the time anything was
 * recorded. This is how a leaf keeps its identity across that hop.
 *
 * Declared here, beside the unions it carries, so every component in the chain
 * refers to one type instead of re-declaring the shape and drifting.
 */
export interface UpgradeClickOptions {
  /** $1-trial exposure surface, when the caller is a trial-specific trigger. */
  surface?: "t6_upgrade_entry" | "t7_first_build" | "t8_survey";
  /** The exact affordance the user pressed. */
  entrypoint?: UpgradeEntryPoint;
  entrySurface?: UpgradeSurface;
  /** What prompted it (user_click, credit_gate, paid_feature_request, …). */
  trigger?: string;
  /**
   * Finer-grained origin than `entrypoint` — the bucket/variant of the same
   * affordance (e.g. "build_quality_negative").
   *
   * `UpgradeEventProps` has carried a `source` field all along and nothing ever
   * populated it, because this options object — the only way a leaf can describe
   * itself across the `onUpgradeClick` hop — had nowhere to put one. The
   * build-quality survey was already computing exactly this value for its own
   * event and throwing it away one line later.
   */
  source?: string;
  /** Paid-feature key that prompted the click (lib/features/paid-features.ts). */
  featureRequested?: string;
  /** Which survey produced this, when the leaf is a survey (t8_survey). */
  surveyId?: TrialSurveySource;
  /** What the user answered in that survey, immediately before the offer. */
  surveyAnswer?: string | null;
  /** The leaf already fired its own `upgrade_entrypoint_clicked`. */
  alreadyTracked?: boolean;
  /** Raised by the app, not clicked — must not count as a click. */
  autoDispatched?: boolean;
}

/**
 * The eligibility decision, as the funnel needs to read it (PANDA-1723).
 *
 * `is_trial_eligible` is a boolean, and a boolean cannot answer the ticket's
 * question "are any entrypoints incorrectly showing the trial to ineligible or
 * PAID users?" — a paid user, a user who already burned their trial, and a
 * free user inside the holdout are all `false`, but they are three completely
 * different product situations and only one of them is a bug.
 *
 * Kept to a SMALL closed set on purpose: this is the funnel-breakdown key, so
 * it must stay stable and low-cardinality. The raw server string rides
 * alongside as `eligibility_reason` for debugging.
 */
export type TrialEligibilityStatus =
  /** Server said yes and handed us an offer to render. */
  | "eligible"
  /** On a paid plan (or mid-trial) — the trial is not applicable. */
  | "paid_user"
  /** Free, but has already used the $1 trial. */
  | "already_used_trial"
  /** Free and refused for any other durable reason: previously paid, expired,
   *  suppressed, holdout, frequency-capped, flag off. */
  | "ineligible"
  /** We could not find out — network error, session not ready, DB blip. NOT a
   *  no: routing a user to full price on an unknown is the PANDA-1500 bug. */
  | "unknown";

/**
 * Map a client eligibility state onto the funnel enum.
 *
 * Lives here rather than in lib/trial/ so that every emitter — the entry
 * client, the modal, the credit gate — derives the property the same way. Two
 * call sites classifying `previously_paid` differently would silently split the
 * funnel.
 *
 * `plan` is consulted only when the server gave no usable reason: a paid user
 * usually never reaches an eligibility read at all (the entry client
 * short-circuits them), so their state is `unknown` with a `free`-looking
 * reason and only the plan tells them apart.
 */
export function trialEligibilityStatus(input: {
  status: "eligible" | "ineligible" | "unknown";
  reason?: string | null;
  plan?: string | null;
  trialActive?: boolean;
}): TrialEligibilityStatus {
  if (input.status === "eligible") return "eligible";
  if (input.trialActive) return "paid_user";
  switch (input.reason) {
    case "trial_used":
      return "already_used_trial";
    case "trial_active":
    case "paid_plan":
      return "paid_user";
    // `previously_paid` is deliberately NOT paid_user. It means a former
    // subscriber who cancelled back to Free — free TODAY. The acceptance
    // criterion this property exists for is "is any entry point showing the
    // trial to a PAID user?", which is an alarm; a churned free user seeing
    // the plan picker is correct behaviour. Folding them together would make
    // that alarm fire on healthy traffic. `eligibility_reason` keeps the
    // distinction for anyone who wants it.
    case "previously_paid":
      return "ineligible";
  }
  const plan = input.plan;
  if (typeof plan === "string" && plan !== "" && plan !== "free") {
    return "paid_user";
  }
  return input.status === "unknown" ? "unknown" : "ineligible";
}

export interface UpgradeEventProps {
  entry_point: UpgradeEntryPoint;
  surface: UpgradeSurface;
  /** Optional finer-grained origin (e.g. which handler/variant fired it). */
  source?: string;
  /** Which survey produced this row, for the t8_survey family (see
   *  TrialFunnelProps.survey_id — same vocabulary, so the upgrade funnel and the
   *  trial funnel break down by one key). Absent on non-survey rows. */
  survey_id?: TrialSurveySource;
  /** What the user answered in that survey, immediately before the offer.
   *  Explicitly null — not absent — when a survey produced the row but the user
   *  skipped the question, so "asked and declined to say" stays distinguishable
   *  from "no survey involved" (upgradeBaseProps drops undefined, keeps null). */
  survey_answer?: string | null;
  /** What prompted the surface (e.g. "credit_exhausted", "manual"). */
  trigger?: string;
  /** Paid-feature key from lib/features/paid-features.ts, when gating a feature. */
  feature_requested?: string;
  project_id?: string | null;
  plan_before?: string | null;
  plan_after?: string | null;
  credits_before?: number | null;
  credits_after?: number | null;
  /** Was the account trial-eligible at the moment the CTA was clicked? */
  is_trial_eligible?: boolean;
  /** Did this click actually surface the $1 modal? */
  trial_offer_shown?: boolean;
  /**
   * WHICH surface the click resolved to (lib/trial/upgrade-entry-surface.ts).
   *
   * The ticket's analytics requirement is that every entry point reports the
   * modal that was actually shown — $1 trial vs Upgrade-to-Pro vs Add credits
   * vs plan management. `is_trial_eligible` alone cannot answer that: an
   * ineligible free user and a paid user are both "not eligible" but land on
   * completely different screens.
   */
  resolved_surface?:
    /** The card-first wall — a free account with no payment method
     *  (PANDA-2375). Distinct from "trial": different modal, different offer,
     *  and the funnel has to be able to tell them apart during the rollout. */
    | "card_first"
    | "trial"
    | "choose_plan"
    | "add_credits"
    | "plan_management"
    | "grow_plans";
  /**
   * The routing decision in the form the funnel breaks down by (PANDA-1723).
   * Answers "is any entry point showing the $1 offer to a paid or ineligible
   * user?", which `is_trial_eligible` cannot — see TrialEligibilityStatus.
   */
  eligibility_status?: TrialEligibilityStatus;
  /** Raw server reason behind `eligibility_status` (previously_paid, holdout,
   *  frequency_capped, no_user, request_failed…). High-cardinality and for
   *  DEBUGGING — break funnels down by eligibility_status, not by this. */
  eligibility_reason?: string | null;
  /** Which $1-trial exposure surface this click belongs to, when it routed to
   *  the trial (t6_upgrade_entry / t7_first_build / t8_survey). Lets an
   *  upgrade_entrypoint_clicked be joined to the trial_offer_* family. */
  trial_surface?: string | null;
  /** Credits left at click time — the ticket's `credits_remaining`. */
  credits_remaining?: number | null;
  checkout_session_id?: string | null;
  stripe_customer_id?: string | null;
  /** Ticket marker so every route this audit added is selectable as one cohort
   *  ("are any PANDA-1618 routes missing events?") without listing entry
   *  points by hand. Only set on the PANDA-1618-routed paths. */
  source_ticket?: string | null;
  /** A/B variant tag (e.g. finish-line experiment: "control" | "finish_line"). */
  variant?: string;
  /** Upgrade/trial COPY test arm ("control" | "variant"), from
   *  lib/billing/upgrade-copy-experiment.ts. Deliberately its own property
   *  rather than reusing `variant`: the finish-line experiment already owns
   *  that key and both can ride the same event (the exhaustion modal fires
   *  upgrade_modal_shown), so sharing one key would make either readout
   *  unsplittable. */
  copy_variant?: string;
  /** Plain-language work left at click time (finish-line reframe). */
  estimated_changes_left?: number | null;
  /**
   * WHEN an auto-dispatched post-first-build offer was allowed to fire
   * (lib/trial/first-build-offer.ts). Mirrors TrialFunnelProps.offer_timing so
   * the decision row (trial_eligibility_evaluated) and the exposure row
   * (trial_offer_shown) can be joined on timing as well as attribution —
   * otherwise a delayed offer that resolved to something OTHER than the trial
   * would be the one case with no timing at all. Flattened, not nested, by
   * upgradeBaseProps.
   */
  offer_timing?: FirstBuildOfferTiming | null;
}

/** Best-effort deploy environment for client events (server uses VERCEL_ENV). */
function analyticsEnvironment(): string {
  return (
    process.env.NEXT_PUBLIC_VERCEL_ENV ??
    (process.env.NODE_ENV === "production" ? "production" : "development")
  );
}

/**
 * Merge the caller's props with UTM + device context + environment, dropping
 * any `undefined` keys so the event schema stays clean (callers pass only what
 * the site knows).
 */
function upgradeBaseProps(props: UpgradeEventProps): Record<string, unknown> {
  const { offer_timing, ...rest } = props;
  const merged: Record<string, unknown> = {
    ...getUtmProperties(),
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
    ...rest,
    // Flattened rather than passed through as a nested object: PostHog can only
    // break down by top-level properties, and "how long did we wait?" is the
    // whole question this timing exists to answer.
    ...firstBuildOfferTimingProps(offer_timing),
  };
  for (const key of Object.keys(merged)) {
    if (merged[key] === undefined) delete merged[key];
  }
  return merged;
}

/** An upgrade CTA rendered / entered view (impression). */
export function trackUpgradeEntrypointViewed(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("upgrade_entrypoint_viewed", upgradeBaseProps(props));
}

/** User clicked an upgrade CTA. */
export function trackUpgradeEntrypointClicked(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("upgrade_entrypoint_clicked", upgradeBaseProps(props));
}

// ── Payment-issue banner (migration 173) ────────────────────────────────
// The client half of the dunning funnel. The server owns the state changes
// (billing_payment_issue_started / _recovered in lib/posthog/billing-events.ts,
// fired from the Stripe webhook, which is the only authoritative source of
// payment truth); these two only measure whether the product actually told
// anyone, and whether telling them moved them. Kept OUT of the
// upgrade_entrypoint_* family on purpose — this is not an upsell, and folding it
// in would inflate upgrade-funnel numbers with people who already pay us.

/** The payment-issue banner rendered. Once per mount, per surface. */
export function trackPaymentIssueBannerShown(props: { surface: string }) {
  if (!initialized) return;
  posthog.capture("payment_issue_banner_shown", { surface: props.surface });
}

/**
 * The banner's "Update payment method" was clicked.
 *
 * Recovery is NOT measured here: the click only opens billing, and Panda cannot
 * see whether the new card works. billing_payment_issue_recovered fires from the
 * webhook when Stripe's next charge actually succeeds, which is the only moment
 * anyone should count as fixed.
 */
export function trackPaymentIssueFixClicked(props: { surface: string }) {
  if (!initialized) return;
  posthog.capture("payment_issue_fix_clicked", { surface: props.surface });
}

/** An upgrade modal opened. */
export function trackUpgradeModalShown(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("upgrade_modal_shown", upgradeBaseProps(props));
}

/** An upgrade modal closed without converting. */
export function trackUpgradeModalDismissed(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("upgrade_modal_dismissed", upgradeBaseProps(props));
}

/** Primary CTA inside an upgrade modal clicked. */
export function trackUpgradeCtaClicked(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("upgrade_cta_clicked", upgradeBaseProps(props));
}

/** A paywall / hard-gate surface shown. */
export function trackPaywallShown(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("paywall_shown", upgradeBaseProps(props));
}

/** A paywall / hard-gate surface dismissed. */
export function trackPaywallDismissed(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("paywall_dismissed", upgradeBaseProps(props));
}

/**
 * A purchase was blocked by the payments-paused kill switch.
 *
 * The record of a sale we could not take. Fired from
 * `interceptIfPaymentsPaused()` the instant the click is caught — BEFORE the
 * pop-up renders and regardless of whether the buyer then emails support — so
 * every blocked purchase is counted even when they close the pop-up and leave.
 *
 * Its own event rather than a property on `upgrade_cta_clicked`: those clicks
 * still fire (the buttons stay live on purpose, so intent keeps landing in the
 * existing funnels), and this is the separate question of how much revenue the
 * outage is holding up, broken down by what they were trying to buy.
 */
export function trackPaymentsPausedBlocked(props: {
  intent: string;
  surface: string;
  plan?: string | null;
  cadence?: string | null;
  credits?: number | null;
  amountUsd?: number | null;
  label?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("payments_paused_blocked", {
    ...getUtmProperties(),
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
    intent: props.intent,
    surface: props.surface,
    plan: props.plan ?? null,
    cadence: props.cadence ?? null,
    credits: props.credits ?? null,
    amount_usd: props.amountUsd ?? null,
    label: props.label ?? null,
  });
}

/**
 * A blocked purchase was queued for support to put through by hand.
 *
 * The conversion half of `payments_paused_blocked`: that event counts everyone
 * who hit the wall, this one counts everyone who pressed proceed and had the
 * request actually saved. The gap between them is how many buyers we lose at the
 * pop-up itself. `duplicate` marks a repeat click on a request already queued —
 * still a success for the user, but not a second sale to chase.
 */
export function trackPaymentsPausedQueued(props: {
  intent: string;
  surface: string;
  plan?: string | null;
  amountUsd?: number | null;
  duplicate?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("payments_paused_queued", {
    ...getUtmProperties(),
    environment: analyticsEnvironment(),
    intent: props.intent,
    surface: props.surface,
    plan: props.plan ?? null,
    amount_usd: props.amountUsd ?? null,
    duplicate: Boolean(props.duplicate),
  });
}

/**
 * The queue write failed, so the buyer was shown the support-email fallback.
 *
 * Worth its own event because it is the state where we promised nothing and a
 * sale is sitting entirely outside the queue — if this fires at all, someone
 * needs to look at whether migration 172 has actually been applied.
 */
export function trackPaymentsPausedQueueFailed(props: {
  intent: string;
  surface: string;
  status: number;
}) {
  if (!initialized) return;
  posthog.capture("payments_paused_queue_failed", {
    environment: analyticsEnvironment(),
    intent: props.intent,
    surface: props.surface,
    status: props.status,
  });
}

/**
 * A $1-trial routing decision was made at an upgrade entry point (PANDA-1723).
 *
 * Emitted by lib/trial/upgrade-entry-client.ts on EVERY call — eligible or not,
 * trial or not — so the funnel has one row per decision rather than only the
 * happy path. This is the event that answers the ticket's questions, because it
 * is the only place that carries the entry point AND the eligibility result AND
 * the modal that actually opened, together.
 *
 * Its own event name rather than more properties on `upgrade_entrypoint_clicked`
 * for two reasons: the two auto-dispatched surfaces (post-first-build timer,
 * chat thumbs-up) are decisions with no click at all and would otherwise inflate
 * click counts on every existing dashboard, and a decision that resolves to
 * `choose_plan` is a routing FALLBACK we need counted — the ticket asks for
 * ineligible users routed to the regular flow to be "tracked distinctly".
 */
export function trackTrialEligibilityEvaluated(props: UpgradeEventProps) {
  if (!initialized) return;
  posthog.capture("trial_eligibility_evaluated", {
    // Paid marker so this sits in the same selectable family as every other
    // $1-trial event and can never be read as a free-trial funnel.
    ...TRIAL_MARKER,
    ...upgradeBaseProps(props),
  });
}

// ── Finish-line credit reframe ─────────────────────────────────────────────
//
// The low-credit / exhaustion "finish line" experiment. `variant` ("control" |
// "finish_line") rides on every event so top-up conversion, dismissal, and
// publish rate can be sliced A/B. The CTA-click and modal-dismiss moments reuse
// the canonical funnel helpers above (trackUpgradeCtaClicked /
// trackUpgradeModalDismissed) with source = "low_credit_banner" |
// "exhaustion_modal" — only these three impression/outcome events are new.

export interface LowCreditThresholdProps {
  variant: string;
  credits_remaining: number;
  estimated_changes_left: number;
  estimated_prompts_left: number;
  /** 0–100 project-completion signal when one exists; null when it doesn't. */
  project_completion_pct?: number | null;
  project_id?: string | null;
  plan?: string | null;
}

/** User dropped to the low-credit threshold and the nudge surfaced. */
export function trackLowCreditThresholdReached(props: LowCreditThresholdProps) {
  if (!initialized) return;
  posthog.capture("low_credit_threshold_reached", {
    ...props,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

export interface CreditCopyMismatchProps {
  /** Which credit UI rendered the line ("nav_chip", "settings_usage", …). */
  surface: string;
  /** The invariant that broke — see lib/credits/copy-integrity.ts. */
  code: string;
  detail: string;
  plan: string;
  /** What the copy told the user. */
  shown_remaining: number;
  shown_limit: number;
  /** What the ledger-derived view actually held. */
  pool_remaining: number;
  pool_total: number;
  daily_limit: number | null;
  daily_used: number;
  available_today: number;
  /** Credits reserved by an unsettled build at render time (0 when idle). */
  credits_on_hold: number;
  /**
   * Server-reported age (seconds) of the hold behind credits_on_hold; null when
   * idle. The server excludes reservations past HOLD_TTL_MINUTES, so a value at
   * or past the TTL here flags a hold the client should never have been handed
   * — the "on hold with nothing running" regression, caught at render time.
   */
  hold_age_seconds: number | null;
}

/**
 * Rendered credit copy disagreed with the credit view it came from — e.g. a
 * "resets today at 5:34 pm" clause on an account whose reset will add nothing.
 *
 * This should be a flat-zero metric. It is instrumented because the class of
 * bug it covers is silent: the numbers are individually true, the sentence they
 * form is not, and the only other detection channel is a user telling us they
 * waited for credits that never arrived. Any non-zero rate means a credit
 * surface has drifted from formatCreditBadge and is composing its own copy.
 */
export function trackCreditCopyMismatch(props: CreditCopyMismatchProps) {
  if (!initialized) return;
  posthog.capture("credit_copy_ledger_mismatch", {
    ...props,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

export interface CreditValueTranslationProps {
  variant: string;
  estimated_changes_left: number;
  estimated_prompts_left: number;
  credits_remaining: number;
  project_id?: string | null;
}

/** The plain-language "N changes left" translation was shown (Variant B). */
export function trackCreditValueTranslationShown(
  props: CreditValueTranslationProps,
) {
  if (!initialized) return;
  posthog.capture("credit_value_translation_shown", {
    ...props,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

export interface ProjectPublishedAfterTopupProps {
  variant: string;
  project_id: string;
  /** Milliseconds from the credit-block upgrade to a successful publish. */
  time_to_publish_ms: number;
  plan?: string | null;
}

/** A user who hit the credit wall, upgraded, and then published. */
export function trackProjectPublishedAfterTopup(
  props: ProjectPublishedAfterTopupProps,
) {
  if (!initialized) return;
  posthog.capture("project_published_after_topup", {
    ...props,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

/**
 * Returns UTM properties from localStorage to attach to events.
 */
function getUtmProperties(): Record<string, string> {
  const utm = getStoredUtm();
  if (!utm) return {};
  const props: Record<string, string> = {};
  if (utm.utm_source) props.utm_source = utm.utm_source;
  if (utm.utm_medium) props.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) props.utm_campaign = utm.utm_campaign;
  if (utm.utm_term) props.utm_term = utm.utm_term;
  if (utm.utm_content) props.utm_content = utm.utm_content;
  if (utm.referrer) props.referrer = utm.referrer;
  return props;
}

/**
 * Best-effort device / OS / browser context, parsed from the User-Agent.
 *
 * PostHog already auto-captures `$device_type` / `$os` / `$browser` on every
 * event — those remain the authoritative segmentation. These explicit,
 * un-prefixed properties exist only because the onboarding-funnel spec asks
 * for `device_type` / `os` / `browser` by name. Conservative: anything we
 * can't confidently classify falls back to "desktop" / "unknown".
 */
export function getDeviceContext(): {
  device_type: "desktop" | "mobile" | "tablet";
  os: string;
  browser: string;
} {
  if (typeof navigator === "undefined") {
    return { device_type: "desktop", os: "unknown", browser: "unknown" };
  }
  const ua = navigator.userAgent || "";
  const touchPoints =
    typeof navigator.maxTouchPoints === "number" ? navigator.maxTouchPoints : 0;

  // device_type — iPadOS 13+ masquerades as "Macintosh", so a Mac UA with
  // multiple touch points is classified as a tablet.
  const isTablet =
    /iPad/i.test(ua) ||
    (/Android/i.test(ua) && !/Mobile/i.test(ua)) ||
    (/Macintosh/i.test(ua) && touchPoints > 1);
  const isMobile =
    !isTablet && /Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua);
  const device_type: "desktop" | "mobile" | "tablet" = isTablet
    ? "tablet"
    : isMobile
      ? "mobile"
      : "desktop";

  // os — check Android before Linux (Android UAs contain "Linux").
  let os = "unknown";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";

  // browser — order matters: Edge/Opera/Samsung/Chrome-iOS UAs also contain
  // "Chrome"/"Safari", so the specific ones must be matched first.
  let browser = "unknown";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/i.test(ua)) browser = "Opera";
  else if (/SamsungBrowser/i.test(ua)) browser = "Samsung Internet";
  else if (/Firefox\/|FxiOS\//i.test(ua)) browser = "Firefox";
  else if (/CriOS\/|Chrome\//i.test(ua)) browser = "Chrome";
  else if (/Safari\//i.test(ua)) browser = "Safari";

  return { device_type, os, browser };
}

/**
 * The triage envelope every client-side FAILURE event carries, so the failure
 * dashboard (`docs/failure-monitoring.md`) can answer the same questions of any
 * of them without per-event special-casing:
 *
 *  - `environment` — server failure events all stamp this already
 *    (`lib/posthog/*.ts`), client ones did not, so a preview-deploy failure was
 *    indistinguishable from a production one in the same insight. This is the
 *    gap that made "is this real-user impact?" unanswerable.
 *  - `session_id` / `session_replay_url` — jump straight from a row in the
 *    dashboard to the recording of the user hitting it.
 *  - `current_url` / `user_agent` — we run `autocapture: false`, so the
 *    `$current_url` / `$browser` / `$os` enrichment that pageviews get is NOT
 *    attached to custom events (observed null in the publish-failed report).
 *    Capture the raw signals explicitly so each failure event is self-describing.
 *
 * Guarded end to end: returns partial context rather than throwing, because a
 * failure event must never itself fail. `getSessionContext` already drops its
 * keys when the recorder hasn't booted.
 */
/**
 * Network conditions from the Network Information API, for events where "how
 * big was it" is only half the question — a payload that ships in two seconds
 * on wifi can outrun a 45s budget on a congested uplink, and we were recording
 * only the payload.
 *
 * Keys are OMITTED rather than nulled when unavailable, matching the
 * convention used by the boot/load events so percentiles aren't dragged to
 * zero. Unavailable is the common case on iOS: Safari implements no part of
 * this API, so an iPhone reports nothing here at all — absence is not a
 * healthy connection, and any analysis has to treat it as unknown.
 */
export function getConnectionContext(): Record<string, unknown> {
  if (typeof navigator === "undefined") return {};
  const conn = (
    navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
    }
  ).connection;
  if (!conn) return {};
  return {
    ...(typeof conn.effectiveType === "string"
      ? { connection_type: conn.effectiveType }
      : {}),
    ...(typeof conn.downlink === "number"
      ? { connection_downlink_mbps: conn.downlink }
      : {}),
    ...(typeof conn.rtt === "number" ? { connection_rtt_ms: conn.rtt } : {}),
    ...(typeof conn.saveData === "boolean"
      ? { connection_save_data: conn.saveData }
      : {}),
  };
}

function failureContext(): Record<string, unknown> {
  return {
    environment: analyticsEnvironment(),
    ...getSessionContext(),
    current_url: typeof window !== "undefined" ? window.location.href : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  };
}

/**
 * Report a build failure that only becomes known on the client — after
 * server-side generation succeeded — to the authenticated telemetry endpoint,
 * which emits the canonical `pipeline_build_failed`. Covers sandbox creation,
 * preview compilation/boot, and autofix-exhausted states.
 *
 * Fire-and-forget: `keepalive` lets it survive a navigation, and every failure
 * mode is swallowed so telemetry can never break the build/recovery flow. The
 * caller owns dedup by build_attempt_id (a re-render or repeated compile log
 * must not re-post); the server sink dedupes too as a backstop.
 */
export function reportClientBuildFailure(args: {
  buildAttemptId: string | null;
  projectId: string;
  stage:
    | "generation"
    | "parsing"
    | "validation"
    | "persistence"
    | "sandbox"
    | "preview_compile"
    | "finalization";
  subStage?: string | null;
  failureCode: string;
  error?: string | null;
  buildType?: string | null;
  source?: string | null;
  autofixAttempted?: boolean;
  autofixAttemptCount?: number | null;
  /**
   * WHAT the failed work was trying to deliver, in the user's terms — "add
   * detailed animal profile pages", not the prompt the app was built from.
   *
   * The build sink is the one incident source that never captured this, and the
   * omission is what produced the reported recovery: with no `failed_request` on
   * the incident, lib/recovery/repair-prompt.ts falls back to the APP-CREATION
   * prompt, so "Recover my build" told the model to fix "the wildlife atlas" and
   * it duly picked something, changed it, and reported success while the phase
   * that actually failed was never touched.
   */
  failedRequest?: string | null;
  /** Files the failed attempt was working on — the candidate that didn't land.
   *  lib/recovery/scope-check.ts measures a repair against these, and without
   *  them it can only ever return `unscoped`, which blocks nothing. */
  failedPaths?: string[] | null;
}): void {
  try {
    void fetch("/api/telemetry/build-failed", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        build_attempt_id: args.buildAttemptId,
        project_id: args.projectId,
        stage: args.stage,
        sub_stage: args.subStage ?? null,
        failure_code: args.failureCode,
        error: args.error ?? null,
        build_type: args.buildType ?? null,
        source: args.source ?? null,
        autofix_attempted: args.autofixAttempted ?? false,
        autofix_attempt_count: args.autofixAttemptCount ?? null,
        failed_request: args.failedRequest ?? null,
        failed_paths: args.failedPaths ?? null,
        device_type: getDeviceContext().device_type,
      }),
    }).catch(() => {});
  } catch {
    // Telemetry must never break the build flow.
  }
}

/**
 * Report whether this build attempt actually delivered a USABLE preview —
 * `pipeline_preview_delivered`. Only the client can know this, and it is the
 * real first-value-moment signal (`pipeline_build_completed` only means
 * generation finished; a build that completed and then died at the preview
 * counted as a success until this existed — PANDA-1830).
 *
 * Same fire-and-forget `keepalive` contract as {@link reportClientBuildFailure}.
 * Emitted from every settle path, including ones that also report a failure:
 * the two answer different questions. The server sink dedupes per attempt+verdict.
 */
export function reportClientPreviewOutcome(args: {
  buildAttemptId: string | null;
  projectId: string;
  verdict: "delivered" | "deferred" | "failed_terminal" | "failed_transient";
  failureClass?: string | null;
  httpStatus?: number | null;
  appSaved?: boolean;
  msToVerdict?: number | null;
  buildType?: string | null;
  source?: string | null;
}): void {
  try {
    void fetch("/api/telemetry/preview-outcome", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        build_attempt_id: args.buildAttemptId,
        project_id: args.projectId,
        verdict: args.verdict,
        failure_class: args.failureClass ?? null,
        http_status: args.httpStatus ?? null,
        app_saved: args.appSaved ?? false,
        ms_to_verdict: args.msToVerdict ?? null,
        build_type: args.buildType ?? null,
        source: args.source ?? null,
        device_type: getDeviceContext().device_type,
      }),
    }).catch(() => {});
  } catch {
    // Telemetry must never break the build flow.
  }
}

/**
 * Report an explicit user-initiated build cancellation. Same fire-and-forget
 * contract as {@link reportClientBuildFailure}; emits `pipeline_build_cancelled`.
 */
export function reportClientBuildCancelled(args: {
  buildAttemptId: string | null;
  projectId: string;
  stage?: string | null;
  buildType?: string | null;
}): void {
  try {
    void fetch("/api/telemetry/build-cancelled", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        build_attempt_id: args.buildAttemptId,
        project_id: args.projectId,
        stage: args.stage ?? null,
        build_type: args.buildType ?? null,
        device_type: getDeviceContext().device_type,
      }),
    }).catch(() => {});
  } catch {
    // Telemetry must never break the build flow.
  }
}

// ──────────────────────────────────────────────────
// Event tracking functions
// ──────────────────────────────────────────────────

/**
 * Track: User completed signup
 *
 * `source` is the CTA/surface that opened the signup modal (see
 * StartFreeSource in lib/marketing/start-free.ts). It closes the funnel
 * opened by start_free_cta_clicked → signup_modal_opened, so a drop-off rate
 * can be computed per CTA without joining on session.
 */
export function trackSignup(userId: string, email?: string, source?: string) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("user_signed_up", {
    user_id: userId,
    email,
    signup_source: source ?? null,
    ...utm,
    // Device context on the funnel's terminal event: without it a
    // mobile-Safari-only signup failure is invisible in a breakdown.
    ...getDeviceContext(),
  });
}

// ── Post-signup personalization wizard (PANDA-1523) ────────────────────────
//
// The wizard's three answers (style / role / company size) were persisted but
// never tracked, so onboarding completion, drop-off per step, and "does role or
// company size predict activation" were all unanswerable. These events mirror
// the wizard exactly: a view + a selection + a completion per step, plus one
// terminal event that says whether the user answered everything or skipped.
//
// Every capture is wrapped in `captureOnboarding`, which swallows failures —
// onboarding progression must never depend on analytics succeeding.

export type OnboardingSurface = "home" | "project";

interface OnboardingStepContext {
  /** Where the wizard was rendered: the homepage overlay or the project page. */
  surface: OnboardingSurface;
  /** 1-based, so PostHog reads "step 2 of 3". */
  stepIndex: number;
  questionKey: OnboardingQuestionKey;
  email?: string | null;
}

/** Terminal-event answer set, in raw storage values (may be partial). */
export interface OnboardingAnswerSet {
  theme?: string | null;
  role?: string | null;
  companySize?: string | null;
}

/**
 * Analytics must never break the wizard: a PostHog outage, a blocked bundle, or
 * a serialization error has to leave the user's Next/Skip click working.
 */
function captureOnboarding(event: string, props: Record<string, unknown>) {
  if (!initialized) return;
  try {
    posthog.capture(event, props);
  } catch {
    // Intentionally silent — see above.
  }
}

function onboardingSessionId(): string | null {
  try {
    return posthog.get_session_id() ?? null;
  } catch {
    return null;
  }
}

function onboardingUserId(): string | null {
  try {
    // Post-identify this is the Supabase user id (identifyUser above); the
    // wizard only ever renders for an authenticated user.
    return posthog.get_distinct_id() ?? null;
  } catch {
    return null;
  }
}

/** Properties shared by every onboarding event. */
function onboardingBaseProps(args: {
  surface: OnboardingSurface;
  email?: string | null;
  stepIndex?: number;
  questionKey?: OnboardingQuestionKey;
}): Record<string, unknown> {
  return {
    user_id: onboardingUserId(),
    email: args.email ?? null,
    onboarding_step: args.questionKey ?? null,
    question_key: args.questionKey ?? null,
    step_index: args.stepIndex ?? null,
    total_steps: ONBOARDING_TOTAL_STEPS,
    source: "signup_onboarding",
    surface: args.surface,
    environment: analyticsEnvironment(),
    timestamp: new Date().toISOString(),
    session_id: onboardingSessionId(),
    ...getDeviceContext(),
  };
}

/** Normalized answers, flattened onto the terminal events. */
function onboardingAnswerProps(answers: OnboardingAnswerSet): Record<string, unknown> {
  const style = onboardingAnalyticsValue("style_preference", answers.theme);
  const role = onboardingAnalyticsValue("role", answers.role);
  const companySize = onboardingAnalyticsValue("company_size", answers.companySize);
  return {
    style_preference: style,
    role,
    company_size: companySize,
    answered_count: [style, role, companySize].filter(Boolean).length,
  };
}

/** One event name per question, so each step gets its own funnel step. */
const ONBOARDING_SELECTED_EVENTS: Record<OnboardingQuestionKey, string> = {
  style_preference: "onboarding_style_selected",
  role: "onboarding_role_selected",
  company_size: "onboarding_company_size_selected",
};

/**
 * Track: the one-time wizard was NOT opened, because the surface asking was not
 * a legitimate post-signup entry point — the project builder, a load that exists
 * to hand a prompt off into a build, or a live build / repair / publish
 * pipeline. It is DEFERRED, not dismissed — nothing is persisted, so the user is
 * offered it again on their next idle load.
 *
 * Deliberately NOT reported as an impression. Firing `onboarding_step_viewed`
 * here would turn every deferred user into a phantom step-1 drop-off and make
 * the funnel unreadable — the same rule the other `_suppressed` gates in this
 * file follow.
 *
 * `suppressed_reason` carries the machine-readable cause — `active_builder`
 * (the project workspace, which is never an entry point), `pending_build_handoff`
 * (a stashed prompt about to become one), or the live phase that was running —
 * so "how often does onboarding lose to a build, and to what?" is answerable
 * without a code read.
 */
export function trackOnboardingSuppressed(args: {
  surface: OnboardingSurface;
  projectId: string | null;
  reason: OnboardingBlockedReason;
  email?: string | null;
}) {
  captureOnboarding("onboarding_suppressed", {
    ...onboardingBaseProps({ surface: args.surface, email: args.email }),
    project_id: args.projectId,
    suppressed_reason: args.reason,
  });
}

/**
 * Track: the server granted this user their one onboarding offer and we are
 * putting the wizard on screen.
 *
 * Fired from the claim path only (`claimOnboardingOffer` →
 * `POST {"event":"claim"}`), which returns `owed: true` at most once per user
 * ever — so **more than one of these for a single `distinct_id` means the
 * one-time guarantee is broken**, and which load it happened on is right there
 * in the event.
 *
 * That is the whole reason it exists. "Onboarding questions reappear" has now
 * been reported repeatedly without a reproducible sequence, and every previous
 * investigation went looking for the failing path by reading code. This makes
 * the question answerable from data instead: distinct_ids with two or more
 * `onboarding_shown` events, ordered by time.
 *
 * Distinct from `onboarding_step_viewed`, which fires per step per mount and so
 * cannot answer it.
 */
export function trackOnboardingShown(args: {
  surface: OnboardingSurface;
  email?: string | null;
}) {
  captureOnboarding(
    "onboarding_shown",
    onboardingBaseProps({ surface: args.surface, email: args.email }),
  );
}

/**
 * Track: a wizard step came into view. Step 1 doubles as "onboarding started" —
 * the entry point of the funnel. The wizard fires this once per step per mount.
 */
export function trackOnboardingStepViewed(ctx: OnboardingStepContext) {
  captureOnboarding("onboarding_step_viewed", onboardingBaseProps(ctx));
}

/**
 * Track: the user picked an answer. Fires on the pick itself (not on Next), so
 * an answer given and then abandoned is still visible; re-picking the SAME
 * value is not re-reported, changing to a different one is.
 *
 * `selected_value` is the normalized analytics value (`just_me`, `dark_mode`);
 * the raw storage value and display label ride along for cross-referencing the
 * `user_credits.onboarding_*` columns.
 */
export function trackOnboardingAnswerSelected(
  ctx: OnboardingStepContext & { value: string },
) {
  captureOnboarding(ONBOARDING_SELECTED_EVENTS[ctx.questionKey], {
    ...onboardingBaseProps(ctx),
    selected_value: onboardingAnalyticsValue(ctx.questionKey, ctx.value),
    selected_value_raw: ctx.value,
    selected_label: onboardingOptionLabel(ctx.questionKey, ctx.value),
  });
}

/** Track: the user advanced past a step (Next / Continue) with an answer. */
export function trackOnboardingStepCompleted(
  ctx: OnboardingStepContext & { value: string | null },
) {
  captureOnboarding("onboarding_step_completed", {
    ...onboardingBaseProps(ctx),
    selected_value: onboardingAnalyticsValue(ctx.questionKey, ctx.value),
    selected_value_raw: ctx.value ?? null,
  });
}

/**
 * Track: "Skip for now" — deliberately distinct from `onboarding_completed`,
 * even though both mark the wizard done server-side. Carries the step the user
 * bailed on plus whatever they had already answered.
 */
export function trackOnboardingSkipped(
  ctx: OnboardingStepContext & { answers: OnboardingAnswerSet },
) {
  captureOnboarding("onboarding_skipped", {
    ...onboardingBaseProps(ctx),
    ...onboardingAnswerProps(ctx.answers),
    skipped_at_step: ctx.questionKey,
  });
}

/** Track: all steps answered and submitted — the end of the onboarding funnel. */
export function trackOnboardingCompleted(args: {
  surface: OnboardingSurface;
  email?: string | null;
  answers: OnboardingAnswerSet;
}) {
  captureOnboarding("onboarding_completed", {
    ...onboardingBaseProps({ surface: args.surface, email: args.email }),
    ...onboardingAnswerProps(args.answers),
    completed_steps: ONBOARDING_TOTAL_STEPS,
  });
  identifyOnboardingSegment(args.answers);
}

/**
 * Stamp the onboarding answers onto the PERSON, not just the wizard's own
 * events.
 *
 * The wizard funnel was already measurable, but the question the ticket asks —
 * do activation, build satisfaction and conversion differ by segment? — is
 * about events that happen long after the wizard and know nothing about it.
 * Person properties answer that without threading role/company size through
 * every publish, purchase and build event: any existing funnel can simply be
 * broken down by `onboarding_role`.
 *
 * Idempotent and safe to call on every load, which is deliberate — calling it
 * when the stored answers are read back means users who onboarded BEFORE this
 * shipped get segmented too, rather than only new signups.
 */
export function identifyOnboardingSegment(answers: OnboardingAnswerSet) {
  if (!initialized) return;
  const props: Record<string, string> = {};
  if (answers.theme) {
    props.onboarding_style = onboardingAnalyticsValue("style_preference", answers.theme) ?? answers.theme;
  }
  if (answers.role) {
    props.onboarding_role = onboardingAnalyticsValue("role", answers.role) ?? answers.role;
  }
  if (answers.companySize) {
    props.onboarding_company_size =
      onboardingAnalyticsValue("company_size", answers.companySize) ?? answers.companySize;
  }
  if (Object.keys(props).length === 0) return;
  // Same contract as captureOnboarding above: this runs inside the wizard's
  // submit path, so a PostHog outage, a blocked bundle, or an SDK build without
  // setPersonProperties must not throw and take the user's click with it.
  try {
    if (typeof posthog.setPersonProperties === "function") {
      posthog.setPersonProperties(props);
    }
  } catch {
    // Intentionally silent — segmentation is never worth a broken submit.
  }
}

/**
 * Track: User created their first build (project)
 */
export function trackProjectCreated(projectId: string, templateId: string, prompt: string) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("project_created", {
    project_id: projectId,
    template_id: templateId,
    prompt_length: prompt.length,
    prompt_preview: prompt.slice(0, 200),
    ...utm,
  });
}

/**
 * Track: an AI edit/apply turn failed and Panda showed a fallback message
 * (PANDA placement-fallback fix). Captures the structured failure category, the
 * exact fallback copy shown, the underlying error, the request that failed, and
 * whether recovery (retry/repair) was attempted — so we can audit that server /
 * context / credit / unsupported failures get accurate responses.
 *
 * Also carries the request-shape dimensions the copy is now built from
 * (lib/chat/apply-failure-copy.ts), of which `askedToClick` is the one to watch:
 * the message it replaced told every user to "click the exact section" no matter
 * what they had asked for.
 */
export function trackApplyFailed(props: {
  projectId: string;
  category: string;
  fallbackShown: string;
  surface: "edit" | "visual" | "build" | "deploy-fix";
  /**
   * THE join key for one user edit (the client's per-turn `turnId`, also the
   * server's credit-dedupe key). One typed request can emit `pipeline_edit_
   * started` several times and `edit_apply_atomic_reject` in between, and until
   * this existed the only way to group them was project_id + a session window —
   * which cannot tell one turn's retry from the next turn's first attempt. The
   * reported bug was exactly that shape (atomic reject → full-file retry →
   * network drop → apply_failed), and it could not be reconstructed as a single
   * attempt from the events alone.
   */
  turnId?: string | null;
  /**
   * WHICH recovery hop was in flight when the turn finally died —
   * "edit-retry-fullfile", "edit-retry-reduced-context", … (the same surface
   * string the credit gate logs). `retryAttempted` only says a retry happened;
   * this says which one lost the turn, so a drop on the full-file rewrite is
   * separable from one on the first pass.
   */
  retrySurface?: string | null;
  underlyingError?: string | null;
  requestPreview?: string | null;
  retryAttempted?: boolean;
  repairAttempted?: boolean;
  /** Paths of files whose edits did NOT land (missed SEARCH anchors, cap/guard
   *  rejects) — first-class so a query can group by file instead of scraping the
   *  free-form error string, which truncates at 300 chars. */
  failingFiles?: string[];
  /** Generated files dropped before apply, with the exact reason
   *  (unresolved-search-replace-markers, oversized-full-file, …). */
  droppedFiles?: Array<{ path: string; reason: string }>;
  /** Short server request id (from the /api/ai/chat error event) so an HTTP-500
   *  apply failure can be joined to the backend log for the failing route. */
  requestId?: string | null;
  /** What KIND of request failed — content_addition, page_design, element_level,
   *  … (lib/chat/apply-failure-copy.ts RequestShape). The copy is now shaped by
   *  this, so it's the dimension to segment the funnel on. */
  requestShape?: string;
  /** The surface the copy named ("your animals page"), or null when we named
   *  none. Lets us see how often we could attribute a failure to a real screen
   *  rather than asking the user where they meant. */
  inferredTarget?: string | null;
  /**
   * Whether the message told the user to click an element.
   *
   * THE regression metric for the bug this instrumentation was added for: the
   * old copy told everyone to "click the exact section" regardless of what they
   * asked. Tracked as a boolean rather than grepped out of fallback_preview so
   * the acceptance query is trivial —
   *   apply_failed where asked_to_click and request_shape != 'element_level'
   * must be zero.
   */
  askedToClick?: boolean;
  /**
   * Model-visible images on the turn that failed.
   *
   * The dimension that makes the screenshot regression queryable at all: a user
   * who attached a picture of the rows they wanted deleted was told to "click
   * the one you want changed in the preview", and nothing in this event
   * distinguished that turn from one sent with no image. Paired with
   * `askedToClick` it gives the acceptance query directly —
   *   apply_failed where asked_to_click and attached_image_count > 0
   * must be zero — and on its own it answers the broader question of how often
   * a failed edit had visual context we could have used.
   */
  attachedImageCount?: number;
  /** How many starter-set chips were offered alongside the message. */
  optionCount?: number;
  /** True when the diagnosis pass's own specific blocker/question was surfaced
   *  instead of a fallback — i.e. the salvage path did its job. */
  diagnoseBlockerSurfaced?: boolean;
  /**
   * WHICH pipeline stage discarded the candidate — no_edit_output,
   * candidate_patch, validation_reject, verification_reject, no_op_apply
   * (lib/ai/apply-stage.ts).
   *
   * `category` says what the USER was told; this says where the work was
   * actually lost, and they are frequently different. The reported regression
   * was categorised `atomic_reject` while the turn was really lost at
   * verification three passes earlier — segmenting on this is the only way to
   * see that, and it is the acceptance query for grouping failures by stage.
   */
  failureStage?: string | null;
  /** The stage's own verdict — "unreachable-dead-code", "missing:…". */
  failureStageReason?: string | null;
  /**
   * The sandbox write's own failure record, when `failureStage` is
   * `sandbox_write` (lib/sandbox/apply-failure.ts).
   *
   * THE FIELDS THE REPORTED INCIDENT NEEDED AND DID NOT HAVE. "Failed to apply
   * changes: Load failed" reached PostHog as free text on a beacon that named no
   * turn, no status, no chunk and no sandbox — every one of which existed at the
   * throw site and was dropped there. Flattened onto this event rather than
   * given one of its own because a write that never landed IS an apply failure,
   * and splitting it would mean two events to join for one question.
   */
  applyFailure?: PreviewApplyFailure | null;
}) {
  if (!initialized) return;
  posthog.capture("apply_failed", {
    project_id: props.projectId,
    turn_id: props.turnId ?? null,
    retry_surface: props.retrySurface ?? null,
    category: props.category,
    surface: props.surface,
    fallback_preview: props.fallbackShown.slice(0, 140),
    request_shape: props.requestShape ?? null,
    inferred_target: props.inferredTarget ?? null,
    asked_to_click: props.askedToClick ?? false,
    attached_image_count: props.attachedImageCount ?? 0,
    option_count: props.optionCount ?? 0,
    diagnose_blocker_surfaced: props.diagnoseBlockerSurfaced ?? false,
    failure_stage: props.failureStage ?? null,
    failure_stage_reason: props.failureStageReason?.slice(0, 200) ?? null,
    // Null on every failure that is not a sandbox write, so the columns stay
    // meaningful: a populated `apply_http_status` always describes a real write.
    apply_stage: props.applyFailure?.stage ?? null,
    apply_error_name: props.applyFailure?.errorName ?? null,
    apply_error_message: props.applyFailure?.message.slice(0, 300) ?? null,
    apply_http_status: props.applyFailure?.httpStatus ?? null,
    apply_chunk_index: props.applyFailure?.chunkIndex ?? null,
    apply_chunk_count: props.applyFailure?.chunkCount ?? null,
    apply_payload_bytes: props.applyFailure?.payloadBytes ?? null,
    apply_sandbox_id: props.applyFailure?.sandboxId ?? null,
    // Whether the one bounded retry was spent. A rising share of `true` means
    // retrying is no longer covering the transient faults it was added for.
    apply_retried: props.applyFailure?.retried ?? null,
    error_preview: props.underlyingError?.slice(0, 300) ?? null,
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
    retry_attempted: props.retryAttempted ?? false,
    repair_attempted: props.repairAttempted ?? false,
    failing_files: props.failingFiles?.slice(0, 20) ?? null,
    failing_file_count: props.failingFiles?.length ?? 0,
    dropped_files:
      props.droppedFiles?.slice(0, 20).map((d) => `${d.path}:${d.reason}`) ??
      null,
    dropped_file_count: props.droppedFiles?.length ?? 0,
    request_id: props.requestId ?? null,
    ...failureContext(),
    ...getDeviceContext(),
  });
}

/**
 * Diagnostic: a non-scoped SEARCH/REPLACE batch was rejected atomically because
 * at least one required patch missed or a generated file was dropped
 * (PANDA-1364). This fires at DETECTION — BEFORE the full-file recovery/retry
 * runs — so it is deliberately NOT `apply_failed`. Recovery frequently succeeds,
 * and emitting `apply_failed` here (as the code used to) counted those recovered
 * turns as user-visible failures AND double-counted the terminal ones — which is
 * why the `atomic_reject` bucket looked so large. `apply_failed` now counts only
 * turns that actually failed for the user; pair its `category=atomic_reject`
 * slice with this event to read the true atomic-reject rate and recovery rate.
 *
 * `turnId` is what makes that pairing exact rather than approximate: both events
 * now carry the edit's turn id, so a reject and the `apply_failed` (or absence
 * of one) that followed it belong to the same attempt by key, not by guessing
 * from a session window.
 */
export function trackAtomicReject(props: {
  projectId: string;
  surface: "edit" | "visual";
  applied: number;
  patchCount: number;
  missed: number;
  failingFiles?: string[];
  droppedFiles?: Array<{ path: string; reason: string }>;
  requestPreview?: string | null;
  /** The edit turn this reject belongs to — joins to `apply_failed.turn_id`
   *  and the server's `pipeline_edit_*` events for the same attempt. */
  turnId?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_apply_atomic_reject", {
    project_id: props.projectId,
    turn_id: props.turnId ?? null,
    surface: props.surface,
    applied: props.applied,
    patch_count: props.patchCount,
    missed: props.missed,
    failing_files: props.failingFiles?.slice(0, 20) ?? null,
    failing_file_count: props.failingFiles?.length ?? 0,
    dropped_files:
      props.droppedFiles?.slice(0, 20).map((d) => `${d.path}:${d.reason}`) ??
      null,
    dropped_file_count: props.droppedFiles?.length ?? 0,
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
  });
}

/**
 * The model's completion recap named a component or file that this turn did not
 * change, so it was replaced with the deterministic copy (see
 * lib/ai/summary-grounding.ts). A rising rate means edits are drifting off the
 * user's request — the failure mode where Panda claimed it had updated a
 * ProfileView while the user had asked for connector lines on a diagram.
 */
/**
 * A turn claimed to apply a value the user explicitly stated, and the files it
 * changed contain none of them — the false-success half of the
 * instruction-following ticket ("Updated your site-wide styling" over an
 * unchanged cream background).
 *
 * Fired from the landed-check, which also refuses to mount the edit and refunds
 * the turn. Countable here so the failure shows up in PostHog rather than only
 * in a user's screenshot, and so the weekly regression review has a queue to
 * pull from.
 */
export function trackStatedConstraintMissing(props: {
  project_id: string;
  /** Which kind of stated value went missing. "color" today. */
  kind: string;
  /** The values the turn was expected to honour. */
  stated: string;
  changed_paths: string;
  /** The request that was supposed to apply them. */
  message: string;
}) {
  if (!initialized) return;
  posthog.capture("stated_constraint_missing", {
    project_id: props.project_id,
    kind: props.kind,
    stated: props.stated.slice(0, 200),
    changed_paths: props.changed_paths.slice(0, 400),
    message: props.message.slice(0, 200),
    ...getDeviceContext(),
  });
}

export function trackSummaryDriftSuppressed(props: {
  projectId: string;
  /** What the recap claimed to have changed (capped for the event payload). */
  claimed: string[];
  /** What actually changed this turn. */
  changedFiles: string[];
  requestPreview?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_summary_drift_suppressed", {
    project_id: props.projectId,
    claimed: props.claimed.slice(0, 10),
    claimed_count: props.claimed.length,
    changed_files: props.changedFiles.slice(0, 20),
    changed_file_count: props.changedFiles.length,
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
  });
}

/**
 * A successful edit fell back to the deterministic floor in lib/ai/edit-recap.ts
 * — "Updated your shop page." — because the model's own recap never arrived or
 * was refused on the way in.
 *
 * This is the metric for the post-edit confirmation work. Every copy rule that
 * makes a confirmation specific lives in the PROMPT (CONTINUATION_CLOSE), so it
 * only reaches the user when the model's recap survives; when the floor speaks
 * instead, the user gets a line that names a surface and no outcome, which reads
 * as the generic copy that work replaced. `reason` says which of the three ways
 * we lost it, so a rising rate points at a cause instead of at the copy:
 *   - "no-summary": no <<<SUMMARY>>> block arrived or parsed
 *   - "drifted":    the grounding guard refused it (lib/ai/summary-grounding.ts)
 *   - "jargon":     it leaked implementation detail (lib/ai/jargon-detect.ts)
 */
export function trackEditRecapFloor(props: {
  projectId: string;
  reason: "no-summary" | "drifted" | "jargon";
  /** What actually changed this turn. */
  changedFiles: string[];
  requestPreview?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_recap_floor_used", {
    project_id: props.projectId,
    reason: props.reason,
    changed_files: props.changedFiles.slice(0, 20),
    changed_file_count: props.changedFiles.length,
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
  });
}

/**
 * An edit removed existing user work (lib/validation/content-loss.ts).
 *
 * Fires on EVERY detected removal, including ones the user asked for, so the
 * requested/unrequested split is measurable rather than assumed — the whole
 * question this instruments is how often Panda deletes something nobody asked
 * it to. `requested` is the deterministic removal-intent read of the message.
 */
export function trackContentLossDetected(props: {
  projectId: string | null;
  /** True when the user's own message asked for a removal. */
  requested: boolean;
  /** Removed routes, components and exports, e.g. "component:Testimonials". */
  losses: string[];
  lossCount: number;
  changedFiles: string[];
  requestPreview?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_content_loss_detected", {
    project_id: props.projectId,
    requested: props.requested,
    // The signal we act on: a removal nobody asked for.
    unrequested: !props.requested,
    losses: props.losses.slice(0, 20),
    loss_count: props.lossCount,
    changed_files: props.changedFiles.slice(0, 20),
    changed_file_count: props.changedFiles.length,
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
  });
}

/**
 * Pictures an edit replaced or dropped (lib/validation/asset-loss.ts).
 *
 * The sibling of `trackContentLossDetected`, and it exists because that event
 * could not see this failure at all: a swapped photo removes no name, so the
 * content-loss detector reports a clean turn over a page of replaced images.
 *
 * `unrequested_count` is the number to watch — assets changed on a turn whose
 * request never mentioned a picture. It is the ticket's "out-of-scope changes",
 * measured per turn against what was actually asked for.
 */
export function trackAssetLossDetected(props: {
  projectId: string | null;
  /** True when the user's own message authorised picture work. */
  requested: boolean;
  /** Every picture lost, e.g. "Almond Croissant (lib/data/recipes.ts)". */
  losses: string[];
  lossCount: number;
  /** The subset nobody asked for — what reaches the user as a note. */
  unrequestedCount: number;
  /** Files this turn actually changed. */
  changedFiles: string[];
  requestPreview?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_asset_loss_detected", {
    project_id: props.projectId,
    requested: props.requested,
    losses: props.losses.slice(0, 20),
    loss_count: props.lossCount,
    unrequested_count: props.unrequestedCount,
    // The signal we act on: a picture changed on a turn that never mentioned one.
    out_of_scope: props.unrequestedCount > 0,
    changed_files: props.changedFiles.slice(0, 20),
    changed_file_count: props.changedFiles.length,
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
  });
}

/**
 * A pre-commit scope-drift repair round (lib/validation/scope-drift.ts).
 *
 * The number that matters is `surviving`: an edit reached the gate having
 * deleted work nobody asked for, and this says whether the user ended up
 * getting it back. `edit_content_loss_detected` counts the disease; this counts
 * the cure, and the gap between them is how much of the reported bug is still
 * reaching users.
 *
 * `outcome` separates the three ways a round ends, because they need different
 * fixes: "repaired" (drift cleared), "partial" (some came back), "failed" (the
 * model returned nothing usable, or its repair introduced its own problems and
 * was discarded).
 */
export function trackScopeDriftRepair(props: {
  projectId: string | null;
  outcome: "repaired" | "partial" | "failed";
  /** Drift entering the round, e.g. "component:RecipeForm (app/page.tsx)". */
  detected: string[];
  detectedCount: number;
  /** Still missing after the round — the user-visible failure. */
  survivingCount: number;
  changedFiles: string[];
  requestPreview?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_scope_drift_repair", {
    project_id: props.projectId,
    outcome: props.outcome,
    detected: props.detected.slice(0, 20),
    detected_count: props.detectedCount,
    surviving_count: props.survivingCount,
    // The single boolean an alert can watch: work was deleted and stayed gone.
    user_visible_loss: props.survivingCount > 0,
    changed_files: props.changedFiles.slice(0, 20),
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
  });
}

/**
 * A targeted edit moved a property the turn had no mandate for — the MUTATION
 * half of the scope-drift picture above (lib/validation/style-drift.ts).
 *
 * `reverted` is what the gate put back, so a rise there is the gate WORKING and
 * is the direct measure of a defect users could previously only report by
 * noticing it. `observed` is what it saw and deliberately left alone (copy, a
 * file whose shape changed, a drift count too large to be plausible) — the
 * honest record of the gap, and the queue for whatever widens it next.
 *
 * `apply_path` is carried because the suspicion this gate was built on is
 * testable from it: a full-file rewrite re-authors every line of a file,
 * including the ones the turn never meant to touch, so if collateral change
 * concentrates on `patch-atomic-reject` / `patch-fallback-full` then the
 * mechanism is the cause and the budget is the safety net, not the other way
 * round.
 */
export function trackStyleDrift(props: {
  projectId: string | null;
  /** The axes this turn WAS allowed to move. */
  budget: readonly string[];
  /** WHICH thing the turn was about, or null when it named nothing. */
  target: string | null;
  applyPath: string;
  /** Restored, one line each: "app/page.tsx:42 <button> \"Book a call\" text_color: text-white -> text-red-500". */
  reverted: string[];
  /** Seen and left alone. */
  observed: string[];
  /** Files a fail-open condition skipped, with the reason. */
  skipped: Array<{ path: string; reason: string }>;
  requestPreview?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_style_drift", {
    project_id: props.projectId,
    budget: [...props.budget],
    target: props.target,
    apply_path: props.applyPath,
    reverted: props.reverted.slice(0, 20),
    reverted_count: props.reverted.length,
    // Reverts on an element whose fill this same turn legitimately changed —
    // the one pairing where putting a property back can leave the screen worse
    // than the edit did. Nothing deterministic separates a taste change from a
    // necessary one there (lib/validation/style-drift.ts, StyleDrift.dependent),
    // so it is COUNTED rather than decided: a rising number is the budget
    // getting this wrong, and it is the signal that would justify changing it.
    dependent_revert_count: props.reverted.filter((r) =>
      r.includes("[repainted-here]"),
    ).length,
    observed: props.observed.slice(0, 20),
    observed_count: props.observed.length,
    skipped: props.skipped.slice(0, 20),
    // The single boolean an alert can watch: a property nobody asked about
    // moved, and this turn did not put it back.
    user_visible_drift: props.observed.length > 0,
    request_preview: props.requestPreview?.slice(0, 200) ?? null,
  });
}

/**
 * The turn's changed files are imported by no rendered route at all — the model
 * edited dead code (verifyRouteConnection in lib/ai/route-graph.ts returning
 * "unreachable-dead-code").
 *
 * This is the false-success case that is invisible from the outside: the edit
 * genuinely applied, the diff is real, and the preview is unchanged, so the user
 * is told "Updated your pricing section" over a page that never rendered the
 * file that changed. Nothing in the existing apply telemetry separates that from
 * a successful edit, because as far as apply was concerned it WAS one — the only
 * previous evidence was a user saying nothing happened.
 *
 * `route_reason` is carried rather than assumed so the block can be audited from
 * the other side: a rise concentrated on one reason code means the route graph
 * stopped resolving some project shape (route groups, dynamic segments), which
 * would show up as good edits being refused rather than as bad edits being
 * caught.
 */
export function trackEditTargetDisconnected(props: {
  project_id: string;
  /** RouteConnectionReason — "unreachable-dead-code" is the blocking one. */
  route_reason: string;
  /** The route the user was previewing when the edit ran, when known. */
  preview_route?: string | null;
  /** The files the turn changed that no route reaches. */
  changed_paths: string[];
  /** The request that was supposed to change something the user can see. */
  message: string;
}) {
  if (!initialized) return;
  posthog.capture("edit_target_disconnected", {
    project_id: props.project_id,
    route_reason: props.route_reason,
    preview_route: props.preview_route ?? null,
    changed_paths: props.changed_paths.slice(0, 20),
    changed_path_count: props.changed_paths.length,
    message: props.message.slice(0, 200),
    ...getDeviceContext(),
  });
}

/**
 * A parsed edit was thrown away before it reached the project — the apply
 * pipeline (lib/ai/apply-stage.ts) hit a stage it could not honour and dropped
 * the candidate rather than mounting half of it.
 *
 * A discard is silent by construction: the turn ends with the files unchanged
 * and, unless something else speaks, a recap describing work that no longer
 * exists. `stage` is the field to group on — it says WHERE the edit died, so a
 * rise points at one gate instead of at "edits sometimes don't land" — and
 * `reason` says why within that stage. `raw_chars` is carried because a discard
 * on a very short body is a truncated model response, not a rejected one, and
 * the two want opposite fixes: retry the first, tighten the prompt for the
 * second.
 */
export function trackEditApplyDiscarded(props: {
  project_id: string;
  /** Which stage of the apply pipeline dropped it. */
  stage: string;
  /** That stage's own reason code, when it reports one. */
  reason?: string | null;
  /** The single file the discard was attributed to, when it was one file. */
  apply_path?: string | null;
  /** Every path the discarded candidate would have written. */
  paths: string[];
  /** Size of the raw model body — a tiny value means truncation, not refusal. */
  raw_chars?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_apply_discarded", {
    project_id: props.project_id,
    stage: props.stage,
    reason: props.reason ?? null,
    apply_path: props.apply_path ?? null,
    paths: props.paths.slice(0, 20),
    path_count: props.paths.length,
    raw_chars: props.raw_chars ?? null,
    ...getDeviceContext(),
  });
}

/**
 * An edit that had already been written to the project was taken back out
 * (lib/ai/apply-recovery.ts) — the strongest failure signal the edit path has,
 * because it means the broken state actually existed and had to be undone.
 *
 * `recovery_fix` splits two populations that must never be read as one: a
 * rollback the recovery pass then repaired is a save the user may never have
 * noticed, while a rollback with no fix is a turn that ended with nothing to
 * show for it. Averaged together they hide each other — a rising unfixed rate
 * sits flat inside a healthy total, which is precisely the shape of regression
 * that reaches users before it reaches a dashboard.
 */
export function trackEditRolledBack(props: {
  project_id: string;
  /** Why the mounted edit was reverted. */
  reason: string;
  /** True when the recovery pass produced a working replacement. */
  recovery_fix: boolean;
  /** The files that were written and then rolled back. */
  changed_paths: string[];
}) {
  if (!initialized) return;
  posthog.capture("edit_rolled_back", {
    project_id: props.project_id,
    reason: props.reason,
    recovery_fix: props.recovery_fix,
    changed_paths: props.changed_paths.slice(0, 20),
    changed_path_count: props.changed_paths.length,
    ...getDeviceContext(),
  });
}

/**
 * The turn's own recap admits it left part of the request undone — Panda
 * announcing skipped work instead of doing it ("I've updated the header; the
 * footer still needs to be done").
 *
 * The user asked for one thing and got part of it with a note, which reads as a
 * refusal even though every other signal records the turn as a success: the edit
 * applied, nothing was discarded, no content was lost. `verify_disabled` is
 * carried because the value-verify pass (lib/ai/value-verify.ts) is the layer
 * that would otherwise close the gap — skipped work found while verification was
 * OFF is an expected miss, while the same detection with verification ON means
 * the check ran and did not stop it, a different bug with a different owner.
 */
export function trackEditSkippedWorkDetected(props: {
  project_id: string;
  /** Whether the verify pass was off for this turn. */
  verify_disabled: boolean;
  /** The request whose parts went undone. */
  message: string;
  /** The recap text the admission was read out of. */
  summary_preview: string;
}) {
  if (!initialized) return;
  posthog.capture("edit_skipped_work_detected", {
    project_id: props.project_id,
    verify_disabled: props.verify_disabled,
    message: props.message.slice(0, 200),
    summary_preview: props.summary_preview.slice(0, 200),
    ...getDeviceContext(),
  });
}

/**
 * Panda told the user, in so many words, which parts of their request it did not
 * finish.
 *
 * This is the user-visible half of edit_skipped_work_detected: that event counts
 * the detection, this one counts the times the shortfall was actually put in
 * front of someone. The gap between the two rates is the number of turns that
 * left work undone quietly, which is the more damaging failure and had no
 * measurement at all before. `parts` carries the individual items rather than a
 * count alone because if the same kind of part is always the one deferred, that
 * is a prompt fix rather than a model fix.
 */
export function trackEditUnfinishedPartsShown(props: {
  project_id: string;
  /** The specific pieces named as not done. */
  parts: string[];
  part_count: number;
}) {
  if (!initialized) return;
  posthog.capture("edit_unfinished_parts_shown", {
    project_id: props.project_id,
    // Model-authored prose: capped per item as well as per array, so one long
    // part can never inflate the payload.
    parts: props.parts.slice(0, 10).map((p) => p?.slice(0, 200) ?? ""),
    part_count: props.part_count,
    ...getDeviceContext(),
  });
}

/**
 * A retry pass came back with nothing to apply — the recovery attempt after a
 * truncated or discarded response (lib/ai/truncation-recovery.ts) produced no
 * usable file content.
 *
 * The retry exists so a cut-off response does not cost the user their turn; when
 * it returns empty the turn is lost anyway and the user has now waited twice as
 * long to lose it. `pass` separates the two recovery shapes — asking for the
 * whole file again versus asking only for the missing tail — because they fail
 * for different reasons and only one of them is worth retrying harder. Without
 * this event an empty retry is indistinguishable from the original discard it
 * followed, so the recovery path's success rate was never actually known.
 */
export function trackEditRetryPassEmpty(props: {
  project_id: string;
  /** Which recovery shape ran: a whole-file rewrite or a tail completion. */
  pass: "full_file" | "completion";
  /** The stage that discarded the retry's output, when it produced any. */
  discard_stage?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_retry_pass_empty", {
    project_id: props.project_id,
    pass: props.pass,
    discard_stage: props.discard_stage ?? null,
    ...getDeviceContext(),
  });
}

/**
 * A restyle ran and left the surfaces it was meant to change still on the old
 * look (coverage from lib/ai/palette-coverage.ts, repeat detection from
 * lib/ai/restyle-repeat.ts).
 *
 * Restyles are the request most likely to look done and not be: the turn edits
 * SOME files, the recap is honest about those, and the user's actual complaint —
 * "the rest of my site is still cream" — is invisible to every per-file check,
 * because each file that changed changed correctly. `repeat_attempt` is the
 * escalation signal: the second and third time the same user asks for the same
 * restyle the failure is no longer a miss, it is the product not working, and
 * those turns have to be findable as a group instead of being averaged into
 * first attempts that mostly succeed.
 */
export function trackRestyleNoProgressDetected(props: {
  project_id: string;
  /** Surfaces still carrying the old styling after the pass. */
  uncovered_paths: string[];
  /** How many times this restyle has now been asked for (null when unknown). */
  repeat_attempt?: number | null;
  /** The styling values the user actually asked for. */
  requested: string[];
}) {
  if (!initialized) return;
  posthog.capture("restyle_no_progress_detected", {
    project_id: props.project_id,
    uncovered_paths: props.uncovered_paths.slice(0, 20),
    uncovered_path_count: props.uncovered_paths.length,
    repeat_attempt: props.repeat_attempt ?? null,
    requested: props.requested.slice(0, 10),
    ...getDeviceContext(),
  });
}

/**
 * The applied edit broke one of the framework contracts a file must satisfy to
 * render at all — a client component reaching for a server-only API, a route
 * export in a shape the framework does not enter, and the rest of the family
 * checked in lib/validation/deterministic-fixes.ts.
 *
 * These are the breaks a diff review passes and the build fails on, so until now
 * they were only ever visible as a deploy error minutes later, attributed to the
 * deploy rather than to the turn that caused it. `repaired` separates the ones
 * the deterministic pass healed in place — invisible to the user, and the reason
 * the raw break rate is not the number to alarm on — from the ones that survived
 * and reached the build. `kinds` groups them so the contract the model keeps
 * violating can be fixed once in the prompt instead of being repaired forever.
 */
export function trackEditContractBreakDetected(props: {
  project_id: string;
  /** Contract categories violated, e.g. "client-server-boundary". */
  kinds: string[];
  /** The individual violations, for reading the long tail. */
  problems: string[];
  /** True when the deterministic pass fixed them in place. */
  repaired: boolean;
}) {
  if (!initialized) return;
  posthog.capture("edit_contract_break_detected", {
    project_id: props.project_id,
    kinds: props.kinds.slice(0, 10),
    // Violation text can be long; capped per item as well as per array.
    problems: props.problems.slice(0, 10).map((p) => p?.slice(0, 200) ?? ""),
    problem_count: props.problems.length,
    repaired: props.repaired,
    ...getDeviceContext(),
  });
}

/**
 * The recap's closing teaser — the one line that offers the obvious next step —
 * was dropped because it leaked implementation language
 * (lib/ai/jargon-detect.ts), leaving the confirmation to end flat.
 *
 * This is the quieter sibling of edit_recap_floor_used: the recap itself
 * survived, so nothing in the floor metric moves, and the user simply never gets
 * offered the next thing. Since that offer is where follow-up turns come from, a
 * rise here surfaces much later as fewer second edits, with no visible cause and
 * nothing linking it back to the copy that was removed. `teaser_preview` carries
 * the refused wording because the fix is almost always one phrase the prompt
 * keeps reaching for.
 */
export function trackEditRecapTeaserDropped(props: {
  project_id: string;
  /** Why the teaser was refused. "jargon" today. */
  reason: "jargon";
  /** The refused teaser text — the phrasing to fix in the prompt. */
  teaser_preview: string;
}) {
  if (!initialized) return;
  posthog.capture("edit_recap_teaser_dropped", {
    project_id: props.project_id,
    reason: props.reason,
    teaser_preview: props.teaser_preview.slice(0, 200),
    ...getDeviceContext(),
  });
}

/**
 * The candidate gate (lib/validation/candidate-gate.ts) refused or downgraded a
 * generated candidate before it was mounted.
 *
 * The two statuses are different outcomes of the same check and have to stay
 * separable: "unbuildable" means hard, guaranteed-compile-break problems
 * survived and the candidate was refused outright — the user's turn produced
 * nothing — while "soft-blocked" means only self-healing problems remained and
 * the candidate was mounted best-effort. Counted together, a rise in outright
 * refusals hides inside a healthy-looking total.
 *
 * `unbuildable_paths` is carried because the gate is atomic: one bad file
 * refuses the whole multi-file change, so knowing WHICH file recurs is the
 * difference between fixing one generator and losing trust in the gate itself.
 */
export function trackBuildCandidateGate(props: {
  project_id: string;
  status: "unbuildable" | "soft-blocked";
  /** The files whose hard problems refused the candidate. */
  unbuildable_paths: string[];
  /** First error text the gate reported, when it reported one. */
  error_summary?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("build_candidate_gate", {
    project_id: props.project_id,
    status: props.status,
    unbuildable_paths: props.unbuildable_paths.slice(0, 20),
    unbuildable_path_count: props.unbuildable_paths.length,
    error_summary: props.error_summary?.slice(0, 200) ?? null,
    ...getDeviceContext(),
  });
}

/**
 * A build candidate was refused because generated files import symbols the
 * generated modules do not export — `RecipesContext.tsx` asking
 * `lib/data/recipes.ts` for `RECIPES` when that module exports something else
 * (PANDA-2157, the root cause behind the PANDA-2157 progress-reset symptom).
 *
 * `build_candidate_gate` already counts THAT a candidate was refused, but only
 * by path, and its `error_summary` is a truncated prose blob. That is enough to
 * see a refusal rate and useless for fixing one: the whole class is defined by
 * a (source file, symbol, target module) triple, and without those three fields
 * there is no way to tell "the model keeps SCREAMING_CASE-ing data exports"
 * from "the model keeps default-exporting data modules" — two different prompt
 * fixes that look identical in the funnel.
 *
 * `repaired_count` / `repaired_kinds` are the other half of the signal, and the
 * more important half over time: they count the breaks the deterministic pass
 * ABSORBED, which no other event records because nothing failed. A rising
 * repair rate against a flat refusal rate means generation is drifting on
 * export contracts and the repair is the only thing holding the build rate up —
 * the warning that arrives before a shape appears that repair cannot resolve.
 *
 * Pairs with `trackUnresolvedLocalImportsRecovery` via `incident_id`: whether
 * the user was OFFERED a way out is known here, whether they ever got an app is
 * only known later, and a refusal rate reads very differently depending on
 * which. See that function for why the join is two events rather than one.
 */
export function trackUnresolvedLocalImports(props: {
  project_id: string;
  /** Correlates this refusal with the recovery outcome reported later. */
  incident_id: string;
  /** Which generation path produced it — first build and edit regress apart. */
  build_type: string;
  /** One row per unresolved binding: importer, symbol, and intended module. */
  unresolved: Array<{ file: string; symbol: string; from: string }>;
  /** Paths refused for a module that does not resolve AT ALL, for contrast. */
  missing_module_paths: string[];
  /** Bindings the deterministic pass reconciled before the gate ran. */
  repaired_count: number;
  /** The shapes it reconciled, e.g. `case`, `named-to-default`. */
  repaired_kinds: string[];
  /** Whether the user was given a "Continue building" way out. */
  recovery_offered: boolean;
}) {
  if (!initialized) return;
  const unresolved = props.unresolved.slice(0, 20);
  posthog.capture("unresolved_local_imports", {
    project_id: props.project_id,
    incident_id: props.incident_id,
    build_type: props.build_type,
    unresolved,
    unresolved_count: props.unresolved.length,
    // Flattened alongside the structured rows: PostHog can group by a scalar
    // array far more cheaply than it can reach into objects, and "which symbol
    // recurs" / "which module recurs" are the two questions actually asked.
    source_files: [...new Set(unresolved.map((u) => u.file))],
    symbols: [...new Set(unresolved.map((u) => u.symbol))],
    target_modules: [...new Set(unresolved.map((u) => u.from))],
    missing_module_paths: props.missing_module_paths.slice(0, 20),
    repaired_count: props.repaired_count,
    repaired_kinds: [...new Set(props.repaired_kinds)],
    recovery_offered: props.recovery_offered,
    ...getDeviceContext(),
  });
}

/**
 * How an `unresolved_local_imports` refusal ENDED for the user.
 *
 * Split from the refusal event because the two facts are known at different
 * times and cannot honestly share one payload: the refusal knows whether a
 * "Continue building" card was offered, but whether the user ever reached a
 * working app is decided later — or never, if they close the tab. Emitting one
 * event would mean either delaying the refusal (losing it entirely when the
 * session ends) or guessing the outcome. Joined on `incident_id`.
 *
 * The absence of this event for a given `incident_id` is itself the finding:
 * a refusal that was offered recovery and never recorded one is a user who
 * abandoned at "No preview yet", which is the exact user-visible failure the
 * ticket is about and the one a refusal-only funnel cannot see.
 */
export function trackUnresolvedLocalImportsRecovery(props: {
  project_id: string;
  /** The refusal this outcome belongs to. */
  incident_id: string;
  /** True when a later build in this session actually landed an app. */
  recovery_completed: boolean;
  /** Milliseconds between the refusal and this outcome. */
  duration_ms: number;
}) {
  if (!initialized) return;
  posthog.capture("unresolved_local_imports_recovery", {
    project_id: props.project_id,
    incident_id: props.incident_id,
    recovery_completed: props.recovery_completed,
    duration_ms: props.duration_ms,
    ...getDeviceContext(),
  });
}
/**
 * A repair round that the TRANSPORT refused, not the model.
 *
 * `/api/ai/fix` has always capped what it sends the model, but those caps ran
 * inside the route — after `await request.json()`. On a large project the
 * browser posted the entire candidate file set and the platform answered 413
 * before any handler ran, so the build's one repair round silently did nothing
 * and the candidate went to the gate exactly as broken as it arrived. That is
 * the Animal Kingdom Atlas regression, and this event is the only place it is
 * visible: from the outside a 413 looks identical to a repair that found
 * nothing to fix.
 *
 * `bytes` vs `raw_bytes` is the whole diagnostic. Once the client trims, every
 * ordinary round reports a `bytes` in the tens of KB against a `raw_bytes` in
 * the megabytes — the gap IS the 413 that no longer happens. A `bytes` that
 * climbs back toward the ceiling means a pass-through field the caps do not
 * cover has started to grow, which is the next regression of this shape.
 */
export function trackFixPayloadTooLarge(props: {
  project_id: string;
  /** Which repair loop posted it — build gate, preview autofix, publish, … */
  surface: string;
  /** Serialized size we actually sent. */
  bytes: number;
  /** Size the same payload would have been untrimmed. */
  raw_bytes: number;
  /** Files the model could see. */
  sent_paths: string[];
  /** Files the budget left out — what the model was repairing blind. */
  dropped_paths: string[];
  /**
   * What happened next: `"retried-minimal"` (we re-sent an error-only payload),
   * `"trimmed"` (the caps alone brought it under, no retry needed), or
   * `"gave-up"` (nothing left to shed — the round is lost).
   */
  recovery_path: string;
  /** True when the server actually answered 413, false for a pre-emptive trim. */
  rejected: boolean;
}) {
  if (!initialized) return;
  posthog.capture("fix_payload_too_large", {
    project_id: props.project_id,
    surface: props.surface,
    bytes: props.bytes,
    raw_bytes: props.raw_bytes,
    // The ratio is what a dashboard actually groups by; computing it here keeps
    // every consumer from re-deriving it (and getting the zero-guard wrong).
    trim_ratio:
      props.raw_bytes > 0
        ? Math.round((props.bytes / props.raw_bytes) * 100) / 100
        : 1,
    sent_paths: props.sent_paths.slice(0, 20),
    sent_path_count: props.sent_paths.length,
    dropped_paths: props.dropped_paths.slice(0, 20),
    dropped_path_count: props.dropped_paths.length,
    recovery_path: props.recovery_path,
    rejected: props.rejected,
    ...getDeviceContext(),
  });
}

/**
 * The deterministic repair pass (lib/validation/deterministic-fixes.ts) rewrote
 * model output before it was mounted.
 *
 * Every fix counted here is a defect the model produced and a defect the user
 * never saw, which is exactly why it needs counting: the pass is load-bearing
 * and silent, so the model regression it absorbs has no other signal anywhere.
 * If this rate climbs while build failures stay flat, the model got worse and
 * the repairs are the only thing holding the number up — and the day a new
 * defect shape appears that the pass does not cover, the failure arrives with no
 * warning at all.
 *
 * `surface` keeps the first build and later edits apart; they run different
 * prompts and regress independently, and one drowning out the other is how a
 * regression in the rarer path stays invisible.
 */
export function trackDeterministicFixesApplied(props: {
  project_id: string;
  /** How many repairs the pass made this turn. */
  fix_count: number;
  /** The files it had to repair. */
  fixed_paths: string[];
  /** Which path produced the output: the first build or a follow-up edit. */
  surface: "build" | "edit";
}) {
  if (!initialized) return;
  posthog.capture("deterministic_fixes_applied", {
    project_id: props.project_id,
    fix_count: props.fix_count,
    fixed_paths: props.fixed_paths.slice(0, 20),
    fixed_path_count: props.fixed_paths.length,
    surface: props.surface,
    ...getDeviceContext(),
  });
}

/**
 * Text the generated page renders unreadably, and what became of it.
 *
 * The client half of the text-contrast guard, and the half that fires on EVERY
 * turn — the server gate only sees initial builds, so without this an edit that
 * drops a dark paragraph onto a dark photo would be repaired silently and
 * counted nowhere.
 *
 * Fires whenever the pass found something, in shadow as well as repair mode:
 * shadow is where the false-fire rate is read, and a mode that emits nothing has
 * no reason to exist. `examined` is the denominator, so a rise in findings can be
 * told apart from a rise in pages.
 *
 * NOT folded into `trackStatedConstraintMissing`: that event means "a turn
 * claimed to apply a value the USER stated and did not", and it feeds a refund
 * path. Nobody states contrast and nothing here is refunded, so reusing it would
 * corrupt the instruction-following rate it exists to measure.
 */
export function trackTextContrastRepaired(props: {
  project_id: string;
  /** The first build, or a follow-up edit. They regress independently. */
  surface: "build" | "edit";
  mode: "shadow" | "repair";
  /** Text elements whose colour AND backdrop both resolved. */
  examined: number;
  finding_count: number;
  /** Fixed by the deterministic same-ramp rewrite, at no cost to the owner. */
  repaired_count: number;
  /** Left for the model, because the fix needed a design decision. */
  unrepaired_count: number;
  /** `section|role|backdrop|reason|ratio|outcome` — outcome is
   *  deterministic | model. A count cannot say whether a finding was fair. */
  findings: string[];
  changed_paths: string[];
  /** What the owner had asked for, for context on a false fire. */
  message: string;
}) {
  if (!initialized) return;
  posthog.capture("text_contrast_repaired", {
    project_id: props.project_id,
    surface: props.surface,
    mode: props.mode,
    examined: props.examined,
    finding_count: props.finding_count,
    repaired_count: props.repaired_count,
    unrepaired_count: props.unrepaired_count,
    findings: props.findings.slice(0, 12).map((f) => f.slice(0, 160)),
    changed_paths: props.changed_paths.slice(0, 10),
    message: props.message.slice(0, 140),
    ...getDeviceContext(),
  });
}

// ── Browser-notification opt-in funnel ──────────────────────────────────────
// Measures how many users turn on browser (web-push) notifications and where
// they drop out of the opt-in flow. The PostHog funnel is:
//   notification_prompt_shown → notification_enable_clicked →
//   notification_permission_granted → notification_enabled
// Every event carries the same segmentation block (surface/trigger/plan/
// device/project_state) so the funnel can be sliced by where it was shown, the
// user's plan, device, and the project's state. Fired from all three opt-in
// surfaces: the in-chat nudge, the settings notifications card, and the profile
// toggle (permission_granted/denied are centralised in usePushNotifications so
// every surface reports them identically).

/** Where the opt-in was surfaced. */
export type NotificationOptinSurface = "chat_nudge" | "settings" | "profile";

export interface NotificationOptinContext {
  surface: NotificationOptinSurface;
  /** What armed the prompt — e.g. "post_task" (nudge) or "manual" (settings). */
  trigger?: string;
  /** User's plan; pass when known. Null (not "free") when the surface can't read
   *  it, so plan segmentation isn't skewed by a wrong default. */
  plan?: string | null;
  projectId?: string | null;
  /** "generating" | "published" | "idle" where a project is in context, else null. */
  projectState?: string | null;
}

function notificationOptinProps(ctx: NotificationOptinContext) {
  return {
    surface: ctx.surface,
    trigger: ctx.trigger ?? null,
    plan: ctx.plan ?? null,
    project_id: ctx.projectId ?? null,
    project_state: ctx.projectState ?? null,
    ...getDeviceContext(),
  };
}

/** Funnel step 1: the opt-in prompt was actually shown to the user. */
export function trackNotificationPromptShown(ctx: NotificationOptinContext) {
  if (!initialized) return;
  posthog.capture("notification_prompt_shown", notificationOptinProps(ctx));
}

/** Funnel step 2: the user clicked "Enable" (before the browser prompt fires). */
export function trackNotificationEnableClicked(ctx: NotificationOptinContext) {
  if (!initialized) return;
  posthog.capture("notification_enable_clicked", notificationOptinProps(ctx));
}

/** Funnel step 3: the browser permission prompt returned "granted". */
export function trackNotificationPermissionGranted(ctx: NotificationOptinContext) {
  if (!initialized) return;
  posthog.capture("notification_permission_granted", notificationOptinProps(ctx));
}

/** The browser permission prompt returned non-granted. `result` distinguishes an
 *  explicit "denied" from "default" (the user dismissed the OS prompt). */
export function trackNotificationPermissionDenied(
  ctx: NotificationOptinContext & { result?: string },
) {
  if (!initialized) return;
  posthog.capture("notification_permission_denied", {
    ...notificationOptinProps(ctx),
    result: ctx.result ?? "denied",
  });
}

/** The user dismissed the opt-in prompt (the nudge's X) without enabling. */
export function trackNotificationPromptDismissed(ctx: NotificationOptinContext) {
  if (!initialized) return;
  posthog.capture("notification_prompt_dismissed", notificationOptinProps(ctx));
}

/** The user opened the notification settings surface. */
export function trackNotificationSettingsOpened(ctx: NotificationOptinContext) {
  if (!initialized) return;
  posthog.capture("notification_settings_opened", notificationOptinProps(ctx));
}

/** Funnel step 4 (terminal): notifications were enabled AND the subscription was
 *  persisted server-side — a real, delivering opt-in. */
export function trackNotificationEnabled(ctx: NotificationOptinContext) {
  if (!initialized) return;
  posthog.capture("notification_enabled", notificationOptinProps(ctx));
}

/**
 * Track: a client request failed with HTTP 413 because the project grew too
 * large to send in one payload. These 413s are thrown at the platform/proxy
 * boundary BEFORE the route runs, so only the client sees them — hence a
 * client-side event (not a server pipeline event). `operation` names which path
 * hit the limit so we can see whether saves, pushes, or edits are affected and
 * how large the projects are. Distinct, size-specific failure signal — not the
 * generic context_too_large bucket.
 */
export function trackPayloadTooLarge(props: {
  /** PANDA-1917 added "sandbox_files_write": the preview transport was the one
   *  413 path with no telemetry at all, so the reported
   *  "files write failed (chunk 2/3): 413" was invisible in product analytics. */
  operation: "db_save" | "github_push" | "chat_edit" | "sandbox_files_write";
  projectId: string | null;
  fileCount: number;
  payloadBytes: number;
  /** Length of the user's prompt for this turn — lets us confirm a payload
   *  failure happened on an already-focused ask (a short prompt), the case the
   *  user-blaming "make smaller changes" copy mis-handled. */
  promptTextLength?: number;
  /** Paths in the payload that tripped the limit (capped for event-size). */
  selectedPaths?: string[];
  /** Endpoint + HTTP status that rejected the body (e.g. "/api/ai/chat", 413). */
  endpoint?: string;
  httpStatus?: number;
  /** How the set was split, and which piece failed. Distinguishes "one chunk was
   *  mis-sized" from "the whole set is hopeless" without reading logs. */
  chunkCount?: number;
  chunkIndex?: number;
}) {
  if (!initialized) return;
  posthog.capture("payload_too_large", {
    operation: props.operation,
    project_id: props.projectId,
    file_count: props.fileCount,
    payload_bytes: props.payloadBytes,
    prompt_text_length: props.promptTextLength,
    selected_paths: props.selectedPaths?.slice(0, 50),
    endpoint: props.endpoint,
    http_status: props.httpStatus,
    chunk_count: props.chunkCount,
    chunk_index: props.chunkIndex,
    ...getDeviceContext(),
  });
}

/**
 * Track the OUTCOME of the reduced-file retry that follows an edit 413 —
 * specifically whether shrinking the payload actually recovered the turn. Fired
 * once the retry resolves (success or give-up), so a spike in
 * `retry_succeeded=false` flags that the reduced retry is still over-limit (the
 * original "reduced set re-included the huge files → second 413" bug).
 */
export function trackEditPayloadRecovery(props: {
  projectId: string | null;
  promptTextLength: number;
  selectedPaths: string[];
  payloadBytes: number;
  endpoint: string;
  retrySucceeded: boolean;
  /** HTTP status of the retry response (undefined if it threw before responding). */
  retryStatus?: number;
}) {
  if (!initialized) return;
  posthog.capture("edit_payload_recovery", {
    project_id: props.projectId,
    prompt_text_length: props.promptTextLength,
    selected_paths: props.selectedPaths.slice(0, 50),
    payload_bytes: props.payloadBytes,
    endpoint: props.endpoint,
    retry_succeeded: props.retrySucceeded,
    retry_status: props.retryStatus,
    ...getDeviceContext(),
  });
}

/**
 * Track: User clicked share / copied project link
 */
export function trackShareClick(projectId: string) {
  if (!initialized) return;
  posthog.capture("share_clicked", {
    project_id: projectId,
  });
}

/** Which control started a copy — the dedicated field button, the channel row
 *  icon, or the fall-through after the native share sheet was dismissed. */
export type ShareCopySource =
  | "copy_field_button"
  | "channel_row"
  | "native_share_fallback";

/** Why a copy never reached the clipboard. */
export type ShareCopyErrorReason =
  | "clipboard_unavailable"
  | "permission_denied"
  | "no_share_url"
  | "unknown";

/**
 * Track: Share dialog opened.
 *
 * Fired from the dialog itself rather than the buttons that open it, so the
 * desktop header, the mobile actions sheet and the post-upgrade reopen all
 * report identically. `share_clicked` still fires at the two button call sites
 * and is left alone so existing funnels stay continuous.
 */
export function trackShareMenuOpened(projectId: string, planType?: string) {
  if (!initialized) return;
  posthog.capture("share_menu_opened", {
    project_id: projectId,
    plan_type: planType ?? "free",
  });
}

/** The named destinations that sit alongside Copy link in the share dialog. */
export type ShareChannel =
  | "email"
  | "facebook"
  | "x"
  | "linkedin"
  | "reddit"
  | "native";

/**
 * Track: User picked a named share destination.
 *
 * Exists so "copy link as a share of all share actions" has a denominator —
 * without it, `share_copy_link_clicked` is an absolute count with nothing to
 * divide by. Union this with `share_copy_link_clicked` for total share actions.
 */
export function trackShareChannelClicked(projectId: string, channel: ShareChannel) {
  if (!initialized) return;
  posthog.capture("share_channel_clicked", {
    project_id: projectId,
    channel,
  });
}

/** Track: User pressed a copy-link control (intent, before the write lands). */
export function trackShareCopyLinkClicked(
  projectId: string,
  source: ShareCopySource,
) {
  if (!initialized) return;
  posthog.capture("share_copy_link_clicked", {
    project_id: projectId,
    source,
  });
}

/** Track: Outcome of the clipboard write. Fires on both success and failure so
 *  copy_success is a complete denominator for the copy funnel. */
export function trackShareLinkCopied(projectId: string, copySuccess: boolean) {
  if (!initialized) return;
  posthog.capture("share_link_copied", {
    project_id: projectId,
    copy_success: copySuccess,
  });
}

/** Track: Clipboard write failed, with the reason. Always accompanied by a
 *  `share_link_copied` with copy_success: false. */
export function trackShareLinkCopyFailed(
  projectId: string,
  errorReason: ShareCopyErrorReason,
) {
  if (!initialized) return;
  posthog.capture("share_link_copy_failed", {
    project_id: projectId,
    error_reason: errorReason,
  });
}

/** Which surface rendered the shared project-visibility row (GT-07). */
export type VisibilitySurface = "share_dialog" | "project_settings";

/**
 * Track: the visibility row was shown, with the state and affordance it
 * actually rendered.
 *
 * GT-07 existed because the Share dialog and Project settings described the
 * same `is_public` value differently — one of them told free owners with a
 * private project that "free projects stay public", the other offered them a
 * one-way "Make public". Both now render the same component, so this event's
 * job is to prove that stays true: group by `surface` and the state columns
 * should be identical distributions. A `resolved: false` row means the modal
 * was opened before the project's visibility had loaded — the state neither
 * surface should be describing at all.
 */
export function trackVisibilityCopyViewed(props: {
  projectId: string;
  surface: VisibilitySurface;
  /** null when the project's visibility has not loaded yet. */
  isPublic: boolean | null;
  canTogglePrivate: boolean;
  isPublished: boolean;
}) {
  if (!initialized) return;
  posthog.capture("visibility_copy_viewed", {
    project_id: props.projectId,
    surface: props.surface,
    resolved: props.isPublic !== null,
    is_public: props.isPublic,
    can_toggle_private: props.canTogglePrivate,
    is_published: props.isPublished,
  });
}

/**
 * Track: the lifecycle of a visibility READ — the fetch behind the row above.
 *
 * `visibility_copy_viewed` already reported `resolved: false` when a surface
 * opened before visibility had loaded, but nothing said WHY, or whether it ever
 * finished. It never did: on a project created from the home prompt the page's
 * load effect returned early (no files, no messages yet) without ever reading
 * `isPublic`, and then latched itself against re-running — so the row sat on
 * "Checking visibility…" for the whole session and only a hard reload cleared
 * it. A stuck read is now a measurable event rather than a screenshot.
 *
 * `durationMs` is the wall time from the fetch starting; on `timeout` it is the
 * budget, and on `failure` it is however long the failing attempt took.
 */
export function trackVisibilityFetch(
  phase: "started" | "succeeded" | "failed" | "timeout",
  props: {
    projectId: string;
    surface: VisibilitySurface;
    /** 0 for the first read; climbs with each automatic retry. */
    attempt: number;
    durationMs?: number;
    /** Short reason on a failure — an HTTP status or an error name. */
    reason?: string | null;
  },
) {
  if (!initialized) return;
  posthog.capture(`visibility_fetch_${phase}`, {
    project_id: props.projectId,
    surface: props.surface,
    attempt: props.attempt,
    ...(props.durationMs != null
      ? { duration_ms: Math.round(props.durationMs) }
      : {}),
    ...(props.reason ? { reason: props.reason } : {}),
  });
}

/**
 * Track: User exported/downloaded project as ZIP
 */
export function trackExport(projectId: string, projectTitle?: string) {
  if (!initialized) return;
  posthog.capture("project_exported", {
    project_id: projectId,
    project_title: projectTitle,
  });
}

/** Safely extract a URL's host for event segmentation; "" when unparseable. */
function safeHost(url: string | null | undefined): string {
  if (!url) return "";
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
}

/**
 * Why the preview pane is showing a deployed build rather than a live sandbox:
 * `direct_load` = the paid E2B optimization (published + unchanged → skip the
 * VM); `webcontainer_unsupported` = the legacy mobile / unsupported-browser
 * fallback. Used to tell the two deployed-preview paths apart in replay.
 */
export type RemotePreviewReason = "direct_load" | "webcontainer_unsupported";

/**
 * Track: the preview pane is showing the deployed Vercel build instead of an
 * in-browser sandbox. Session replay cannot capture that iframe — it's
 * cross-origin and, unlike the sandbox previews, carries no injected posthog-js
 * relay — so the replay shows the preview area masked/blank. This event is the
 * timeline marker that tells QA a working preview was being served at that
 * moment, and (via `reason`) WHICH deployed-preview path produced it — so a
 * blank replay on the direct-load path is no longer indistinguishable from a
 * user-facing blank page.
 *
 * `meta` is optional for backward compatibility with the original two-arg call.
 */
export function trackRemotePreviewShown(
  projectId: string,
  previewUrl: string,
  meta?: {
    reason?: RemotePreviewReason;
    hasBeenPublished?: boolean;
    publishedAt?: string | null;
    /** In-preview route at the moment the deployed build was shown. */
    route?: string | null;
  },
) {
  if (!initialized) return;
  posthog.capture("remote_preview_shown", {
    project_id: projectId,
    preview_url: previewUrl,
    deployed_host: safeHost(previewUrl),
    reason: meta?.reason ?? null,
    has_been_published: meta?.hasBeenPublished ?? null,
    published_at: meta?.publishedAt ?? null,
    route: meta?.route ?? null,
    ...getDeviceContext(),
  });
}

/**
 * Track: a DIRECT-LOADED deployed preview was abandoned in favour of booting a
 * VM. Fires when the published-build iframe failed to become a usable preview —
 * either its `onLoad` never fired within the window (`iframe_load_timeout`) or a
 * server-side health probe found the deployed page blank/broken. This is the
 * explicit "the deployed direct-load fallback engaged" signal QA needs to tell a
 * silent deployed-preview failure apart from replay capture-masking.
 *
 * Carries everything needed to debug the failure without a repro: project id,
 * the full deployed URL AND the iframe `src` it was loaded under (they differ
 * once the cache-buster is on), the in-preview route, HTTP status when the
 * server probe supplied one, and browser/OS/device from `getDeviceContext()`.
 */
export function trackDeployedPreviewFallback(props: {
  projectId: string;
  reason:
    | "iframe_load_timeout"
    | "health_probe_blank"
    | "health_probe_error"
    | "health_http_error";
  deployedUrl?: string | null;
  /** The iframe's actual `src` (deployed URL + cache-buster). */
  iframeSrc?: string | null;
  /** In-preview route at the moment the fallback engaged. */
  route?: string | null;
  /** Server-side classifier reason / HTTP status, when the probe drove it. */
  detail?: string | null;
  status?: number | null;
  elapsedMs?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("deployed_preview_fallback", {
    project_id: props.projectId,
    reason: props.reason,
    preview_url: props.deployedUrl ?? null,
    deployed_host: safeHost(props.deployedUrl),
    iframe_src: props.iframeSrc ?? props.deployedUrl ?? null,
    route: props.route ?? null,
    detail: props.detail ?? null,
    status: props.status ?? null,
    ...(props.elapsedMs != null ? { elapsed_ms: Math.round(props.elapsedMs) } : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: an E2B preview boot (POST /api/sandbox/create) hit its client abort
 * budget before the sandbox responded — the "signal is aborted without reason"
 * timeout that larger projects trip on a cold boot. Distinct from
 * `pipeline_build_failed { boot_failed }` (which only fires on the applyAiFiles
 * path); this fires from the boot hook itself, so a timeout is visible on EVERY
 * boot path, with enough shape to debug large-project preview timeouts:
 * file count, serialized payload size, the budget that was hit, and whether an
 * automatic retry ran / will run. The user's project is already saved — this is
 * a preview-boot infra event, never an AI-generation failure.
 */
export function trackPreviewBootTimeout(props: {
  projectId: string | null;
  fileCount: number;
  /** The app's serialized wire size (escaped UTF-8 JSON) — the same
   *  measurement `preview_create_split` carries, so the two are comparable.
   *  NOTE: before 2026-08-31 this property held summed `content.length`
   *  (UTF-16 code units, pre-escaping); readings either side of that deploy
   *  are different quantities. */
  payloadBytes: number;
  /** Bytes THIS create actually sent. Equals `payloadBytes` except on the
   *  split path, where the create body is empty by design. */
  requestBodyBytes?: number;
  /** Joins to the server's `sandbox_create_received` / `_body_read` /
   *  `_guarded` markers — the only way to tell a create that never arrived
   *  from one that arrived and wedged. */
  bootId?: string;
  /** `warm_preboot` = the fileless VM started during generation;
   *  `files` = the app's own boot. Two different operations that shared this
   *  event name and have nothing-alike elapsed distributions. */
  bootKind?: "warm_preboot" | "files";
  /** The tab went hidden at some point during this attempt. A backgrounded
   *  phone suspends the abort timer while the wall clock runs on, so this is
   *  what separates a slow boot from a pocketed one. */
  wentHidden?: boolean;
  timeoutMs: number;
  /** 0 = first boot, 1 = the automatic retry. */
  attempt: number;
  /** True when an automatic retry is being scheduled after this timeout. */
  willRetry: boolean;
  /** Wall-clock ms the aborted create actually ran before the abort fired — the
   *  "how close to the budget was it" signal (vs `timeout_ms`, the budget). */
  elapsedMs?: number;
  /** How many exhausted timeout CHAINS this project has hit in a row without a
   *  successful reveal (a chain = attempt 0 → the auto-retries). 1 on the first
   *  full timeout; climbs while the user keeps retrying the same stuck project,
   *  so the non-converging clusters are measurable and separable from one-offs. */
  streak?: number;
}) {
  if (!initialized) return;
  posthog.capture("preview_boot_timeout", {
    project_id: props.projectId,
    file_count: props.fileCount,
    payload_bytes: props.payloadBytes,
    timeout_ms: props.timeoutMs,
    attempt: props.attempt,
    retry_attempted: props.attempt > 0,
    will_retry: props.willRetry,
    ...(props.requestBodyBytes != null
      ? { request_body_bytes: props.requestBodyBytes }
      : {}),
    ...(props.bootId ? { boot_id: props.bootId } : {}),
    ...(props.bootKind ? { boot_kind: props.bootKind } : {}),
    ...(props.wentHidden != null ? { went_hidden: props.wentHidden } : {}),
    ...(props.elapsedMs != null ? { elapsed_ms: Math.round(props.elapsedMs) } : {}),
    ...(props.streak != null ? { timeout_streak: props.streak } : {}),
    ...getDeviceContext(),
    ...getConnectionContext(),
    ...failureContext(),
  });
}

/**
 * Track: a create body that would exceed the platform's request-size cap was
 * split — booted with an empty file set, then streamed in via chunked /files
 * writes — so the preview boot AVOIDED a transport 413. Fired proactively; the
 * user is never blocked. Shape mirrors trackPreviewBootTimeout so large-project
 * preview infra events line up in one PostHog view.
 */
export function trackPreviewCreateSplit(props: {
  projectId: string | null;
  fileCount: number;
  /** Estimated serialized create-body size that triggered the split. */
  payloadBytes: number;
}) {
  if (!initialized) return;
  posthog.capture("preview_create_split", {
    project_id: props.projectId,
    file_count: props.fileCount,
    payload_bytes: props.payloadBytes,
    ...getDeviceContext(),
  });
}

/**
 * Track: a preview create STILL returned a transport 413 (payload too large)
 * despite the split guard — e.g. a single file over the cap, or an estimate
 * that slipped. This is the traceable record for the "create failed: 413" boot
 * failure; the user's app is already saved and the boot-error retry is offered.
 */
export function trackPreviewCreateTooLarge(props: {
  projectId: string | null;
  fileCount: number;
  payloadBytes: number;
  /** True if the split guard already fired for this boot (so a single oversized
   *  file is the likely cause, not the aggregate body). */
  wasSplit: boolean;
}) {
  if (!initialized) return;
  posthog.capture("preview_create_too_large", {
    project_id: props.projectId,
    file_count: props.fileCount,
    payload_bytes: props.payloadBytes,
    was_split: props.wasSplit,
    ...getDeviceContext(),
  });
}

/**
 * Track: User selected a template
 */
export function trackTemplateUsed(
  templateId: string,
  source: "homepage" | "url" | "workspace" | "templates_page" | "gallery",
) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("template_used", {
    template_id: templateId,
    source,
    ...utm,
  });
}

/**
 * Track: User connected Supabase database
 */
export function trackDbSetup(projectId: string) {
  if (!initialized) return;
  posthog.capture("db_setup_completed", {
    project_id: projectId,
  });
}

/**
 * Track: Referral signup (someone signed up via a referral link)
 */
export function trackReferralSignup(referralCode: string, referredUserId: string) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("referral_signup", {
    referral_code: referralCode,
    referred_user_id: referredUserId,
    ...utm,
  });
}

/** Which control on the referral hub produced a copy — the link card's own
 *  button, the empty-state CTA, or a share sheet the user dismissed. */
export type ReferralLinkSource =
  | "link_card"
  | "empty_state"
  | "native_share_fallback";

/** The destinations that sit alongside Copy on the referral hub. `native` is
 *  the OS share sheet. */
export type ReferralShareChannel = "native" | "x" | "whatsapp" | "email";

/** Which door the referral hub was opened through (PANDA-2378): the
 *  /referrals page; in the build canvas, the header button beside "Invite your
 *  team", the desktop project (⋯) menu — the door at widths where that button
 *  would cover other header controls — or the mobile actions sheet (phones
 *  never see the header). Every hub event carries it, so each step of the
 *  funnel can be split by entry point. */
export type ReferralHubEntryPoint =
  | "referrals_page"
  | "build_canvas_header"
  | "build_canvas_project_menu"
  | "build_canvas_actions_sheet";

/**
 * Track: the referral hub was put in front of the user, before anything has
 * loaded (PANDA-2378).
 *
 * `referral_hub_viewed` waits for the link and counts to settle, so that
 * `has_link` describes what was actually on screen — which means a hub closed
 * before then never reports. On the /referrals page the $pageview covers that
 * gap; the canvas panel has no pageview, so without this a quick close or a
 * slow Affonso looked the same as nobody opening it. Opened minus viewed is
 * that drop-off.
 */
export function trackReferralHubOpened(ctx: { entry_point: ReferralHubEntryPoint }) {
  if (!initialized) return;
  posthog.capture("referral_hub_opened", {
    entry_point: ctx.entry_point,
    ...getDeviceContext(),
  });
}

/**
 * Track: the referral hub was opened.
 *
 * GT-04's headline metric. Until this existed there was no way to tell "nobody
 * visits the hub" apart from "nobody can find the hub" — the referral
 * programme (PANDA-1760) read as zero-usage with no instrumentation behind
 * that reading. Fire once per mount; React StrictMode mounts twice in dev, so
 * the call site guards with a ref.
 *
 * `has_link` is the load-bearing property: a hub that renders with no referral
 * link is a dead end, and this is what separates that failure from a healthy
 * view. Segment every other referral funnel on it.
 */
export function trackReferralHubViewed(ctx: {
  entry_point: ReferralHubEntryPoint;
  has_link: boolean;
  clicks: number;
  signups: number;
  credits_earned: number;
}) {
  if (!initialized) return;
  posthog.capture("referral_hub_viewed", {
    entry_point: ctx.entry_point,
    has_link: ctx.has_link,
    clicks: ctx.clicks,
    signups: ctx.signups,
    credits_earned: ctx.credits_earned,
    ...getDeviceContext(),
  });
}

/**
 * Track: outcome of the referral-link clipboard write.
 *
 * Fires on BOTH success and failure so `copy_success` is a complete
 * denominator for the copy funnel — the same contract `share_link_copied`
 * holds for project links. A hub view with no copy is the drop-off GT-04 is
 * trying to move, so a silent failure here would look identical to disinterest.
 */
export function trackReferralLinkCopied(ctx: {
  entry_point: ReferralHubEntryPoint;
  copy_success: boolean;
  source: ReferralLinkSource;
  error_reason?: ShareCopyErrorReason;
}) {
  if (!initialized) return;
  posthog.capture("referral_link_copied", {
    entry_point: ctx.entry_point,
    copy_success: ctx.copy_success,
    source: ctx.source,
    ...(ctx.error_reason ? { error_reason: ctx.error_reason } : {}),
  });
}

/**
 * Track: user pushed their referral link at a named destination or the OS
 * share sheet.
 *
 * `native` is only counted once the sheet actually opened — a dismissed sheet
 * falls through to copy and is counted as a copy instead, so the two events
 * never double-count one intent.
 */
export function trackReferralLinkShared(ctx: {
  entry_point: ReferralHubEntryPoint;
  channel: ReferralShareChannel;
}) {
  if (!initialized) return;
  posthog.capture("referral_link_shared", {
    entry_point: ctx.entry_point,
    channel: ctx.channel,
  });
}

/**
 * Track: Upgrade / subscription change (ready for when payments ship)
 */
export function trackUpgrade(planName: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("user_upgraded", {
    plan: planName,
    ...utm,
    ...properties,
  });
}

/**
 * Track: Plan change (upgrade or downgrade) for existing subscribers
 */
export function trackPlanChange(
  type: "upgrade" | "downgrade",
  fromPlan: string,
  toPlan: string,
  proratedAmount?: number,
) {
  if (!initialized) return;
  posthog.capture("plan_changed", {
    type,
    from_plan: fromPlan,
    to_plan: toPlan,
    prorated_amount: proratedAmount,
  });
}

/**
 * Track: Scheduled downgrade cancelled
 */
export function trackDowngradeCancelled(plan: string) {
  if (!initialized) return;
  posthog.capture("downgrade_cancelled", { plan });
}

/**
 * Track: Post-purchase confirmation modal shown. Fired once per successful
 * upgrade/purchase the instant the prominent confirmation surfaces — never on a
 * cancelled or failed checkout. `scenario` is the resolved key
 * (new_subscription | plan_upgrade | monthly_credits_increase | plan_active |
 * purchase_credits).
 */
export function trackPurchaseConfirmationShown(props: {
  scenario: string;
  kind: string;
  plan?: string | null;
  monthlyCredits?: number | null;
  creditsAdded?: number | null;
  priceUsd?: number | null;
  source: string;
  projectId?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("post_purchase_confirmation_shown", {
    scenario: props.scenario,
    kind: props.kind,
    plan: props.plan ?? null,
    monthly_credits: props.monthlyCredits ?? null,
    credits_added: props.creditsAdded ?? null,
    price_usd: props.priceUsd ?? null,
    source: props.source,
    project_id: props.projectId ?? null,
    ...getUtmProperties(),
  });
}

/**
 * Track: Primary CTA clicked on the post-purchase confirmation modal.
 */
export function trackPurchaseConfirmationCtaClicked(props: {
  scenario: string;
  kind: string;
  plan?: string | null;
  ctaLabel: string;
  source: string;
  projectId?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("post_purchase_confirmation_cta_clicked", {
    scenario: props.scenario,
    kind: props.kind,
    plan: props.plan ?? null,
    cta_label: props.ctaLabel,
    source: props.source,
    project_id: props.projectId ?? null,
    ...getUtmProperties(),
  });
}

/**
 * Track: Credit limit reached — upgrade modal shown
 */
export function trackCreditLimitHit(projectId: string, blockedAction: string) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("credit_limit_hit", {
    project_id: projectId,
    blocked_action: blockedAction,
    ...utm,
  });
}

/**
 * A suggested "Build this next" phase was stopped by CREDITS, not by a defect.
 *
 * The reason this is its own event: a phase blocked by the credit gate used to
 * be indistinguishable from a phase that genuinely broke. It settled the build
 * card as failed and stamped the step "This step didn't finish", so in the data
 * it looked exactly like a codegen, preview, sandbox or GitHub failure — which
 * makes the build-failure rate wrong in one direction and the paywall-conversion
 * funnel blind in the other. Emitting it separately (and never emitting a build
 * failure for it) is what keeps those two numbers honest.
 *
 * `stage` is the half the ticket cares about most:
 *   • "pre_flight"  — /api/credits/check turned it away BEFORE any build state
 *     appeared. The intended path: no tracker, no card, no failure.
 *   • "reservation" — it got past the pre-flight and /api/ai/generate 402'd it.
 *     Still safe (nothing is reserved or charged), but it means the tracker had
 *     already opened, so a non-zero rate here is the regression signal for the
 *     gate drifting out of step with the reservation again.
 */
export function trackPhaseBuildBlocked(props: {
  projectId: string;
  stage: "pre_flight" | "reservation";
  /** The 402 payload's reason (pool_empty / insufficient_balance /
   *  daily_cap_reached), when the block reported one. */
  reason?: string | null;
  plan?: string | null;
  /** 1-based index of the phase that was blocked, and how many the plan has. */
  phaseIndex?: number | null;
  phaseTotal?: number | null;
  phaseTitle?: string | null;
  creditsRemaining?: number | null;
  /** What the gate said was needed to start, when it reported one. */
  requiredCredits?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("phase_build_blocked", {
    project_id: props.projectId,
    stage: props.stage,
    // Fixed marker so this can never be counted as a build/preview/sandbox/
    // GitHub failure in any dashboard that groups by failure kind.
    block_kind: "credits",
    reason: props.reason ?? null,
    plan: props.plan ?? null,
    phase_index: props.phaseIndex ?? null,
    phase_total: props.phaseTotal ?? null,
    phase_title: props.phaseTitle ?? null,
    credits_remaining: props.creditsRemaining ?? null,
    required_credits: props.requiredCredits ?? null,
    ...getDeviceContext(),
  });
}

/**
 * Contextual-repair funnel (PANDA-1699). One event per stage of "a generated
 * app broke → we offered a way forward → the user upgraded → the app was
 * repaired", so the high-intent broken-app moment is measurable end to end:
 *   - error_shown        : a break surfaced a recovery UI
 *   - repair_offered     : we classified the repair (free retry vs upgrade)
 *   - repair_attempted   : the user actually tapped the retry CTA
 *   - upgrade_modal_shown: a contextual upgrade wall opened from the repair CTA
 *   - upgrade_started    : the user began checkout from that wall
 *   - repair_completed   : the previously-failed step succeeded
 *   - repair_exhausted   : the retry ladder ran out; we stopped offering it
 *
 * Emitted as one event name with a `stage` prop (not five names) so the funnel
 * is a single PostHog insight. `repair_class` / `capability` / `blocked_action`
 * carry the classifier's decision so free vs paid repair paths are separable.
 *
 * `repair_attempted` and `repair_exhausted` were added for the animal-atlas
 * retry loop: the funnel could see that a repair was OFFERED and that one
 * eventually COMPLETED, but not how many times the user tapped in between, nor
 * that a phase had stopped being retryable at all. A loop is invisible in a
 * funnel whose only two states are "offered" and "done". The phase/retry props
 * below make one phase's escalation (replay → narrow → split → exhausted) a
 * single readable sequence.
 */
export type AppRepairStage =
  | "error_shown"
  | "repair_offered"
  | "repair_attempted"
  | "upgrade_modal_shown"
  | "upgrade_started"
  | "repair_completed"
  | "repair_exhausted";

export function trackAppRepairFunnel(
  stage: AppRepairStage,
  props: {
    projectId?: string | null;
    /** "free_retry" | "capability_upgrade" | "external_setup" */
    repairClass?: string;
    /** Taxonomy capability id the break hinged on, when any. */
    capability?: string | null;
    /** How the app broke: phase | build | runtime | deploy | generation. */
    errorKind?: string;
    /** Paywall block the contextual wall names, when an upgrade path applies. */
    blockedAction?: string | null;
    reason?: string;
    /** The build phase that broke, and where it sits in the plan. */
    phaseTitle?: string | null;
    phaseIndex?: number | null;
    phaseTotal?: number | null;
    /** 1-based attempts already spent on this phase. */
    retryAttempt?: number | null;
    /** What the next attempt would change: replay | narrow | split | exhausted. */
    retryStrategy?: string | null;
    /** runPatchRequest's cause: truncated | server | stream | connect | http |
     *  duplicate. Null when the failure carried no classification. */
    failureCause?: string | null;
    /** How the user arrived at this repair: the suggested next build, a retry
     *  tap, or a reload that restored the card. */
    entry?: "suggested_next_build" | "retry" | "restore" | null;
    /** The app's own name, so a report naming an app is findable without
     *  joining. NOT the user's email — person props already carry that. */
    projectName?: string | null;
  } = {},
) {
  if (!initialized) return;
  posthog.capture("app_repair_funnel", {
    stage,
    project_id: props.projectId ?? null,
    repair_class: props.repairClass,
    capability: props.capability ?? null,
    error_kind: props.errorKind,
    blocked_action: props.blockedAction ?? null,
    reason: props.reason,
    phase_title: props.phaseTitle ?? null,
    phase_index: props.phaseIndex ?? null,
    phase_total: props.phaseTotal ?? null,
    retry_attempt: props.retryAttempt ?? null,
    retry_strategy: props.retryStrategy ?? null,
    failure_cause: props.failureCause ?? null,
    entry: props.entry ?? null,
    project_name: props.projectName ?? null,
    ...getUtmProperties(),
  });
}

/**
 * Track: User clicked upgrade from the credit limit modal
 */
export function trackUpgradeClicked(planId: string, projectId: string, blockedAction: string) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("upgrade_clicked", {
    plan: planId,
    project_id: projectId,
    blocked_action: blockedAction,
    ...utm,
  });
}

/**
 * Track: User dismissed the upgrade modal without upgrading
 */
export function trackUpgradeDismissed(projectId: string, blockedAction: string) {
  if (!initialized) return;
  posthog.capture("upgrade_dismissed", {
    project_id: projectId,
    blocked_action: blockedAction,
  });
}

/**
 * Track: owner clicked the "Inactive" pill on /projects, which opens the plan
 * wall. Separate from `upgrade_clicked` so the inactivation funnel
 * (app_inactivated → pill click → plan purchased → app_reactivated) can be
 * measured on its own.
 */
export function trackAppInactivePillClicked(projectId: string) {
  if (!initialized) return;
  posthog.capture("app_inactive_pill_clicked", {
    project_id: projectId,
  });
}

/**
 * Track: the one-time free-app heads-up shown after a project's first live
 * publish (the 24h window + the app's own deadline).
 *
 * Kept off `grow_modal` even though it rides the same post-publish timer: this
 * is the WARNING half of the funnel and its numbers answer a different question
 * — does telling a free user up front that their app expires convert them, or
 * only cost us the publish moment? Mixing it into the Grow events would make
 * neither readable.
 */
export function trackAppInactivationNotice(
  action: "shown" | "dismissed" | "upgrade_clicked",
  projectId: string,
) {
  if (!initialized) return;
  posthog.capture("app_inactivation_notice", {
    action,
    project_id: projectId,
  });
}

/**
 * Track: Power user card interaction (3rd build prompt).
 * action: "auto" (10s timeout), "manual" (X button), "click" (upgrade clicked)
 */
export function trackPowerUserCard(action: "auto" | "manual" | "click", buildCount: number) {
  if (!initialized) return;
  posthog.capture("power_user_card", {
    action,
    build_count: buildCount,
  });
}

/**
 * Track: credit_exhausted — user hit zero credits.
 * Used for measuring impatience trigger performance and upgrade conversion from limit state.
 */
export function trackCreditExhausted(properties: {
  user_id: string;
  plan_type: string;
  session_build_count: number;
  reset_time_hours_away: number | null;
}) {
  if (!initialized) return;
  const now = new Date();
  const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  posthog.capture("credit_exhausted", {
    ...properties,
    time_of_day: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
    day_of_week: dayNames[now.getDay()],
  });
}

/**
 * Track: upgrade_prompt_shown — any upgrade trigger displayed to user.
 * trigger_type: "impatience" | "power_user" | "expansion" (topup converter)
 */
export function trackUpgradePromptShown(properties: {
  trigger_type: "impatience" | "power_user" | "expansion";
  user_id: string;
  plan_type: string;
}) {
  if (!initialized) return;
  posthog.capture("upgrade_prompt_shown", properties);
}

/**
 * Track: grow_modal — the Grow-suggestion ("Your app is ready") modal funnel.
 * action: "shown" | "dismissed" | "cta_clicked" | "suppressed"
 * trigger: the readiness signal that armed the modal —
 *   "published" | "follow_up_prompts" | "returning_user"
 * reason (on "suppressed" only):
 *   - "grow_promo_off" — the Grow rollout gate is closed for this environment
 *     (Grow is WIP, see lib/growth/grow-rollout.ts). The chain reached the point
 *     where it WOULD have pitched Grow and stood down instead.
 *
 * Lets us measure view → click → Grow activation per trigger (PANDA-987), and
 * — since the gate landed — tell a suppressed pitch apart from a broken trigger.
 * `suppressed` is deliberately NOT an impression: keep it out of the view→click
 * funnel or a closed gate reads as a collapse in click-through rate.
 */
export function trackGrowModal(
  action: "shown" | "dismissed" | "cta_clicked" | "suppressed",
  projectId: string,
  trigger: string,
  reason?: "grow_promo_off",
) {
  if (!initialized) return;
  posthog.capture("grow_modal", {
    action,
    project_id: projectId,
    trigger,
    ...(reason ? { reason } : {}),
  });
}

/**
 * Track: domain_modal — the post-publish custom-domain step (step 1 of the Grow
 * chain). Its trigger is deliberately gated (1st publish then every 5th, manual
 * publishes only, and skipped when a domain is already connected), so "it didn't
 * show" is usually expected — but with only a `shown` event we could never tell
 * an intended suppression from a real regression from a session replay. This
 * makes the DECISION observable: every qualifying publish emits
 * `eligibility_evaluated`, and each gate that stands the modal down emits
 * `suppressed` with a machine `reason`, so the funnel explains itself.
 *
 * action:
 *   - "eligibility_evaluated" — a manual publish was assessed for the domain step
 *   - "shown"                 — the domain modal opened
 *   - "suppressed"            — it stood down; see `reason`
 *   - "dismissed"             — closed without connecting a domain
 *   - "purchase_clicked"      — the user clicked buy/connect on a domain
 *
 * reason (on "suppressed"):
 *   - "not_on_cadence"           — publish # is not 1 or a multiple of 5
 *   - "grow_mode_active"         — owner is already in Grow mode
 *   - "domain_already_connected" — app already has a domain (app-live shown instead)
 *   - "non_manual_trigger"       — auto / mobile-preview / badge deploy, not a user publish
 */
export function trackDomainModal(
  action:
    | "eligibility_evaluated"
    | "shown"
    | "suppressed"
    | "dismissed"
    | "purchase_clicked",
  projectId: string,
  properties?: {
    publish_count?: number;
    trigger?: string | null;
    eligible?: boolean;
    reason?:
      | "not_on_cadence"
      | "grow_mode_active"
      | "domain_already_connected"
      | "non_manual_trigger";
    domain?: string;
  },
) {
  if (!initialized) return;
  posthog.capture("domain_modal", {
    action,
    project_id: projectId,
    ...properties,
  });
}

/**
 * Track: grow_started — Grow activation completed and the canvas went active.
 */
export function trackGrowStarted(projectId?: string) {
  if (!initialized) return;
  posthog.capture("grow_started", { project_id: projectId });
}

/**
 * Track: grow_progress_step_started — an activation progress step began
 * running (one event per distinct step, e.g. icp / firstDraft / landingPage).
 */
export function trackGrowProgressStepStarted(properties: {
  step: string;
  project_id?: string;
}) {
  if (!initialized) return;
  posthog.capture("grow_progress_step_started", properties);
}

/**
 * Track: upgrade_prompt_converted — user clicked upgrade from a trigger prompt.
 */
export function trackUpgradePromptConverted(properties: {
  trigger_type: "impatience" | "power_user" | "expansion";
  user_id: string;
  plan_type: string;
  plan_selected: string;
  revenue_amount?: number;
}) {
  if (!initialized) return;
  posthog.capture("upgrade_prompt_converted", properties);
}

/**
 * Track: low_credits_modal_shown — the low-credits refill nudge appeared.
 * Only fires for engaged users (paid plan or prior top-up) under the
 * warn threshold; impression counts feed the conversion funnel.
 */
export function trackLowCreditsModalShown(properties: {
  surface: "build" | "grow";
  plan: string;
  credits_remaining: number;
  has_topped_up: boolean;
}) {
  if (!initialized) return;
  posthog.capture("low_credits_modal_shown", properties);
}

/**
 * Track: low_credits_modal_topup_clicked — user clicked "Top up credits"
 * from the low-credits nudge (opens the AddCreditsModal).
 */
export function trackLowCreditsModalTopUpClicked(properties: {
  surface: "build" | "grow";
  plan: string;
  credits_remaining: number;
}) {
  if (!initialized) return;
  posthog.capture("low_credits_modal_topup_clicked", properties);
}

/**
 * Track: low_credits_modal_dismissed — user closed the low-credits nudge
 * without topping up ("Remind me later" / X / backdrop).
 */
export function trackLowCreditsModalDismissed(properties: {
  surface: "build" | "grow";
  plan: string;
  credits_remaining: number;
}) {
  if (!initialized) return;
  posthog.capture("low_credits_modal_dismissed", properties);
}

/**
 * Which credit-exhaustion surface an event came from.
 *
 * The exhausted-credit state has two of them — the slim bar docked above the
 * composer and the full-screen upgrade takeover — and until this existed all
 * four events below were emitted by both, unlabelled. That made the counts
 * uninterpretable in two ways: one exhaustion episode could log two impressions
 * (inflating the denominator), and no conversion rate could be attributed to the
 * surface that actually earned it. Required, not optional, so a new caller has to
 * say which surface it is.
 */
export type CreditExhaustionSurface =
  /** The slim bar docked above the chat composer. */
  | "inline_bar"
  /** The full-screen upgrade takeover. */
  | "takeover";

/**
 * Track: credit_exhausted_state_shown — the dedicated "out of credits for
 * today" choice screen (reset timer + Buy Credits / Upgrade / Come Back
 * Tomorrow) was shown. Fired once per open, only after a blocked AI action
 * (never as a passive wall). reset_hours_away is null when no daily reset
 * applies (e.g. the free window has expired).
 */
export function trackCreditExhaustedStateShown(properties: {
  project_id: string;
  plan: string;
  reset_hours_away: number | null;
  surface: CreditExhaustionSurface;
}) {
  if (!initialized) return;
  posthog.capture("credit_exhausted_state_shown", properties);
}

/**
 * Track: credit_topup_clicked — user chose "Buy Credits" from the
 * exhaustion state (opens the top-up flow).
 */
export function trackCreditTopupClicked(properties: {
  project_id: string;
  plan: string;
  surface: CreditExhaustionSurface;
}) {
  if (!initialized) return;
  posthog.capture("credit_topup_clicked", properties);
}

/**
 * Track: credit_upgrade_clicked — user chose "Upgrade" from the
 * exhaustion state (opens the plans flow).
 */
export function trackCreditUpgradeClicked(properties: {
  project_id: string;
  plan: string;
  surface: CreditExhaustionSurface;
}) {
  if (!initialized) return;
  posthog.capture("credit_upgrade_clicked", properties);
}

/**
 * Track: upgrade_cta_clicked — a free user clicked an upgrade-only CTA that
 * opens the shared "Choose a plan" flow. `source` names the surface it launched
 * from (e.g. "settings_usage") so we can attribute conversions per surface.
 *
 * NOTE: renamed from `trackUpgradeCtaClicked` to avoid a name collision with the
 * newer upgrade-funnel helper of that name (UpgradeEventProps signature, above).
 * The captured event name (`upgrade_cta_clicked`) and props are unchanged, so
 * existing dashboards are untouched.
 */
export function trackChoosePlanCtaClicked(properties: {
  source: string;
  plan: string;
}) {
  if (!initialized) return;
  posthog.capture("upgrade_cta_clicked", properties);
}

/**
 * Track: credit_wait_selected — user chose "Come Back Tomorrow" from the
 * exhaustion state (dismisses; will return after the daily reset).
 */
export function trackCreditWaitSelected(properties: {
  project_id: string;
  plan: string;
  reset_hours_away: number | null;
  surface: CreditExhaustionSurface;
}) {
  if (!initialized) return;
  posthog.capture("credit_wait_selected", properties);
}

/**
 * Track: Badge click — visitor clicked "Remix with Panda" in a generated app
 */
export function trackBadgeClick(projectId: string) {
  if (!initialized) return;
  const utm = getUtmProperties();
  posthog.capture("badge_clicked", {
    project_id: projectId,
    ...utm,
  });
}

/**
 * Track: the owner clicked the X / remove affordance on the "Remix with Panda"
 * badge overlay in their workspace preview (PANDA-1695 remove-branding entry
 * point). DISTINCT from `badge_clicked` above, which is a site VISITOR clicking
 * the live badge link — this is the project OWNER trying to remove the badge.
 * The overlay only renders for non-paid plans, so `plan` here is a free/legacy
 * tier; it opens the remove-branding paywall (see `badge_remove_paywall_shown`).
 */
export function trackBadgeRemoveClicked(projectId: string, plan: string) {
  if (!initialized) return;
  posthog.capture("badge_remove_clicked", {
    project_id: projectId,
    plan,
    ...getUtmProperties(),
  });
}

/**
 * Track: the remove-branding paywall was shown as a result of the badge X
 * click. Mirrors the contextual `*_shown` convention (trackCreditGateShown et
 * al.) — the triggering surface fires its own "shown" event rather than the
 * modal firing one on mount. The downstream "upgrade started"
 * (`checkout_initiated`) and "upgrade completed" (`billing_purchase_completed`)
 * events are attributed to this funnel automatically via the `badge_overlay`
 * source / `remove-branding` intent carried into checkout.
 */
export function trackBadgeRemovePaywallShown(projectId: string, plan: string) {
  if (!initialized) return;
  posthog.capture("badge_remove_paywall_shown", {
    project_id: projectId,
    plan,
    ...getUtmProperties(),
  });
}

/**
 * Track: the "Remix with Panda" badge was shown to the project OWNER in their
 * workspace preview. Fired once per project when the overlay becomes visible
 * (PANDA badge-auto-removal fix). `plan` is the owner's current tier — with the
 * fix the badge shows by DEFAULT on every tier (paid users who kept it, free
 * users, trials) and only hides once a paid user explicitly removes it, so this
 * event now spans all plans rather than free-only.
 */
export function trackBadgeShown(projectId: string, plan: string) {
  if (!initialized) return;
  posthog.capture("badge_shown", {
    project_id: projectId,
    plan,
    ...getUtmProperties(),
  });
}

/**
 * Track: a paid user actually REMOVED the Panda badge via an explicit action.
 * This is the "removed by user action" outcome the acceptance criteria calls
 * for, and is the ONLY way a badge should ever disappear after conversion
 * (upgrading/trialing no longer strips it automatically).
 *
 * `source` attributes which affordance was used. Only "settings" — the nested
 * badge disclosure in project settings — can produce a removal today; the badge
 * no longer carries a close/remove X, and the "badge_x" source it emitted is
 * retired. Historical `badge_removed_by_user` events still carry that value, so
 * read the property as an open set when querying the funnel.
 */
export function trackBadgeRemovedByUser(
  projectId: string,
  plan: string,
  source: "settings",
) {
  if (!initialized) return;
  posthog.capture("badge_removed_by_user", {
    project_id: projectId,
    plan,
    source,
    ...getUtmProperties(),
  });
}

/**
 * Track: the badge-removal SETTING was changed from the Settings/Publish toggle.
 * Fires in BOTH directions (`removed` true = hidden, false = restored) so the
 * funnel can see restores as well as removals — distinct from
 * `badge_removed_by_user`, which only fires on the removal outcome.
 */
export function trackBadgeRemovalSettingChanged(
  projectId: string,
  plan: string,
  removed: boolean,
) {
  if (!initialized) return;
  posthog.capture("badge_removal_setting_changed", {
    project_id: projectId,
    plan,
    removed,
    ...getUtmProperties(),
  });
}

/**
 * Track: Code generation iteration (chat message that triggers AI)
 */
export function trackBuildIteration(projectId: string, templateId: string, isFirst: boolean) {
  if (!initialized) return;
  posthog.capture("build_iteration", {
    project_id: projectId,
    template_id: templateId,
    is_first_build: isFirst,
  });
}

/**
 * Track: AI generation completed successfully within a session.
 * Logged for cohort analysis of session build counts.
 */
export function trackSessionBuildComplete(
  sessionToken: string,
  buildNumber: number,
  projectId: string,
  templateId: string,
) {
  if (!initialized) return;
  posthog.capture("session_build_complete", {
    session_token: sessionToken,
    build_number: buildNumber,
    project_id: projectId,
    template_id: templateId,
  });
}

// ──────────────────────────────────────────────────
// Homepage onboarding funnel (Phase 1)
// ──────────────────────────────────────────────────

/**
 * Track: the homepage was shown to a visitor. GT-03's denominator.
 *
 * Every other homepage event answers "how many people did X". None of them
 * answered "out of how many", so a copy change that doubled traffic and halved
 * conversion looked like a win: 50 signup-starts became 80 while the RATE fell
 * from 5% to 4%. This event is the missing bottom half of that fraction, which
 * is why the ticket's KPI is a *rate* rather than a count.
 *
 * `$pageview` cannot stand in for it. posthog-js fires that automatically at
 * init, before this page knows which copy it rendered or whether a session
 * exists, so it can carry neither property — and it counts every route in the
 * app, not this page.
 *
 * Fires for logged-in visitors too, tagged rather than filtered: they land here
 * as well, and dropping them would silently change the denominator whenever the
 * signed-in/signed-out mix moved.
 */
export function trackHomepageViewed(props: {
  /** Which hero copy was on screen — see heroVariantId in lib/hero-markup.ts. */
  variant: string;
  isLoggedIn: boolean;
}) {
  if (!initialized) return;
  posthog.capture("homepage_viewed", {
    homepage_variant: props.variant,
    is_logged_in: props.isLoggedIn,
    source: "homepage",
    ...getDeviceContext(),
  });
}

/**
 * Track: User selected a homepage onboarding path by clicking a hero tab.
 * The heroMode value "business" maps to the spec's `path: "existing_business"`.
 */
export function trackHomepagePathSelected(path: "idea" | "existing_business") {
  if (!initialized) return;
  posthog.capture("homepage_path_selected", {
    path,
    ...getDeviceContext(),
  });
}

/**
 * Track: User submitted an AI-builder prompt from the homepage ("idea" path).
 * Fires before the signup/redirect gates so abandoned attempts are captured.
 */
export function trackHomepagePromptSubmitted(promptLength: number) {
  if (!initialized) return;
  posthog.capture("homepage_prompt_submitted", {
    path: "idea",
    prompt_length: promptLength,
    source: "homepage",
  });
}

/**
 * Track: User submitted a business URL from the homepage ("existing_business"
 * / growth path). Fires before the signup gate so abandoned attempts count.
 */
export function trackHomepageBusinessUrlSubmitted(domain: string) {
  if (!initialized) return;
  posthog.capture("homepage_business_url_submitted", {
    path: "existing_business",
    has_url: true,
    domain,
    source: "homepage",
  });
}

/**
 * Track: User typed into the homepage prompt box (top-of-funnel intent). Fires
 * once per page load on the first non-empty keystroke — NOT on chip/template/
 * clone fills, which set the value programmatically and have their own events.
 * The caller (homepage) guards the once-per-load semantics.
 */
export function trackHomepagePromptTyped() {
  if (!initialized) return;
  posthog.capture("homepage_prompt_typed", {
    source: "homepage",
    ...getDeviceContext(),
  });
}

/**
 * How a "Clone this" click resolved. `real_fork` copies the source project's
 * files into a new project; `prompt_prefill` only dropped the build's prompt
 * text into the hero box. The homepage shipped `prompt_prefill` under a
 * "Clone this" label, so historical `homepage_clone_clicked` events carry no
 * `mode` and must NOT be read as real clones.
 */
export type HomepageCloneMode = "real_fork" | "prompt_prefill";

/**
 * Track: User clicked "Clone this" on a "Built with Panda" community build
 * card. Fired at click time — before the auth/credit gates — so the funnel
 * still counts attempts that bounce to login. Pair with
 * `homepage_clone_failed` to measure how many attempts actually land.
 */
export function trackHomepageCloneClicked(
  projectId: string,
  title: string,
  mode: HomepageCloneMode,
) {
  if (!initialized) return;
  posthog.capture("homepage_clone_clicked", {
    project_id: projectId,
    title,
    mode,
    source: "homepage",
    ...getDeviceContext(),
  });
}

/**
 * Which homepage control started the clone. `built_with_panda` and
 * `community_card` fork a source PROJECT by id; `homepage_template` clones a
 * curated TEMPLATE by slug. They fail for different reasons and convert at
 * different rates, so every clone-outcome event carries the surface.
 */
export type HomepageCloneSurface =
  | "built_with_panda"
  | "community_card"
  | "homepage_template";

interface HomepageCloneTarget {
  /** Set for the fork-by-id surfaces; null for a template clone. */
  projectId?: string | null;
  /** Set for the homepage template surface; null for a fork. */
  templateSlug?: string | null;
  surface: HomepageCloneSurface;
}

/** Shared identity + context every clone-outcome event carries, so the three
 *  events below can be funnelled against each other without joins. */
function homepageCloneBaseProps(target: HomepageCloneTarget) {
  return {
    project_id: target.projectId ?? null,
    template_slug: target.templateSlug ?? null,
    surface: target.surface,
    source: "homepage",
    // Explicit (rather than relying on PostHog's auto-captured `$current_url`)
    // because the paid-acquisition breakdown for this funnel is read off the
    // campaign params in the landing URL.
    current_url:
      typeof window !== "undefined" ? (window.location.href ?? null) : null,
    ...getUtmProperties(),
    ...getDeviceContext(),
  };
}

/**
 * Track: a clone attempt from the homepage did not produce a project.
 *
 * ONLY for real failures. An interruption the user can come back from — the
 * email-verification gate — is NOT a failure and must fire
 * `trackHomepageCloneVerificationRequired` instead; conflating the two made a
 * recoverable hand-off look like a dead conversion (PANDA-1618).
 *
 * `reason` is the API's error code (credit_limit_reached,
 * project_limit_reached, source_not_clonable, …) or "unknown". `error` is the
 * human-readable detail and is guaranteed non-null so a failure event is never
 * silent about what actually went wrong.
 */
export function trackHomepageCloneFailed(
  target: HomepageCloneTarget,
  reason: string,
  error?: string | null,
) {
  if (!initialized) return;
  const safeReason = reason?.trim() || "unknown";
  posthog.capture("homepage_clone_failed", {
    ...homepageCloneBaseProps(target),
    reason: safeReason,
    // Never null on a real failure — fall back to the reason code rather than
    // emitting an empty field (AC: non-null `error`).
    error: error?.trim() || safeReason,
    ...failureContext(),
  });
}

/**
 * Track: a homepage clone paused because the account still has to verify its
 * email. Distinct from `homepage_clone_failed` — nothing failed, the clone is
 * parked and resumes once the magic link is clicked.
 *
 * `resume_persisted` reports whether the intent actually made it into storage
 * (localStorage throws in private browsing / on quota), so a drop-off caused by
 * a failed stash is visible rather than looking like the user gave up.
 */
export function trackHomepageCloneVerificationRequired(
  target: HomepageCloneTarget,
  resumePersisted: boolean,
) {
  if (!initialized) return;
  posthog.capture("homepage_clone_verification_required", {
    ...homepageCloneBaseProps(target),
    reason: "email_verification_required",
    resume_persisted: resumePersisted,
  });
}

/**
 * Track: a clone parked by the verification gate actually resumed. `mode` is
 * `auto` when the gate's own post-verification reload replayed it, `manual`
 * when the user tapped Resume on a later visit. Paired with
 * `homepage_clone_verification_required`, this is the recovery rate — the
 * number that says whether verification still drops paid traffic.
 */
export function trackHomepageCloneVerificationResumed(
  target: HomepageCloneTarget,
  mode: "auto" | "manual",
) {
  if (!initialized) return;
  posthog.capture("homepage_clone_verification_resumed", {
    ...homepageCloneBaseProps(target),
    mode,
  });
}

/**
 * How a "your app was cloned" notification reached its creator. `in_app` is the
 * growth_notifications row — the only delivery for an owner with no push
 * subscription who has also opted out of email.
 */
export type CloneNotificationChannel = "push" | "email" | "in_app";

/**
 * Track: a creator clicked through from the "your app was cloned" notification.
 *
 * The ticket's headline CTR metric. Fired on arrival at /projects, read off the
 * `src`/`channel` params the notification's deep link carries, because a push
 * click happens inside the service worker where PostHog does not exist — the
 * landing IS the only place the browser can report it.
 *
 * The email channel is NOT reported here: Resend's click webhook already fires
 * this same event server-side (app/api/webhooks/resend/route.ts), and counting
 * it in both places would double every email click. The link's `channel=email`
 * param is what suppresses it.
 */
export function trackCloneNotificationClicked(
  channel: CloneNotificationChannel,
  appId: string,
) {
  if (!initialized) return;
  posthog.capture("clone_notification_clicked", {
    channel,
    app_id: appId,
    ...getDeviceContext(),
  });
}

/**
 * Track: the "Cloned N×" badge was rendered on a project card.
 *
 * The passive half of the trigger — it measures whether creators ever see their
 * clone count at all, including the ones who never received (or never opened) a
 * notification. That cohort is the control group the KPI compares against, so
 * it has to be observable on its own.
 *
 * Callers must dedupe per app per page view; this fires whatever it is given.
 */
export function trackCloneBadgeViewed(appId: string, cloneCount: number) {
  if (!initialized) return;
  posthog.capture("clone_badge_viewed", {
    app_id: appId,
    clone_count: cloneCount,
  });
}

/**
 * Track: User clicked "View live site" on a "Built with Panda" community build
 * card (opens the real deployed URL in a new tab).
 */
export function trackHomepageViewLiveClicked(projectId: string, url: string) {
  if (!initialized) return;
  posthog.capture("homepage_view_live_clicked", {
    project_id: projectId,
    url,
    source: "homepage",
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Community template preview (GT-02 — the gallery preview must never hand a
// visitor a 404). The pair below is the whole KPI: error rate is
// template_preview_error ÷ template_preview_viewed, targeted at 0%. Both carry
// project_id and remix_count so the highest-traffic templates — the ones whose
// breakage costs the most organic reach — can be read off on their own.
// ──────────────────────────────────────────────────

/** Where a template preview was opened from. Only the gallery today; the
 *  homepage's "Built with Panda" band is the obvious next surface, and naming
 *  it now keeps the two comparable rather than needing a backfill. */
export type TemplatePreviewSurface = "community_gallery" | "homepage";

interface TemplatePreviewContext {
  projectId: string;
  title: string;
  remixCount: number;
  surface: TemplatePreviewSurface;
}

function templatePreviewProps(ctx: TemplatePreviewContext) {
  return {
    project_id: ctx.projectId,
    template_title: ctx.title,
    remix_count: ctx.remixCount,
    surface: ctx.surface,
    ...getDeviceContext(),
  };
}

/**
 * Track: a visitor opened a community template's preview.
 *
 * The denominator of the error rate, so it fires on EVERY open — including the
 * ones that go on to fail. Fired once per open, before the outcome is known.
 */
export function trackTemplatePreviewViewed(ctx: TemplatePreviewContext) {
  if (!initialized) return;
  posthog.capture("template_preview_viewed", templatePreviewProps(ctx));
}

/**
 * Why a preview could not be shown. Mirrors the server's health reasons plus the
 * two the client discovers on its own, so a spike can be attributed without
 * cross-referencing logs:
 *   - `http_not_found` / `http_server_error` / `error_page` / `blank_body` /
 *     `empty_document` — the health probe caught a dead deployment.
 *   - `inactive` — the app was deliberately taken dark (free-plan window).
 *   - `not_published` — the row carries no framable URL at all.
 *   - `frame_blocked` — the iframe never loaded (X-Frame-Options / CSP), which
 *     only the client can observe.
 *   - `probe_failed` — we could not even ask; counted so a broken probe never
 *     looks like a clean run.
 */
export type TemplatePreviewErrorReason =
  | "http_not_found"
  | "http_server_error"
  | "error_page"
  | "blank_body"
  | "empty_document"
  | "inactive"
  | "not_published"
  | "frame_blocked"
  | "probe_failed";

/**
 * Track: a community template preview failed to show the live site.
 *
 * Fired whether or not the visitor sees a screenshot in its place — a degraded
 * preview is still a broken preview for KPI purposes, and counting only the
 * screenshot-less case would hide the failure behind the fallback. `http_status`
 * is the status the server observed (null when the failure was client-side).
 */
export function trackTemplatePreviewError(
  ctx: TemplatePreviewContext & {
    reason: TemplatePreviewErrorReason;
    httpStatus: number | null;
    /** True when the screenshot stood in, so the visitor still saw the design. */
    fellBackToScreenshot: boolean;
  },
) {
  if (!initialized) return;
  posthog.capture("template_preview_error", {
    ...templatePreviewProps(ctx),
    reason: ctx.reason,
    http_status: ctx.httpStatus,
    fell_back_to_screenshot: ctx.fellBackToScreenshot,
  });
}

// ──────────────────────────────────────────────────
// Pricing page CTA funnel (PANDA-1009 — pricing CTAs must reflect auth +
// plan state and never dead-end). Every event carries the same context so
// the funnel can be segmented by auth / plan / device without joins.
// device_type (+ os / browser) is appended via getDeviceContext().
// ──────────────────────────────────────────────────

interface PricingCtaContext {
  is_logged_in: boolean;
  current_plan: string;
  selected_plan?: string | null;
  cta_label?: string | null;
}

function pricingCtaProps(ctx: PricingCtaContext) {
  return {
    is_logged_in: ctx.is_logged_in,
    current_plan: ctx.current_plan,
    selected_plan: ctx.selected_plan ?? null,
    cta_label: ctx.cta_label ?? null,
    ...getDeviceContext(),
  };
}

/**
 * Which pricing surface an event came from. The ticket names two — the Pro
 * pricing card and the compare view, both of which live inside the in-app plan
 * flow — and the public marketing page is a third that carries the same toggle,
 * so it gets its own value rather than being folded into either.
 */
export type PricingSurface = "pro_pricing" | "compare_plans" | "pricing_page";

export type BillingCadenceValue = "monthly" | "annual";

/** Track: a pricing surface was viewed (fires once per visit to that surface).
 *  `default_cadence` is the interval the surface OPENED on — the persisted
 *  preference, which is not always the product default — so an annual-mix
 *  reading can separate "chose annual" from "was shown annual". */
export function trackPricingPageViewed(ctx: {
  is_logged_in: boolean;
  current_plan: string;
  surface: PricingSurface;
  default_cadence: BillingCadenceValue;
}) {
  if (!initialized) return;
  posthog.capture("pricing_page_viewed", {
    is_logged_in: ctx.is_logged_in,
    current_plan: ctx.current_plan,
    surface: ctx.surface,
    default_cadence: ctx.default_cadence,
    ...getDeviceContext(),
  });
}

/** Track: any pricing CTA was clicked (plan card or the bottom free card). */
export function trackPricingCtaClicked(ctx: PricingCtaContext) {
  if (!initialized) return;
  posthog.capture("pricing_cta_clicked", pricingCtaProps(ctx));
}

/** Track: the monthly/annual billing toggle was switched. */
export function trackBillingToggleClicked(ctx: {
  billing: "monthly" | "annual";
  is_logged_in: boolean;
  current_plan: string;
}) {
  if (!initialized) return;
  posthog.capture("billing_toggle_clicked", {
    billing: ctx.billing,
    is_logged_in: ctx.is_logged_in,
    current_plan: ctx.current_plan,
    ...getDeviceContext(),
  });
}

/**
 * Track: the monthly/annual toggle was switched on a pricing surface.
 *
 * Distinct from `billing_toggle_clicked` above, which pre-dates the shared
 * cadence work: it fires only from the marketing page and records neither the
 * surface nor what the user switched FROM, so it can't answer "what share of
 * viewers move to annual, and where". That event keeps firing untouched so the
 * dashboards built on it don't break.
 */
export function trackPricingToggleSwitched(ctx: {
  surface: PricingSurface;
  from_cadence: BillingCadenceValue;
  to_cadence: BillingCadenceValue;
}) {
  if (!initialized) return;
  posthog.capture("pricing_toggle_switched", {
    surface: ctx.surface,
    from_cadence: ctx.from_cadence,
    to_cadence: ctx.to_cadence,
    ...getDeviceContext(),
  });
}

/** Track: a plan's purchase CTA was clicked, with the cadence it was clicked
 *  under. Fires on the CLICK — before any trial/auth gate — so a blocked click
 *  still counts as intent. `checkout_started` is what confirms we got as far as
 *  asking Stripe. */
export function trackPlanCtaClicked(ctx: {
  surface: PricingSurface;
  plan: string;
  cadence: BillingCadenceValue;
}) {
  if (!initialized) return;
  posthog.capture("plan_cta_clicked", {
    surface: ctx.surface,
    plan: ctx.plan,
    cadence: ctx.cadence,
    ...getDeviceContext(),
  });
}

/** Track: a subscription checkout actually began for a plan + cadence — fired
 *  immediately before the request that creates the Stripe session (or charges a
 *  saved card). Paired with `plan_cta_clicked`, the gap between the two is
 *  clicks we refused (trial lock, signed-out). `surface` is beyond the ticket's
 *  listed props but costs nothing and keeps the funnel segmentable end to end. */
export function trackCheckoutStarted(ctx: {
  plan: string;
  cadence: BillingCadenceValue;
  surface?: PricingSurface | null;
}) {
  if (!initialized) return;
  posthog.capture("checkout_started", {
    plan: ctx.plan,
    cadence: ctx.cadence,
    surface: ctx.surface ?? null,
    ...getDeviceContext(),
    ...getUtmProperties(),
  });
}

/** Track: a paid plan card was chosen (intent to upgrade/checkout). */
export function trackPlanSelected(ctx: PricingCtaContext) {
  if (!initialized) return;
  posthog.capture("plan_selected", pricingCtaProps(ctx));
}

/** Track: a pricing CTA failed (checkout error, missing handler, etc.). */
export function trackPricingCtaError(
  ctx: PricingCtaContext & { message: string },
) {
  if (!initialized) return;
  posthog.capture("pricing_cta_error", {
    ...pricingCtaProps(ctx),
    message: ctx.message,
  });
}

// ──────────────────────────────────────────────────
// External-site / business-URL → Build/clone funnel
// (PANDA: "Route external-site and business-URL users into Build or clone")
// ──────────────────────────────────────────────────

/** Canonical funnel event for any business/website URL submission. */
export function trackBusinessUrlSubmitted(domain: string, surface: string) {
  if (!initialized) return;
  posthog.capture("business_url_submitted", {
    domain,
    surface,
    ...getDeviceContext(),
  });
}

/**
 * The user asked to edit/clone an external live site (detected in chat or via
 * a home business URL). `surface` = "grow_chat" | "build_chat" | "home".
 */
export function trackExternalSiteRequestDetected(
  surface: string,
  domain?: string,
) {
  if (!initialized) return;
  posthog.capture("external_site_request_detected", {
    surface,
    domain: domain ?? null,
    ...getDeviceContext(),
  });
}

/** The user clicked a "Clone / build a Panda version" CTA. */
export function trackExternalSiteBuildCtaClicked(
  surface: string,
  domain?: string,
) {
  if (!initialized) return;
  posthog.capture("external_site_build_cta_clicked", {
    surface,
    domain: domain ?? null,
    ...getDeviceContext(),
  });
}

/** A clone/build kicked off from a business URL. */
export function trackBusinessUrlBuildStarted(domain: string, surface: string) {
  if (!initialized) return;
  posthog.capture("business_url_build_started", {
    domain,
    surface,
    ...getDeviceContext(),
  });
}

/** Intent (prompt or business URL) stashed at the signup/login gate. */
export function trackPreSignupIntentCaptured(intentKind: string) {
  if (!initialized) return;
  posthog.capture("pre_signup_intent_captured", {
    intent_kind: intentKind,
    ...getDeviceContext(),
  });
}

/** Stashed pre-signup intent recovered after authentication. */
export function trackPreSignupIntentRestored(intentKind: string) {
  if (!initialized) return;
  posthog.capture("pre_signup_intent_restored", {
    intent_kind: intentKind,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// "Start free" top-of-funnel CTAs
// (PANDA: "Make every Start Free button launch the signup flow")
//
// Four events form the funnel, all keyed on the same `source` string so a
// per-CTA conversion + drop-off rate is a single PostHog breakdown:
//   start_free_cta_clicked → signup_modal_opened → user_signed_up
//                                               ↘ signup_modal_dismissed
//                                               ↘ signup_failed(stage, reason)
//                                                 ↳ signup_recovered → user_signed_up
//   user_signed_up → pre_signup_intent_restored → auth_returned_to_project_action
//                  ↘ pre_signup_intent_lost(reason)
// ──────────────────────────────────────────────────

/**
 * A "Start free"-class CTA was clicked on a public surface.
 *
 * `destination` records what the click actually did, so a regression back to
 * the old refocus-the-hero behavior is visible in the data rather than
 * silently eating intent.
 */
export function trackStartFreeCtaClicked(ctx: {
  source: string;
  is_logged_in: boolean;
  destination: "signup" | "signed_in_app";
  has_prompt?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("start_free_cta_clicked", {
    source: ctx.source,
    is_logged_in: ctx.is_logged_in,
    destination: ctx.destination,
    has_prompt: ctx.has_prompt ?? false,
    ...getDeviceContext(),
  });
}

/** The signup modal became visible. */
export function trackSignupModalOpened(source: string) {
  if (!initialized) return;
  posthog.capture("signup_modal_opened", {
    source,
    ...getDeviceContext(),
  });
}

/**
 * The signup modal closed without the user authenticating — the drop-off leg
 * of the funnel. `reached_step` says how far they got before leaving.
 */
export function trackSignupModalDismissed(ctx: {
  source: string;
  reached_step: string;
}) {
  if (!initialized) return;
  posthog.capture("signup_modal_dismissed", {
    source: ctx.source,
    reached_step: ctx.reached_step,
    ...getDeviceContext(),
  });
}

/** Which leg of the signup funnel broke. */
export type SignupFailureStage =
  /** supabase.auth.signUp did not produce an account. */
  | "create_account"
  /** The account exists; establishing the session failed. */
  | "sign_in_after_create"
  /** The magic link could not be sent. */
  | "verification_email"
  /** The stashed pre-signup prompt could not be recovered. */
  | "intent_restore"
  /** The push to the user's project did not happen. */
  | "project_routing";

/** Why it broke. Shares the "offline vs server" vocabulary of classifyBuildError. */
export type SignupFailureReason =
  /** navigator.onLine === false — the device really has no route out. */
  | "offline"
  /** The fetch died while the browser reported it was online → our side. */
  | "server_unreachable"
  /** 429 from gotrue or from our own route. */
  | "rate_limited"
  /** check-email answered checked:true + exists:true after sign-in failed. */
  | "account_created_signin_failed"
  | "already_registered"
  | "wrong_password"
  /** A different email is already signed in on this device. */
  | "other_user_session"
  /** gotrue returned a deterministic 4xx (weak password, invalid email…). */
  | "rejected"
  /** signUp resolved with no error and no user. */
  | "no_user"
  | "unknown";

/**
 * Track: a signup attempt failed in a way the user actually saw.
 *
 * Fires ONCE per user-visible outcome — never per retry; the retry count rides
 * on `attempts`. This is the event whose absence made the original report
 * unanswerable: a false "we couldn't reach our servers" left no trace at all,
 * so a genuine network failure and an accidentally-correct one looked
 * identical in the data.
 *
 * `account_exists` is the server-authoritative answer from /api/auth/check-email
 * — null whenever that probe was not run or was not authoritative (checked:false,
 * 429, timeout), so a rate-limited probe is never recorded as "no account".
 * Carries no password and no raw gotrue message.
 */
export function trackSignupFailed(props: {
  /** The CTA/surface that opened the modal. */
  source: string;
  stage: SignupFailureStage;
  reason: SignupFailureReason;
  /** HTTP status when the error carried one (429, 422, 500…), else null. */
  status?: number | null;
  /** Network attempts made before giving up. 1 = no retry ran. */
  attempts: number;
  /** True when a retry produced a live session before this event. */
  recovered: boolean;
  /** Server-authoritative account existence, or null when not established. */
  accountExists?: boolean | null;
}) {
  if (!initialized) return;
  posthog.capture("signup_failed", {
    source: props.source,
    stage: props.stage,
    reason: props.reason,
    status: props.status ?? null,
    attempts: props.attempts,
    recovered: props.recovered,
    account_exists: props.accountExists ?? null,
    environment: clientEnvironment(),
    ...getDeviceContext(),
  });
}

/**
 * Track: a signup that hit a client-side network failure was pulled back into a
 * live session by the retry loop.
 *
 * Pairs with user_signed_up — when `provenance` is "created" both fire; when it
 * is "unknown" only this one does, because we could not prove the account was
 * new and must not inflate the signup funnel.
 */
export function trackSignupRecovered(props: {
  source: string;
  /** Sign-in attempts it took. */
  attempts: number;
  /**
   * "created" = the email-step pre-flight authoritatively said no account
   * existed for this address, so this signup made it. "unknown" = the
   * pre-flight was not authoritative, so the session may belong to an account
   * that already existed.
   */
  provenance: "created" | "unknown";
}) {
  if (!initialized) return;
  posthog.capture("signup_recovered", {
    source: props.source,
    attempts: props.attempts,
    provenance: props.provenance,
    environment: clientEnvironment(),
    ...getDeviceContext(),
  });
}

/**
 * Track: the user authenticated but their stashed pre-signup intent never made
 * it to a project.
 *
 * The mirror of pre_signup_intent_restored — without it a dropped prompt is
 * indistinguishable from a user who never had one, which is exactly the gap
 * that made "did intent restoration or project routing fail?" unanswerable.
 */
export function trackPreSignupIntentLost(props: {
  reason:
    /** ?prompt= present but nothing in storage (private mode / TTL expiry). */
    | "no_stash"
    /** SIGNED_IN landed somewhere other than the homepage. */
    | "wrong_path"
    /** login/signup/next params still on the URL — the modal owns navigation. */
    | "modal_owns_navigation"
    | "already_resumed";
  stage: "restore" | "routing";
}) {
  if (!initialized) return;
  posthog.capture("pre_signup_intent_lost", {
    reason: props.reason,
    stage: props.stage,
    intent_kind: "prompt",
    ...getDeviceContext(),
  });
}

/** User was returned to their intended project/action after auth. */
export function trackAuthReturnedToProjectAction(action: string) {
  if (!initialized) return;
  posthog.capture("auth_returned_to_project_action", {
    action,
    ...getDeviceContext(),
  });
}

/**
 * Best-effort deploy environment for a CLIENT-side event. `VERCEL_ENV` is
 * server-only (never inlined into the browser bundle), so we prefer an explicit
 * `NEXT_PUBLIC_VERCEL_ENV` if configured and fall back to `NODE_ENV` (which Next
 * DOES inline for the client — "production" for any prod build).
 */
function clientEnvironment(): string {
  return (
    process.env.NEXT_PUBLIC_VERCEL_ENV ??
    process.env.NODE_ENV ??
    "development"
  );
}

/** Why a redirect to /login can be classified. Mirrors the reasons the ticket
 *  asked us to make visible (no active session / expired / refresh failed / a
 *  transient blip that should NOT have ejected / manual / unknown). */
export type AuthRedirectReason =
  | "no_session"
  | "session_expired"
  | "refresh_failed"
  | "transient_error"
  | "manual"
  | "unknown";

/**
 * Track: a protected route sent the user to /login. This is the event the
 * "user redirected to /login with no reason in the logs" bug asked for — every
 * redirect now carries WHY (reason), from WHERE (from_route + source), for WHOM
 * (user_id, nullable when there's no session), plus the session diagnostics
 * (has_session, token_expiry, error_code) needed to tell a real logout apart
 * from a transient token blip. `source` is which layer decided to redirect:
 * client_guard | api_401 | manual | auth_callback.
 */
export function trackAuthRedirectToLogin(props: {
  reason: AuthRedirectReason;
  fromRoute: string;
  source: "client_guard" | "api_401" | "manual" | "auth_callback";
  projectId?: string | null;
  userId?: string | null;
  hasSession?: boolean;
  tokenExpiry?: number | null;
  errorCode?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("auth_redirect_to_login", {
    reason: props.reason,
    from_route: props.fromRoute,
    source: props.source,
    project_id: props.projectId ?? null,
    user_id: props.userId ?? null,
    has_session: props.hasSession ?? false,
    token_expiry: props.tokenExpiry ?? null,
    error_code: props.errorCode ?? null,
    environment: clientEnvironment(),
    ...getDeviceContext(),
  });
}

/** Track: the password reset / update-password flow was entered (a valid
 *  recovery session was established and the "set a new password" form shown). */
export function trackPasswordResetFlowStarted() {
  if (!initialized) return;
  posthog.capture("password_reset_flow_started", {
    environment: clientEnvironment(),
    ...getDeviceContext(),
  });
}

/** Track: the user successfully set a new password (updateUser succeeded). */
export function trackPasswordResetFlowCompleted() {
  if (!initialized) return;
  posthog.capture("password_reset_flow_completed", {
    environment: clientEnvironment(),
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Projects-page onboarding funnel
// (parallel to the homepage hero — the /projects page has the same
//  idea/existing-business toggle + prompt/URL input)
// ──────────────────────────────────────────────────

/**
 * Track: User selected an onboarding path on the /projects page. The toggle's
 * "business" value maps to the spec's `path: "existing_business"`.
 */
export function trackProjectsPathSelected(path: "idea" | "existing_business") {
  if (!initialized) return;
  posthog.capture("projects_path_selected", {
    path,
    ...getDeviceContext(),
  });
}

/**
 * Track: User submitted an AI-builder prompt from the /projects page
 * ("idea" path).
 */
export function trackProjectsPromptSubmitted(promptLength: number) {
  if (!initialized) return;
  posthog.capture("projects_prompt_submitted", {
    path: "idea",
    prompt_length: promptLength,
    source: "projects",
  });
}

/**
 * Track: User submitted a business URL from the /projects page
 * ("existing_business" / growth path).
 */
export function trackProjectsBusinessUrlSubmitted(domain: string) {
  if (!initialized) return;
  posthog.capture("projects_business_url_submitted", {
    path: "existing_business",
    has_url: true,
    domain,
    source: "projects",
  });
}

// ──────────────────────────────────────────────────
// Ownership questions funnel (PANDA-1050)
//
// A lightweight, skippable 2-3 question step shown before the FIRST build so
// the user shapes their project. These events measure the step funnel:
//   shown -> answered (per question) -> completed | skipped -> build_started.
// `inferred_intent_confidence` is the 0..1 share of questions the prompt parser
// could answer on its own; `project_category` is the chosen/inferred category.
// ──────────────────────────────────────────────────

interface OwnershipEventBase {
  project_id: string;
  project_category?: string | null;
  prompt_length: number;
  inferred_intent_confidence: number;
  source?: string | null;
}

/** Track: the ownership step was shown to the user. PANDA-1200 — also records
 *  the basic app type detected from the prompt and whether the shown set came
 *  from the AI or the prompt-aware fallback, so we can confirm the questions
 *  match the prompt's app type. */
export function trackOwnershipQuestionShown(
  props: OwnershipEventBase & {
    detected_category?: string | null;
    questions_source?: "ai" | "fallback" | null;
    question_count?: number;
  },
) {
  if (!initialized) return;
  posthog.capture("ownership_question_shown", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user answered a single ownership question. */
export function trackOwnershipQuestionAnswered(
  props: OwnershipEventBase & { question_id: string; answer: string },
) {
  if (!initialized) return;
  posthog.capture("ownership_question_answered", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user skipped the ownership step (proceed with inferred defaults). */
export function trackOwnershipQuestionSkipped(props: OwnershipEventBase) {
  if (!initialized) return;
  posthog.capture("ownership_question_skipped", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user finished the ownership step (answered at least one). */
export function trackOwnershipStepCompleted(props: OwnershipEventBase) {
  if (!initialized) return;
  posthog.capture("ownership_step_completed", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the first build kicked off right after the ownership step. */
export function trackBuildStartedAfterOwnershipStep(
  props: OwnershipEventBase & { skipped: boolean },
) {
  if (!initialized) return;
  posthog.capture("build_started_after_ownership_step", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Paid pre-build option gate (PANDA-1695 follow-up)
//
// A pre-build question option can deliver a capability the viewer's plan does
// not include ("Sign up" → customer accounts). The option carries the paid
// feature id, server-annotated per plan, so these events only ever fire for a
// user who is NOT entitled — a paid or trial user sees no badge and no gate.
//
// The funnel: option_selected -> gate_shown -> (upgrade | free | back).
// `free_option_selected` is the free-path resolution and carries HOW it was
// resolved — accepting the demo build, or swapping to a non-paid option — so
// the two are one funnel step rather than two events that have to be unioned.
// ──────────────────────────────────────────────────

interface PaidPrebuildEventBase {
  /** Paid feature id from lib/features/paid-features.ts, e.g. customer-accounts. */
  feature: string;
  question_id: string;
  option_value: string;
}

/** Track: the user selected a pre-build option that needs a paid plan. Fires on
 *  every selection, including ones already acknowledged (which show no gate),
 *  so intent is measurable separately from how often the gate interrupts. */
export function trackPaidPrebuildOptionSelected(props: PaidPrebuildEventBase) {
  if (!initialized) return;
  posthog.capture("paid_prebuild_option_selected", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the gate was shown. `trigger` separates the immediate on-selection
 *  gate from the submit-time backstop — a rising share of "submit" means
 *  selections are reaching the review unacknowledged, which is a bug signal. */
export function trackPaidPrebuildGateShown(
  props: PaidPrebuildEventBase & { trigger: "selection" | "submit" },
) {
  if (!initialized) return;
  posthog.capture("paid_prebuild_gate_shown", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user chose to upgrade from the gate. Intent, not conversion —
 *  the paywall that opens next owns the purchase events. */
export function trackPaidPrebuildGateUpgradeClicked(
  props: PaidPrebuildEventBase,
) {
  if (!initialized) return;
  posthog.capture("paid_prebuild_gate_upgrade_clicked", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user backed out of the gate to change their answer. The option is
 *  deselected, so this is a real abandonment of the paid choice. */
export function trackPaidPrebuildGateBackClicked(props: PaidPrebuildEventBase) {
  if (!initialized) return;
  posthog.capture("paid_prebuild_gate_back_clicked", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user resolved the gate by taking the free path.
 *  `resolution: "demo"`   — accepted the demo build of the paid capability.
 *  `resolution: "swapped"` — went back and picked a non-paid option instead. */
export function trackPaidPrebuildFreeOptionSelected(
  props: PaidPrebuildEventBase & { resolution: "demo" | "swapped" },
) {
  if (!initialized) return;
  posthog.capture("paid_prebuild_free_option_selected", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Build-intent gate (PANDA — "gather build intent before setup")
//
// Before the setup questions / build start, Panda checks whether the first
// message carries enough core build intent. These events make that decision
// observable: when we hold the build to ask "what would you like to build?"
// and when a (possibly clarified) first message proceeds into the build.
//   • `entry` — where the first message arrived: "url" (home → ?prompt) or
//     "chat" (typed into the composer on an empty project).
//   • `confidence` / `reason` — the classifier's 0..1 score and reason code.
//   • `clarification_round` — how many times we have asked on this project so
//     far (1 = first ask).
// ──────────────────────────────────────────────────

interface BuildIntentEventBase {
  project_id: string;
  /** Truncated first message — the input the decision was made on. */
  message: string;
  message_length: number;
  /** 0..1 classifier confidence that intent is sufficient. */
  confidence: number;
  /** Reason code (e.g. ask:greeting_or_filler, ready:concrete_subject). */
  reason: string;
  /** How many readable (vision-supported) images the decision saw. A
   *  clarification asked while this is > 0 is the "asked over the attached
   *  design" regression signature; without it, that failure is
   *  indistinguishable from a genuinely vague text-only first message. */
  vision_attachment_count?: number;
  /** Where the attachment census found its answer: memory | idb | none —
   *  plus "timeout" when the census could not settle and the decision was
   *  made attachment-blind (the degraded path, worth counting on its own). */
  attachment_census_source?: string;
  entry: "url" | "chat";
  source?: string | null;
}

/** Track: a vague first message was held and Panda asked what to build. */
export function trackBuildIntentClarificationAsked(
  props: BuildIntentEventBase & { clarification_round: number },
) {
  if (!initialized) return;
  posthog.capture("build_intent_clarification_asked", {
    ...props,
    setup_started: false,
    ...getDeviceContext(),
  });
}

/** Track: a first message carried enough intent and proceeded into the build. */
export function trackBuildIntentReady(
  props: BuildIntentEventBase & { clarification_round: number },
) {
  if (!initialized) return;
  posthog.capture("build_intent_ready", {
    ...props,
    setup_started: true,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Follow-up edit-intent gate (PANDA-1336)
//
// The existing-project counterpart of the build-intent gate above: before a
// typed follow-up starts an edit (credits, working state, task), Panda checks
// whether it actually states an action. These events make that observable —
// when an ambiguous message was held for clarification, and when a (possibly
// clarified) message proceeded into the edit.
// ──────────────────────────────────────────────────

interface EditIntentEventBase {
  project_id: string;
  /** Truncated message — the input the decision was made on. */
  message: string;
  message_length: number;
  /** 0..1 gate confidence that intent is sufficient. */
  confidence: number;
  /** Reason code (e.g. subject_without_action, action_stated). */
  reason: string;
}

/** Track: an ambiguous follow-up was held and Panda asked what to do.
 *
 *  `has_design_intent` / `is_correction` are carried because the reason code
 *  alone cannot tell an acceptable hold from the reported bug. Every unnecessary
 *  clarification in this ticket logged the SAME reason — subject_without_action —
 *  while being a correction about the app's colours, so the failure was
 *  invisible in PostHog and only ever surfaced as a user's screenshot. A hold on
 *  a message that carries either flag is a bug by definition; these two columns
 *  make that query one filter instead of a manual read of every held message. */
export function trackEditIntentClarificationAsked(
  props: EditIntentEventBase & {
    clarification_round: number;
    has_design_intent?: boolean;
    is_correction?: boolean;
  },
) {
  if (!initialized) return;
  posthog.capture("edit_intent_clarification_asked", {
    ...props,
    edit_started: false,
    ...getDeviceContext(),
  });
}

/**
 * Track: a VAGUE-SCOPE follow-up ("add more stuff", "make it better") was held
 * and Panda proposed concrete next steps instead of quoting the phrase back.
 *
 * Separate from edit_intent_clarification_asked (which still fires alongside it)
 * because this is a distinct funnel: shown → option tapped → build started. The
 * original wording is carried so the detector's coverage can be checked against
 * what users actually type, and the offered options so a low tap-through rate
 * can be traced to bad suggestions rather than the gate itself.
 */
export function trackVaguePromptClarificationShown(props: {
  project_id: string;
  /** The user's exact vague message (truncated). */
  original_prompt: string;
  /** The concrete options offered as chips. */
  suggested_options: string[];
  option_count: number;
  /** "model" | "deterministic" — which generator produced the options. */
  option_source: string;
  reason: string;
  clarification_round: number;
}) {
  if (!initialized) return;
  posthog.capture("vague_prompt_clarification_shown", {
    ...props,
    surface: "project_chat",
    edit_started: false,
    ...getDeviceContext(),
  });
}

/** Track: a clarified follow-up (round > 0) proceeded into the edit. */
export function trackEditIntentReady(
  props: EditIntentEventBase & { clarification_round: number },
) {
  if (!initialized) return;
  posthog.capture("edit_intent_ready", {
    ...props,
    edit_started: true,
    ...getDeviceContext(),
  });
}

/**
 * The enforcement backstop fired: every layer resolved this turn as a clear
 * edit, and the model still replied with a question and no file changes, so the
 * question was suppressed and the turn re-run with the decide-and-act directive
 * (lib/ai/decide-and-act.ts).
 *
 * This is the standing regression detector for "Panda asks unnecessary
 * clarification questions". The bug arrives as NEW PHRASINGS, which is why five
 * rounds of unit tests keyed to known phrases never caught the sixth — but every
 * instance, whatever its wording, passes through here. A rise in this rate is
 * the sixth report arriving before a user files it.
 */
export function trackEditIntentEnforced(props: {
  project_id: string;
  /** The user's message that was already resolved as a clear edit. */
  message: string;
  /** The question the model asked instead of acting — the phrasing to inspect. */
  question: string;
}) {
  if (!initialized) return;
  posthog.capture("edit_intent_enforced_decide_and_act", {
    ...props,
    ...getDeviceContext(),
  });
}

/**
 * The model asked a clarifying question and it was allowed through — the
 * complement of edit_intent_enforced_decide_and_act, which counts only the
 * questions that were suppressed.
 *
 * On its own the enforcement event cannot say whether the gate is working:
 * suppressions can hold perfectly steady while the model learns new phrasings
 * that walk straight past it, and the rate would never move.
 * `resolved_as_clear_edit` is the column that makes that miss countable — a
 * question accepted on a message every layer had ALREADY resolved as a clear
 * edit is the reported bug getting through, not a legitimate ask, and those are
 * the rows the weekly review needs. `question` carries the phrasing because the
 * bug arrives as new wording every time, which is why five rounds of unit tests
 * keyed to known phrases never caught the sixth.
 */
export function trackModelClarificationAccepted(props: {
  project_id: string;
  /** The user's message the question was asked about. */
  message: string;
  /** The question the model asked instead of acting. */
  question: string;
  /** How many clarification rounds this turn has already been through. */
  clarification_round: number;
  /** True when the intent layers had already called this a clear edit. */
  resolved_as_clear_edit: boolean;
}) {
  if (!initialized) return;
  posthog.capture("model_clarification_accepted", {
    project_id: props.project_id,
    message: props.message.slice(0, 200),
    question: props.question.slice(0, 200),
    clarification_round: props.clarification_round,
    resolved_as_clear_edit: props.resolved_as_clear_edit,
    ...getDeviceContext(),
  });
}

/**
 * A follow-up the gate had held was released on the client because it carries
 * design intent — the override that stops "make the buttons rounder" being
 * answered with "what would you like me to change?".
 *
 * `held_reason` is the whole point of the event: it records what the gate WOULD
 * have blocked on, so the override's caseload reads as a queue of gate bugs
 * rather than as a permanent workaround nobody revisits. Every unnecessary
 * clarification in this ticket logged the SAME reason code, which is why the
 * reason alone can never separate a good hold from the reported failure —
 * counting the overrides BY reason is how the gate itself gets fixed and the
 * override eventually stops being needed.
 *
 * Named `_client` because the same decision is reachable on the server; keeping
 * the two events distinct stops one surface's regression from being buried under
 * the other's volume.
 */
export function trackEditIntentDesignOverrideClient(props: {
  project_id: string;
  /** The held message that was released. */
  message: string;
  /** The reason code the gate had held it on. */
  held_reason: string;
}) {
  if (!initialized) return;
  posthog.capture("edit_intent_design_override_client", {
    project_id: props.project_id,
    message: props.message.slice(0, 200),
    held_reason: props.held_reason,
    ...getDeviceContext(),
  });
}

/**
 * A bare "yes" / "sure" was bound to the offer in the turn immediately before it
 * (lib/ai/affirmation.ts) rather than to an older one still sitting further up
 * the conversation.
 *
 * The failure this prevents is Panda acting on something the user agreed to
 * twenty minutes and six turns ago: an affirmation carries no object of its own,
 * so it attaches to whatever offer can be found, and the oldest match is usually
 * the wrong one — which the user experiences as Panda doing things nobody just
 * asked for. `had_older_offer` is what makes the barrier's value measurable: the
 * events where an older offer existed are the only ones where binding to the
 * last turn actually changed the outcome, and if that subset ever falls to zero
 * the barrier has gone inert because something upstream moved, not because the
 * problem was solved.
 */
export function trackAffirmationBoundToLastTurn(props: {
  /** Null on surfaces where no project is in context. */
  project_id?: string | null;
  /** Which barrier bound it. "awaits_reply" today. */
  barrier: "awaits_reply";
  /** True when an older, still-open offer existed and was NOT chosen. */
  had_older_offer: boolean;
}) {
  if (!initialized) return;
  posthog.capture("affirmation_bound_to_last_turn", {
    project_id: props.project_id ?? null,
    barrier: props.barrier,
    had_older_offer: props.had_older_offer,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Build-readiness gate ("skip clarifying questions for build-ready prompts")
//
// After the build-intent gate passes, and before the setup questions, Panda
// classifies whether the prompt already carries enough to build well. A
// READY_TO_BUILD verdict skips the setup questions entirely and goes straight to
// the build; NEEDS_CLARIFICATION falls through to the questions as before. This
// event makes that decision observable next to the ownership funnel.
//   • `decision` — "ready" (skipped questions) or "clarify" (asked questions).
//   • `reason` / `readiness_source` — the classifier's short reason code and
//     where it came from (model | deterministic | fallback).
// ──────────────────────────────────────────────────
export function trackBuildReadinessDecision(props: {
  project_id: string;
  decision: "ready" | "clarify";
  reason: string;
  readiness_source: string;
  prompt_length: number;
  detected_category?: string | null;
  source?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("build_readiness_decision", {
    ...props,
    ...getDeviceContext(),
  });
}

/**
 * Track: the scope gate declined an out-of-scope build request (native desktop /
 * mobile app, browser extension, CLI tool, etc.) BEFORE any credit was spent —
 * showing the user a friendly explanation + web-based alternative instead of
 * starting a build that could never satisfy the request. `entry` is the build
 * funnel the decline interrupted; `scope_category` is the out-of-scope bucket.
 */
export function trackBuildScopeDeclined(props: {
  project_id: string;
  scope_category: string | null;
  reason: string;
  prompt_length: number;
  entry: "url" | "chat";
  source?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("build_scope_declined", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Workspace / activation funnel (Phase 2)
// ──────────────────────────────────────────────────

/**
 * Track: Preview became viewable for the first time this project load.
 * Fires for BOTH desktop (in-browser WebContainer) and mobile/unsupported
 * browsers (the auto-deployed Vercel iframe) — they converge on the same
 * `previewReady` signal. `is_remote_preview` = true means the deployed-iframe
 * path (mobile / WebContainer-unsupported).
 */
export function trackPreviewReady(
  projectId: string,
  isRemotePreview: boolean,
  meta?: {
    /** "direct_load" | "webcontainer_unsupported" | "sandbox" — which preview
     *  path became viewable, so a deployed-preview "ready" (where replay can't
     *  capture the pixels) is distinguishable from a sandbox one. */
    reason?: string;
    deployedUrl?: string | null;
  },
) {
  if (!initialized) return;
  posthog.capture("preview_ready", {
    project_id: projectId,
    is_remote_preview: isRemotePreview,
    preview_kind: meta?.reason ?? (isRemotePreview ? "remote" : "sandbox"),
    ...(meta?.deployedUrl ? { deployed_host: safeHost(meta.deployedUrl) } : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: a returning user reopened an existing project and the restore began
 * (GT-08). Scoped to genuine return visits by `isSessionRestore` — a first
 * build, a template/clone first-open and a Stripe bounce-back are all excluded
 * upstream, because folding a minutes-long first build into this percentile
 * would hide exactly the returning-user latency the ticket is about.
 *
 * The opening half of the funnel: a restore the user abandons mid-wait shows
 * up as a `started` with no `completed`, which is the churn signal itself.
 * Joins to `preview_load_*` on project_id — that measures the preview booting,
 * this measures the SHELL becoming usable (top bar honest, chat typeable).
 */
export function trackSessionRestoreStarted(props: {
  projectId: string;
  /** How the project was entered — always "restore" today, carried so the
   *  event stays readable if eligibility ever widens. */
  origin: string;
  /** Sandbox backend resolved for this load ("e2b" | "webcontainer"). */
  backend?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("session_restore_started", {
    project_id: props.projectId,
    origin: props.origin,
    ...(props.backend ? { backend: props.backend } : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: the restore finished and the builder shell became usable.
 *
 * `duration_ms` is the ticket's KPI number — effect start to `isLoading` false
 * — and `over_target` stamps it against SESSION_RESTORE_TARGET_MS so "p50/p95
 * trending down" is one property rather than a threshold re-typed into every
 * chart. `fetch_ms` / `hydrate_ms` split it into the network+server share and
 * the client share, which is what says whether a regression is the API or the
 * page.
 *
 * `publish_label` is the second half of the ticket: it records the label the
 * top bar actually settled on, so "0% mislabeled buttons" is measurable rather
 * than asserted. A `publish_label` of "Publish" on a row where `published` is
 * true is precisely the defect — it should now be unreachable, and this is how
 * we would know if it came back.
 */
export function trackSessionRestoreCompleted(props: {
  projectId: string;
  durationMs: number;
  outcome: "restored" | "empty" | "failed";
  overTarget: boolean;
  /** Whether a live deploy exists — the returning-user segment that the
   *  "Republish" label depends on. */
  published: boolean;
  /** The label the publish button resolved to once state was known. */
  publishLabel?: string | null;
  fetchMs?: number | null;
  hydrateMs?: number | null;
  fileCount?: number | null;
  messageCount?: number | null;
  /** Served straight from the deployed build, no VM boot — the fast path. */
  directLoad?: boolean | null;
  backend?: string | null;
  origin?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("session_restore_completed", {
    project_id: props.projectId,
    duration_ms: Math.round(props.durationMs),
    outcome: props.outcome,
    over_target: props.overTarget,
    published: props.published,
    // Additive fields are omitted (not nulled) when unknown so percentiles
    // aren't skewed by zeros and existing dashboards keep working.
    ...(props.publishLabel ? { publish_label: props.publishLabel } : {}),
    ...(props.fetchMs != null ? { fetch_ms: Math.round(props.fetchMs) } : {}),
    ...(props.hydrateMs != null
      ? { hydrate_ms: Math.round(props.hydrateMs) }
      : {}),
    ...(props.fileCount != null ? { file_count: props.fileCount } : {}),
    ...(props.messageCount != null
      ? { message_count: props.messageCount }
      : {}),
    ...(props.directLoad != null ? { direct_load: props.directLoad } : {}),
    ...(props.backend ? { backend: props.backend } : {}),
    ...(props.origin ? { origin: props.origin } : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: a preview load began — the boot/loading overlay first appeared for this
 * load. The opening half of the load funnel, so `preview_load_completed` has a
 * denominator and an abandoned load (the user leaves before it ever paints) is
 * visible as a started-with-no-terminal-event rather than silence.
 */
export function trackPreviewLoadStarted(props: {
  projectId: string;
  isRemotePreview: boolean;
  /** How the VM was acquired, when already known at overlay time. */
  bootType?: "cold" | "resume" | "warm" | null;
}) {
  if (!initialized) return;
  posthog.capture("preview_load_started", {
    project_id: props.projectId,
    is_remote_preview: props.isRemotePreview,
    ...(props.bootType ? { boot_type: props.bootType } : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: the build finished but the preview still hasn't painted after the
 * handoff timeout. Distinct from `preview_load_stalled`, which can only fire
 * AFTER a sandbox url is revealed: this one also covers the pre-reveal window
 * (VM still booting / generated files still being applied), the ~5-minute
 * "Almost ready to show you" hang on mobile. `has_preview_url` splits the two
 * cases, and `is_booting` / `is_applying` say which backend phase was in flight.
 * `elapsed_ms` counts VISIBLE time only — a backgrounded phone isn't a timeout.
 */
export function trackPreviewLoadTimeout(props: {
  projectId: string;
  elapsedMs: number;
  hasPreviewUrl: boolean;
  isBooting: boolean;
  isApplying: boolean;
  isRemotePreview: boolean;
  fileCount?: number | null;
  payloadBytes?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("preview_load_timeout", {
    project_id: props.projectId,
    elapsed_ms: Math.round(props.elapsedMs),
    has_preview_url: props.hasPreviewUrl,
    is_booting: props.isBooting,
    is_applying: props.isApplying,
    is_remote_preview: props.isRemotePreview,
    ...(props.fileCount != null ? { file_count: props.fileCount } : {}),
    ...(props.payloadBytes != null ? { payload_bytes: props.payloadBytes } : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: the automatic preview-load retry lifecycle. `phase` selects the event
 * name — `started` (a retry was fired), `succeeded` (the preview painted after
 * it), `failed` (it never painted and the user was handed the recoverable retry
 * card). `trigger` keeps the two independent retry paths from double-counting:
 * `preview_load_timeout` is the in-place iframe reload fired by the preview's
 * handoff watchdog, `boot_timeout` is the sandbox hook's own create-timeout
 * retry chain (which owns recovery before a url is ever revealed).
 */
export function trackPreviewAutoRetry(
  phase: "started" | "succeeded" | "failed",
  props: {
    projectId: string | null;
    trigger: "preview_load_timeout" | "boot_timeout";
    /** 1-based retry number within the current chain. */
    attempt?: number | null;
    elapsedMs?: number | null;
  },
) {
  if (!initialized) return;
  posthog.capture(`preview_auto_retry_${phase}`, {
    project_id: props.projectId,
    trigger: props.trigger,
    ...(props.attempt != null ? { attempt: props.attempt } : {}),
    ...(props.elapsedMs != null
      ? { elapsed_ms: Math.round(props.elapsedMs) }
      : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: the recoverable "preview stalled" retry card. `shown` fires once per
 * appearance of the RETRY CTA (not of the card — the same card shows a waiting
 * state while a build/apply/fix is still running, and that is deliberately not
 * an offer), `clicked` when the user takes it. The in-flight flags are recorded
 * at the moment of the offer so a CTA raised over live work — the failure this
 * pairing exists to catch (PANDA-1912) — shows up as a property rather than
 * only in a bug report. `preview_retry_card_shown` vs `_clicked` is also the
 * take-rate for the card.
 */
export function trackPreviewRetryCard(
  phase: "shown" | "clicked",
  props: {
    projectId: string;
    isGenerating: boolean;
    isApplying: boolean;
    isFixing: boolean;
    isBooting: boolean;
    hasPreviewUrl: boolean;
  },
) {
  if (!initialized) return;
  posthog.capture(`preview_retry_card_${phase}`, {
    project_id: props.projectId,
    is_generating: props.isGenerating,
    is_applying: props.isApplying,
    is_fixing: props.isFixing,
    is_booting: props.isBooting,
    // The one derived field, because it is the acceptance criterion: the CTA
    // must never be offered while any of the above is true.
    is_active_build: props.isGenerating || props.isApplying || props.isFixing,
    has_preview_url: props.hasPreviewUrl,
    ...getDeviceContext(),
  });
}

/**
 * Track: a fresh preview finished loading and became viewable. `duration_ms` is
 * wall-clock from the boot/loading overlay first appearing to the preview being
 * shown — the end-to-end "how long did the user wait" number that the preview
 * watchdog work is measured against. `recovered` is true when the load only
 * completed after the watchdog had to step in (auto-reload or a retry card), so
 * clean loads and rescued ones can be separated in analysis.
 */
export function trackPreviewLoadCompleted(props: {
  projectId: string;
  durationMs: number;
  isRemotePreview: boolean;
  recovered: boolean;
  /** How the VM was acquired (server bootType, `resume` folding reused+resumed).
   *  Lets the end-to-end load funnel be split cold vs warm vs resumed. */
  bootType?: "cold" | "resume" | "warm" | null;
  /** Client-side ms from boot start to the first post-boot iframe load. */
  iframeReadyMs?: number | null;
  /** Server-reported per-stage timings, joined here for one full-funnel event. */
  sandboxCreateMs?: number | null;
  serverReadyMs?: number | null;
  publicUrlMs?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("preview_load_completed", {
    project_id: props.projectId,
    duration_ms: Math.round(props.durationMs),
    is_remote_preview: props.isRemotePreview,
    recovered: props.recovered,
    // Additive stage/type fields — omitted (not null) when absent so percentiles
    // aren't skewed and old dashboards keep working.
    ...(props.bootType ? { boot_type: props.bootType } : {}),
    ...(props.iframeReadyMs != null
      ? { iframe_ready_ms: Math.round(props.iframeReadyMs) }
      : {}),
    ...(props.sandboxCreateMs != null
      ? { sandbox_create_ms: Math.round(props.sandboxCreateMs) }
      : {}),
    ...(props.serverReadyMs != null
      ? { server_ready_ms: Math.round(props.serverReadyMs) }
      : {}),
    ...(props.publicUrlMs != null
      ? { public_url_ms: Math.round(props.publicUrlMs) }
      : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: the preview stalled while loading — a sandbox URL was revealed but the
 * iframe never finished loading (the dev server / E2B edge hung mid-response),
 * the failure mode behind the 10+ minute "stuck on the loading screen" hang.
 * Fired by the preview watchdog at each escalation step so we can measure how
 * often previews stall and how long users wait before recovery. `action` is what
 * the watchdog did: a silent in-place reload first, then a user-facing retry card
 * if it's still stuck. `elapsed_ms` is the time the load had been pending when
 * the step fired (the timeout/failure-reason signal).
 */
export function trackPreviewLoadStalled(props: {
  projectId: string;
  /** "iframe_load" | "render_health" | "deployed_blank" | "deployed_load" — the
   *  detection surface, so a deployed-preview stall is separable from a sandbox
   *  one. */
  stage: string;
  elapsedMs: number;
  /** "auto_reload" | "show_retry" | "fallback_to_vm" — what recovery step ran. */
  action: "auto_reload" | "show_retry" | "fallback_to_vm";
  isRemotePreview: boolean;
  /** Optional classifier/reason detail (e.g. the deployed-health verdict). */
  reason?: string | null;
  /** Generated-app size for the loaded preview — lets a stall be correlated with
   *  project complexity, the same signal preview_boot_timeout already carries.
   *  Omitted when the parent hasn't plumbed the file set (e.g. deployed preview). */
  fileCount?: number | null;
  payloadBytes?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("preview_load_stalled", {
    project_id: props.projectId,
    stage: props.stage,
    elapsed_ms: Math.round(props.elapsedMs),
    action: props.action,
    is_remote_preview: props.isRemotePreview,
    reason: props.reason ?? null,
    ...(props.fileCount != null ? { file_count: props.fileCount } : {}),
    ...(props.payloadBytes != null ? { payload_bytes: props.payloadBytes } : {}),
    ...getDeviceContext(),
    ...failureContext(),
  });
}

/**
 * Track: the preview pane is covering itself with the reload-gap loader while
 * there is NO last-known-good preview behind it — the state a user reads as "my
 * preview went black" (PANDA-2041).
 *
 * The gap loader normally sits over a stable preview during a rebuild, so it is
 * a quiet spinner by design. When the stable URL has been dropped (a blank
 * render verdict unpromoting it, a respawn) that spinner is the ONLY thing on
 * screen, over a build the chat has already called finished. Nothing reported
 * that state, so every prior report of it arrived as a screenshot.
 *
 * `phase` selects the event name: `entered` when the pane goes to the loader
 * with nothing behind it, `recovered` when it paints again. An `entered` with no
 * matching `recovered` IS the bug — that gap is the alert to build. `reason`
 * says which path emptied the pane, so a blank-render unpromotion is separable
 * from an in-place reload or a fresh URL that never loaded.
 */
export function trackPreviewPaneUncovered(
  phase: "entered" | "recovered",
  props: {
    projectId: string;
    /** "blank_render" | "url_change" | "in_place_reload" | "unknown" */
    reason: string;
    isRemotePreview: boolean;
    /** A build had already completed for this project — i.e. the chat is very
     *  likely showing a success message over this empty pane. */
    appBuilt: boolean;
    /** How long the pane sat empty. Only on `recovered`. */
    dwellMs?: number | null;
  },
) {
  if (!initialized) return;
  posthog.capture(`preview_pane_uncovered_${phase}`, {
    project_id: props.projectId,
    reason: props.reason,
    is_remote_preview: props.isRemotePreview,
    app_built: props.appBuilt,
    ...(props.dwellMs != null ? { dwell_ms: Math.round(props.dwellMs) } : {}),
    ...getDeviceContext(),
    ...failureContext(),
  });
}

/* ── Preview repair funnel (PANDA-1861) ─────────────────────────────────────
 * Four events, one per step, so the question the bug was filed about — "the
 * preview is visibly broken and the user has no way out" — is measurable rather
 * than anecdotal:
 *
 *   preview_error_overlay_detected → preview_fix_cta_rendered
 *                                  → preview_fix_cta_clicked
 *                                  → preview_repair_started
 *
 * A detected-without-rendered gap is the reported bug reappearing (an error we
 * saw but offered no repair path for). All four carry `kind` (compile / runtime
 * / internal) and getDeviceContext, so the mobile story is separable from
 * desktop — the failure only showed on iOS Safari.
 *
 * Stale-asset failures are deliberately NOT in this funnel: they are preview
 * infrastructure, never routed to "Fix in chat", and have their own
 * preview_chunk_load_failed event above.
 */

/**
 * Track: the preview detected a build or runtime failure and took ownership of
 * the screen. Fired once per incident (upstream state already collapses a
 * repeating error into one incident), NOT once per render. `fullPage` records
 * whether the page behind it was wiped, which selects the CTA surface below.
 */
export function trackPreviewErrorDetected(props: {
  projectId: string | null;
  kind: string;
  fullPage: boolean;
  /** Advisory = a cosmetic React warning; the page still works. */
  advisory: boolean;
  /**
   * WHICH channel found it (PANDA-1947):
   *   `terminal` — the dev-server log stream (the historical path).
   *   `probe`    — the server-side build-state probe read it off the page the
   *                sandbox is actually serving.
   *   `bridge`   — relayed from inside the iframe by the injected bridge.
   *   `write`    — caught at write time (e.g. a merge-conflict marker), before
   *                the compiler saw it at all.
   *
   * This is the dimension that measures the fix. The reported bug was a build
   * error that NO channel saw: the app failed to compile, so the bridge never
   * mounted, and the log line landed inside an edit's suppression window and was
   * dropped. A non-zero `probe` share is the direct evidence that errors which
   * used to reach nobody now reach the repair CTA.
   */
  source?: "terminal" | "probe" | "bridge" | "write";
}) {
  if (!initialized) return;
  posthog.capture("preview_error_overlay_detected", {
    project_id: props.projectId,
    kind: props.kind,
    full_page: props.fullPage,
    advisory: props.advisory,
    source: props.source ?? "terminal",
    ...getDeviceContext(),
  });
}

/**
 * Track: a "Fix in chat" CTA was actually put on screen for that incident, and
 * on which surface (`overlay` = full-cover error card, `pill` = floating bar
 * over a still-usable page). Once per incident per surface. The denominator for
 * the click-through below, and the direct check that every detected error ends
 * up with a repair affordance.
 */
export function trackPreviewFixCtaRendered(props: {
  projectId: string | null;
  kind: string;
  surface: "overlay" | "pill";
}) {
  if (!initialized) return;
  posthog.capture("preview_fix_cta_rendered", {
    project_id: props.projectId,
    kind: props.kind,
    surface: props.surface,
    ...getDeviceContext(),
  });
}

/** Track: the user tapped "Fix in chat". */
export function trackPreviewFixCtaClicked(props: {
  projectId: string | null;
  kind: string;
  surface: "overlay" | "pill";
}) {
  if (!initialized) return;
  posthog.capture("preview_fix_cta_clicked", {
    project_id: props.projectId,
    kind: props.kind,
    surface: props.surface,
    ...getDeviceContext(),
  });
}

/**
 * Track: the tap became a real repair turn — chat opened and the error was
 * submitted. Split from the click because on mobile the two are different
 * things: the click happens on the preview pane and the send happens after the
 * chat sheet is raised, and a gap between them is a broken hand-off.
 */
export function trackPreviewRepairStarted(props: {
  projectId: string | null;
  kind: string;
  /** True when the mobile chat sheet had to be opened to show the turn. */
  openedChat: boolean;
}) {
  if (!initialized) return;
  posthog.capture("preview_repair_started", {
    project_id: props.projectId,
    kind: props.kind,
    opened_chat: props.openedChat,
    ...getDeviceContext(),
  });
}

/**
 * Track: a chat EDIT reached its terminal confirmation. `outcome` mirrors
 * confirmEditCompiled's verdict — `compiled` (a fresh reveal confirmed the edit
 * repainted), `timeout` (no fresh reveal within the window — the preview may be
 * stale), or `error` (a compile error was live). `previewRefreshed` records
 * whether the preview actually repainted for this edit (the reveal counter
 * advanced), the direct signal for the auto-refresh-after-edit bug. Split by
 * getDeviceContext so desktop-vs-mobile refresh reliability is comparable.
 *
 * `apiVerified` is a SEPARATE axis from `outcome`, and the reason this event
 * needed one: a "Fix in chat" repair of a 500ing Route Handler reports
 * `outcome: "compiled"` whether or not it fixed anything, because a broken
 * endpoint compiles. It records whether the endpoint the repair was sent to fix
 * actually answered afterwards — true/false for a verified api repair, null for
 * every other turn (nothing was claimed, so nothing was checked). A non-zero
 * `false` share is the direct measure of repairs that used to be announced as
 * done while the app stayed broken.
 */
export function trackEditCompleted(props: {
  projectId: string;
  outcome: "compiled" | "timeout" | "error";
  previewRefreshed: boolean;
  apiVerified?: boolean | null;
  /** The turn this verdict belongs to — joins to `edit_confirmed` /
   *  `edit_discarded`, `apply_failed`, and the server's `turn_id` events. */
  turnId?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_completed", {
    project_id: props.projectId,
    turn_id: props.turnId ?? null,
    outcome: props.outcome,
    preview_refreshed: props.previewRefreshed,
    api_verified: props.apiVerified ?? null,
    ...getDeviceContext(),
  });
}

/**
 * THE terminal verdict for one edit turn, in two events — and the reason both
 * exist.
 *
 * `edit_completed` above is not a completion signal and never was: it fires on
 * the error and timeout branches too, carrying the truth in an `outcome`
 * property that no funnel groups by. In the reported incident it fired one
 * second after the preview failed, and every dashboard counted the turn as
 * finished. The server's `pipeline_edit_completed` is no better a measure — it
 * means "the model produced applicable output", which it cannot know the app
 * ever received.
 *
 * So the completion metric moves here. At most one of these fires per edit turn,
 * under the turn's own id: `edit_confirmed` means the files reached the sandbox
 * and the turn's work stands (`preview_refreshed` says whether a fresh repaint
 * was actually observed — a slow build is confirmed-but-unrepainted, not a
 * failure), and `edit_discarded` means the work was thrown away and the user is
 * back where they started.
 *
 * "At most", not "exactly": a repair that landed but did not clear the error it
 * was sent to fix is neither — nothing was discarded and nothing is being
 * claimed — and that class already has its own honest measure in
 * `edit_completed.api_verified`. Inventing a verdict for it would put a wrong
 * answer in the metric rather than leave a gap.
 *
 * Keeping `edit_completed` unchanged alongside them is deliberate — existing
 * dashboards keep working while they migrate, and the two can be reconciled
 * because both now carry `turn_id`.
 */
export function trackEditConfirmed(props: {
  projectId: string;
  turnId: string | null;
  previewRefreshed: boolean;
  apiVerified?: boolean | null;
}) {
  if (!initialized) return;
  posthog.capture("edit_confirmed", {
    project_id: props.projectId,
    turn_id: props.turnId ?? null,
    preview_refreshed: props.previewRefreshed,
    api_verified: props.apiVerified ?? null,
    ...getDeviceContext(),
  });
}

/**
 * The other half: this turn's work was thrown away and the user is back on the
 * version they had.
 *
 * `stage` says where it was lost (an ApplyDiscardStage, or `preview_compile`
 * when the files landed and the build broke). `reason` is the string sent to
 * /api/ai/chat/refund-turn, so a discard can be joined to the refund it asked
 * for — and `refundRequested: false` is the query that finds turns thrown away
 * without anyone asking for the credit back.
 */
export function trackEditDiscarded(props: {
  projectId: string;
  turnId: string | null;
  stage: string;
  reason: string;
  refundRequested: boolean;
  /** True when the user is still looking at a working preview of the previous
   *  version — the difference between a safe failure and a broken app. */
  previousPreviewPreserved: boolean;
}) {
  if (!initialized) return;
  posthog.capture("edit_discarded", {
    project_id: props.projectId,
    turn_id: props.turnId ?? null,
    stage: props.stage,
    reason: props.reason,
    refund_requested: props.refundRequested,
    previous_preview_preserved: props.previousPreviewPreserved,
    ...getDeviceContext(),
  });
}

/**
 * Track: the post-edit preview auto-refresh lifecycle. `phase` selects the
 * event name — `requested` (a reveal reload was fired), `succeeded` (the
 * reloaded iframe finished painting), `failed` (the reload never confirmed a
 * load within the safety window). `trigger` distinguishes the normal
 * log-stream compile-success reveal from the fallback timer that fires when
 * that log line is lost. Lets us measure how often the preview goes stale and
 * whether the fallback recovers it, on both desktop and mobile
 * (getDeviceContext).
 */
export function trackPreviewAutoRefresh(
  phase: "requested" | "succeeded" | "failed",
  props: {
    projectId: string | null;
    trigger: "log_compile_success" | "fallback_timer";
    elapsedMs?: number | null;
  },
) {
  if (!initialized) return;
  posthog.capture(`preview_auto_refresh_${phase}`, {
    project_id: props.projectId,
    trigger: props.trigger,
    ...(props.elapsedMs != null
      ? { elapsed_ms: Math.round(props.elapsedMs) }
      : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: the preview asked the sandbox for a build asset that no longer exists —
 * `ChunkLoadError: Loading chunk app/layout failed` and friends. Emitted for
 * EVERY occurrence, including the ones the in-iframe script recovers by itself,
 * so the rate is measurable rather than only its visible tail.
 *
 * The properties are what a post-mortem actually needs: which chunk, which build
 * it belonged to, which sandbox host served it, where the user was, and whether
 * recovery worked (`recovered` false = the user saw Panda's repair card).
 * `source` separates the in-iframe script — the only reporter that survives a
 * failed LAYOUT chunk — from the bridge relay, which needs the client bundle to
 * have loaded at all.
 */
export function trackPreviewChunkLoadFailure(props: {
  projectId: string | null;
  route: string | null;
  chunkPath: string | null;
  buildId: string | null;
  previewHost: string | null;
  attempt: number;
  recovered: boolean;
  source: "iframe_script" | "bridge_relay";
}) {
  if (!initialized) return;
  posthog.capture("preview_chunk_load_failed", {
    project_id: props.projectId,
    route: props.route,
    chunk_path: props.chunkPath,
    build_id: props.buildId,
    preview_host: props.previewHost,
    attempt: props.attempt,
    recovered: props.recovered,
    report_source: props.source,
    user_agent:
      typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
    ...getDeviceContext(),
  });
}

/**
 * Track: User opened the preview pane on mobile (mobileView -> "preview").
 * Mobile-only — desktop shows chat + preview side by side. `mode`
 * distinguishes the Build vs Grow workspace.
 */
export function trackPreviewOpened(
  projectId: string,
  mode?: "build" | "growth",
) {
  if (!initialized) return;
  posthog.capture("preview_opened", {
    project_id: projectId,
    mode,
  });
}

/**
 * Track: User opened the chat pane on mobile (mobileView -> "chat").
 * Mobile-only. Fires in both Build and Grow mode (`mode` distinguishes them).
 */
export function trackChatOpened(projectId: string, mode?: "build" | "growth") {
  if (!initialized) return;
  posthog.capture("chat_opened", {
    project_id: projectId,
    mode,
  });
}

/**
 * Track: User left the chat pane on mobile (mobileView moved away from
 * "chat"). Mobile-only. Fires in both Build and Grow mode.
 */
export function trackChatClosed(projectId: string, mode?: "build" | "growth") {
  if (!initialized) return;
  posthog.capture("chat_closed", {
    project_id: projectId,
    mode,
  });
}

/**
 * Track: The Publish CTA became available to the user (a build produced files
 * and we're not in growth mode). Fires once per project load.
 */
export function trackPublishCtaShown(projectId: string) {
  if (!initialized) return;
  posthog.capture("publish_cta_shown", {
    project_id: projectId,
  });
}

/**
 * Which affordance the user opened the publish flow from (GT-22). Carried on
 * `publish_clicked` so "what share of publishes originate from the inline chat
 * CTA vs. the top-right button?" — the ticket's KPI — is a single breakdown
 * rather than an inference from event ordering.
 *
 * "unknown" covers the deep links (a domain-suggestion email, the post-upgrade
 * restore) that reopen the dialog without a user pressing any publish surface.
 */
export type PublishSource =
  /** The top-right header button — the control arm's only publish surface. */
  | "header"
  /** The same button on mobile, where it lives in the merged preview toolbar. */
  | "mobile_header"
  | "share_dialog"
  /** The never-published exit prompt's "Publish now". */
  | "exit_prompt"
  /** GT-22 — the inline CTA on the build-completion message. */
  | "inline_chat_cta"
  | "unknown";

/**
 * Track: User genuinely initiated a publish from the Publish dialog. Does NOT
 * fire for the automatic deploys (auto-publish after build, mobile preview
 * auto-deploy) — those call handleDeploy() directly. `is_update` = true means
 * re-publishing an already-live site.
 *
 * `source` attributes the publish to the affordance that opened the flow, and
 * `variant` records which arm of the inline-CTA experiment the user was in — so
 * publish RATE can be compared across arms, not just clicks on the new button.
 * Both are optional: older call sites stay valid and report `unknown` / null.
 */
export function trackPublishClicked(
  projectId: string,
  isUpdate: boolean,
  source: PublishSource = "unknown",
  variant?: InlinePublishCtaVariant | null,
) {
  if (!initialized) return;
  posthog.capture("publish_clicked", {
    project_id: projectId,
    is_update: isUpdate,
    source,
    inline_publish_cta_variant: variant ?? null,
  });
}

// ──────────────────────────────────────────────────
// GT-22 — inline "Publish your app" CTA in the build-completion message
//
// The pair of events the ticket names. Both arms of the experiment emit
// `inline_publish_cta_shown`, including control, where `rendered` is false: an
// A/B test needs the same denominator on both sides, and "users who reached a
// completion message that WOULD have carried the CTA" is that denominator. With
// a control exposure the lift query is a straight comparison of publish rate
// between variants; without it, control has no measurable population at all.
// ──────────────────────────────────────────────────

/**
 * Track: a build-completion message that qualifies for the inline publish CTA
 * was rendered. Fires at most once per (project, message) per page session —
 * the CTA lives on a durable chat row, so an unguarded impression would refire
 * on every re-render and inflate the denominator.
 */
export function trackInlinePublishCtaShown(props: {
  project_id: string;
  /** The completion message the CTA belongs to — dedupe key and funnel join. */
  message_id: string;
  variant: InlinePublishCtaVariant;
  /** False in the control arm: the user was eligible, the button did not paint. */
  rendered: boolean;
  /** The app already has a live deploy, so the CTA reads "Publish your update". */
  is_update: boolean;
}) {
  if (!initialized) return;
  posthog.capture("inline_publish_cta_shown", props);
}

/**
 * Track: the user tapped the inline "Publish your app" CTA. Only ever fires in
 * the "inline" arm — control has no button to press.
 *
 * `ms_since_shown` is the whole point of the ticket made measurable: the CTA
 * exists so users can act on satisfaction *in the moment*, and this is how long
 * that moment lasted.
 */
export function trackInlinePublishCtaClicked(props: {
  project_id: string;
  message_id: string;
  variant: InlinePublishCtaVariant;
  is_update: boolean;
  ms_since_shown: number | null;
}) {
  if (!initialized) return;
  posthog.capture("inline_publish_cta_clicked", props);
}

/**
 * Track: Automatic publish triggered after a successful build (the
 * auto-publish-for-all-users feature). This is NOT a user click — it fires by
 * itself once a build succeeds. Pair with publish_clicked (manual) and the
 * pipeline_deploy_* outcome events to see the auto -> (fail) -> manual
 * republish flow. `is_update` = re-publishing an already-live site.
 */
export function trackAutoPublishTriggered(
  projectId: string,
  isUpdate: boolean,
) {
  if (!initialized) return;
  posthog.capture("auto_publish_triggered", {
    project_id: projectId,
    is_update: isUpdate,
  });
}

// ──────────────────────────────────────────────────
// Live update — edit → live, without a Republish click
//
// The ticket's own acceptance criterion is "instrument edit-to-live timing,
// failures, and retries so the team can confirm this is not becoming a
// bottleneck", and these three events are that instrument. They are deliberately
// SEPARATE from `publish_clicked` / `auto_publish_triggered`: an automatic
// update is not a publish the user performed, and mixing them would make the
// publish funnel's denominator meaningless the day this ships.
//
// The number that answers the ticket is `ms_edit_to_live` on
// `live_update_completed` — measured from the edit that created the pending
// state, NOT from the moment the deploy started, because the quiet window and
// any wait for an in-flight build are part of what the user experiences as
// "how long until my change is live".
// ──────────────────────────────────────────────────

/**
 * Track: an automatic live update started. `attempt` is 1 for the first try and
 * 2 for the one automatic retry, so "how often does this need a second go?" is
 * a single breakdown rather than an inference from event ordering.
 */
export function trackLiveUpdateStarted(props: {
  project_id: string;
  attempt: number;
  /** ms from the edit that created the pending state to this deploy starting. */
  ms_since_edit: number | null;
  /** True when the update waited for a build/publish to finish first. */
  waited_for_build: boolean;
}) {
  if (!initialized) return;
  posthog.capture("live_update_started", props);
}

/**
 * Track: the live site now carries the edit. `ms_edit_to_live` is the metric
 * the ticket is judged on; `ms_deploying` separates our pipeline's share of it
 * from the coalescing window and any build the update queued behind.
 */
export function trackLiveUpdateCompleted(props: {
  project_id: string;
  attempt: number;
  ms_edit_to_live: number | null;
  ms_deploying: number | null;
}) {
  if (!initialized) return;
  posthog.capture("live_update_completed", props);
}

/**
 * Track: an automatic live update failed. `will_retry` is what makes the retry
 * budget visible: a failure that retries and a failure that hands the user back
 * a Republish button are very different outcomes, and only the second one is a
 * regression to the friction this path removed.
 *
 * `failure_code` is the same publish failure code the panel classifies on, so
 * these rows join straight onto `publish_failed`.
 */
export function trackLiveUpdateFailed(props: {
  project_id: string;
  attempt: number;
  failure_code: string | null;
  will_retry: boolean;
  ms_since_edit: number | null;
}) {
  if (!initialized) return;
  posthog.capture("live_update_failed", props);
}

/**
 * Track: an edit could have gone live by itself and deliberately did not — the
 * rules in lib/publish/live-update.ts held it back. Fires once per (project,
 * reason) per page session; `reason` is the rule that held it.
 *
 * Without this the funnel could only see updates that HAPPENED, and "users are
 * still doing the republish chore" would show up as an absence of events —
 * which is indistinguishable from "nobody edited anything".
 */
export function trackLiveUpdateHeld(props: {
  project_id: string;
  reason: string;
}) {
  if (!initialized) return;
  posthog.capture("live_update_held", props);
}

/**
 * Track: a publish attempt failed, tagged with the failure code so PostHog can
 * separate a GitHub branch conflict ("github_conflict" — another change landed
 * on `main` mid-publish and the edge function's rebase-and-retry couldn't win
 * the race) from a transient upload failure ("github_upload"), a blocked build
 * ("files_dropped" / "unresolved_imports"), or an untagged "generic" failure.
 * Fires for both manual and automatic publishes; `trigger` records which. This
 * is the signal that lets a branch conflict be distinguished from a generic
 * publish failure in the funnel (PANDA-1453).
 *
 * Callers also pass `failure_kind` (repairable / retryable — which recovery the
 * user was offered) and `failure_surface` (service / app — whose fault it was)
 * in `extra`, both derived from lib/publish/failure-copy.ts. `failure_code`
 * remains the grouping key; those two exist so "what share of publish failures
 * were OUR infrastructure?" is one filter rather than a list of codes pasted
 * into a dashboard and left to rot as codes are added.
 */
export function trackPublishFailed(
  projectId: string,
  failureCode: string,
  trigger: string,
  extra?: Record<string, unknown>,
) {
  if (!initialized) return;
  posthog.capture("publish_failed", {
    project_id: projectId,
    failure_code: failureCode,
    trigger,
    // Device/URL context was NULL in the funnel (PANDA publish-failed report):
    // we run posthog with autocapture:false, so the $browser/$os/$current_url
    // enrichment that pageviews get isn't attached to this custom event.
    // `failureContext` captures the raw signals explicitly (current_url +
    // user_agent) so the event stays self-describing, and adds the environment
    // / session context every failure event shares.
    ...failureContext(),
    ...getDeviceContext(),
    // Structured repair/import context (unresolved_imports): importer file,
    // failing specifier, whether an auto-repair ran and how it ended.
    ...extra,
  });
}

// ──────────────────────────────────────────────────
// Return-to-chat feedback survey (PANDA-1112)
//
// A short, skippable two-question survey shown the FIRST time a user returns
// to the chat after reviewing their preview. It captures the frustrated
// mid-build leaver who session recordings only let us guess at. Funnel:
//   shown -> answered(satisfaction) -> [answered(missing)] -> completed | skipped
// `satisfaction` is the Q1 single-tap answer; `missing_reason` is the Q2
// quick-pick value (or "other" when free text was used).
// ──────────────────────────────────────────────────

type FeedbackSatisfaction = "yes" | "not_quite" | "no";

/** Track: the return-feedback survey was shown to the user. */
export function trackFeedbackSurveyShown(props: {
  project_id: string;
  is_remote_preview: boolean;
}) {
  if (!initialized) return;
  posthog.capture("return_feedback_survey_shown", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user answered Q1 ("Did this do what you needed?"). */
export function trackFeedbackSurveySatisfaction(props: {
  project_id: string;
  satisfaction: FeedbackSatisfaction;
}) {
  if (!initialized) return;
  posthog.capture("return_feedback_satisfaction", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user finished the survey — Q1 always present; Q2 only when Q1
 *  was not "yes". `missing_detail` carries the free-text when the reason is
 *  "other" (or when the user typed alongside a quick-pick). */
export function trackFeedbackSurveyCompleted(props: {
  project_id: string;
  satisfaction: FeedbackSatisfaction;
  missing_reason?: string | null;
  missing_label?: string | null;
  missing_detail?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("return_feedback_completed", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user dismissed the survey without finishing. `step` is where
 *  they bailed ("satisfaction" = Q1, "missing" = Q2). */
export function trackFeedbackSurveySkipped(props: {
  project_id: string;
  step: "satisfaction" | "missing";
}) {
  if (!initialized) return;
  posthog.capture("return_feedback_skipped", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Build-quality survey (PANDA-1244)
//
// A short, skippable modal shown once, a few seconds after a user's FIRST
// build renders in the preview, asking "Did Panda build what you expected?".
// Captures the gap between what the user pictured and what Panda produced, at
// the exact moment they first see the result. Funnel:
//   shown -> completed | skipped
// The completed event carries the shared survey response schema (survey_id /
// survey_answer / survey_text / trigger_source / …) so analytics, filtering,
// and research workflows stay comparable across all four planned surveys.
// ──────────────────────────────────────────────────

export type BuildQualityAnswer =
  | "better"
  | "exactly"
  | "somewhat"
  | "not-at-all";

/**
 * Read the current PostHog session id and a direct deep-link to its session
 * recording. Used to stamp the build-quality response so the Slack message can
 * link straight to the replay (PANDA-1244). Best-effort: returns nulls when the
 * recorder hasn't started or the SDK isn't loaded yet.
 */
export function getSessionReplayInfo(): {
  sessionId: string | null;
  replayUrl: string | null;
} {
  try {
    const sessionId = posthog.get_session_id?.() ?? null;
    const replayUrl = posthog.get_session_replay_url?.() ?? null;
    return { sessionId, replayUrl };
  } catch {
    return { sessionId: null, replayUrl: null };
  }
}

/** Track: the build-quality survey was shown to the user. */
export function trackBuildQualitySurveyShown(props: {
  project_id: string;
  is_remote_preview: boolean;
}) {
  if (!initialized) return;
  posthog.capture("build_quality_survey_shown", {
    survey_id: "build_quality",
    trigger_source: "first_preview_render",
    ...props,
    ...getDeviceContext(),
  });
}

/**
 * Track: the user submitted a build-quality answer. Emits the SHARED
 * `survey_response` event — the same event the other three lifecycle surveys
 * use (never_built PANDA-1245, never_published PANDA-1246, never_upgraded
 * PANDA-1247) — keyed by `survey_id` so all four are comparable for analytics,
 * filtering and research, exactly as every survey ticket requires. Carries the
 * common schema (survey_id / survey_text / trigger_source / has_built /
 * has_published / has_upgraded / session_id / timestamp) plus build-quality's
 * additive fields (survey_answer / build_prompt). `$session_id` links the event
 * to the user's session recording for the #panda-feedback replay link, and
 * `flag_for_interview` marks the negative answers ("somewhat" / "not at all")
 * for follow-up outreach.
 */
export function trackBuildQualitySurveyCompleted(props: {
  project_id: string;
  survey_answer: BuildQualityAnswer;
  survey_text: string | null;
  build_prompt: string | null;
  has_published: boolean;
  has_upgraded: boolean;
  session_id: string | null;
}) {
  if (!initialized) return;
  const flagForInterview =
    props.survey_answer === "somewhat" || props.survey_answer === "not-at-all";
  posthog.capture("survey_response", {
    survey_id: "build_quality",
    trigger_source: "first_preview_render",
    has_built: true,
    flag_for_interview: flagForInterview,
    timestamp: new Date().toISOString(),
    ...props,
    ...(props.session_id ? { $session_id: props.session_id } : {}),
    ...getDeviceContext(),
  });
}

/**
 * Track: the user answered the build-quality survey, was shown the follow-on
 * offer, and dismissed it ("Maybe later" / "No thanks" / X / Esc).
 *
 * Distinct from `build_quality_survey_skipped`, which means the user closed the
 * survey WITHOUT answering. Conflating them would make the recovery offer look
 * like it was refused by everyone who never saw it. Keyed by answer so a decline
 * after "not at all what I expected" is separable from one after "better than
 * expected" — the same bucket split the impression and click events carry.
 */
export function trackBuildQualityOfferDismissed(props: {
  project_id: string;
  survey_answer: BuildQualityAnswer;
  /** Whether the card the user closed was the $1 trial offer or the generic
   *  upgrade nudge shown to trial-ineligible free users. */
  trial_offer_shown: boolean;
}) {
  if (!initialized) return;
  posthog.capture("build_quality_offer_dismissed", {
    survey_id: "build_quality",
    trigger_source: "first_preview_render",
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user dismissed the build-quality survey without answering. */
export function trackBuildQualitySurveySkipped(props: { project_id: string }) {
  if (!initialized) return;
  posthog.capture("build_quality_survey_skipped", {
    survey_id: "build_quality",
    trigger_source: "first_preview_render",
    ...props,
    ...getDeviceContext(),
  });
}

/**
 * Track: the Discord help CTA was SHOWN after a negative build-quality answer.
 *
 * A user who has just said the first preview missed is the one most likely to
 * churn, so the survey hands them a recovery path instead of a dead-end
 * thank-you. These two events are what make that path measurable: shown →
 * clicked, keyed by the answer that produced it, so "did this retain
 * disappointed users?" is answerable per bucket rather than in aggregate.
 *
 * `trial_offer_shown` separates the two placements — the CTA rides along under
 * the $1 recovery offer for a trial-eligible user, and stands alone on the
 * confirmation card for everyone else. Their click-through is not comparable,
 * because in one the CTA competes with a priced primary button.
 */
export function trackBuildQualityDiscordCtaShown(props: {
  project_id: string;
  survey_answer: BuildQualityAnswer;
  trial_offer_shown: boolean;
}) {
  if (!initialized) return;
  posthog.capture("build_quality_discord_cta_shown", {
    survey_id: "build_quality",
    trigger_source: "first_preview_render",
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user clicked through to Discord from that CTA. Pairs with
 *  `build_quality_discord_cta_shown` to give the recovery path a click-through
 *  rate; it does not imply the survey closed, since the invite opens in a new
 *  tab and leaves the card standing. */
export function trackBuildQualityDiscordCtaClicked(props: {
  project_id: string;
  survey_answer: BuildQualityAnswer;
  trial_offer_shown: boolean;
}) {
  if (!initialized) return;
  posthog.capture("build_quality_discord_cta_clicked", {
    survey_id: "build_quality",
    trigger_source: "first_preview_render",
    ...props,
    ...getDeviceContext(),
  });
}

/* ── Build-feedback eligibility funnel (PANDA-1289) ──
   The build-quality prompt is now gated on the user actually evaluating the
   preview for the current build, not just on `previewReady`. These four events
   record the funnel — became eligible → shown → submitted/dismissed — each
   carrying the build version, how long the preview was visibly evaluated, and
   the qualifying interaction type, so we can confirm the gate behaves and
   compare against the old early-trigger behaviour. */
export type BuildFeedbackInteractionType = "interaction" | "dwell";

interface BuildFeedbackFunnelProps {
  project_id: string;
  /** Monotonic counter identifying the build being evaluated this session. */
  build_version: number;
  /** Cumulative active-visible time of the preview before this moment (ms). */
  preview_visible_ms: number;
  /** What qualified the user as having evaluated the build: a real preview
   *  interaction, or enough passive dwell time on a static page. */
  interaction_type: BuildFeedbackInteractionType;
}

/** Track: the build-quality prompt became eligible (the user evaluated the
 *  current build), before it is actually shown at the next safe transition. */
export function trackBuildFeedbackEligible(props: BuildFeedbackFunnelProps) {
  if (!initialized) return;
  posthog.capture("build_feedback_eligible", {
    survey_id: "build_quality",
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the build-quality prompt was shown after the eligibility gate. */
export function trackBuildFeedbackShown(props: BuildFeedbackFunnelProps) {
  if (!initialized) return;
  posthog.capture("build_feedback_shown", {
    survey_id: "build_quality",
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user submitted an answer to the gated build-quality prompt. */
export function trackBuildFeedbackSubmitted(props: BuildFeedbackFunnelProps) {
  if (!initialized) return;
  posthog.capture("build_feedback_submitted", {
    survey_id: "build_quality",
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user dismissed the gated build-quality prompt without answering. */
export function trackBuildFeedbackDismissed(props: BuildFeedbackFunnelProps) {
  if (!initialized) return;
  posthog.capture("build_feedback_dismissed", {
    survey_id: "build_quality",
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Pre-publish exit-intent survey (PANDA-1119)
// ──────────────────────────────────────────────────
// Fires only before the app has ever been published, when the user shows
// exit intent (mouse-leave on desktop, idle on mobile). The branch mirrors
// the prototype: shown -> choice(improve|leave) -> [reason] -> [money] ->
// founder_call. Every step is skippable; dismissing returns the user to the
// editor so they can still publish.

/** Track: the exit-intent popup was shown. */
export function trackExitIntentShown(props: {
  project_id: string;
  is_remote_preview: boolean;
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_shown", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user picked a path on the first exit screen. */
export function trackExitIntentChoice(props: {
  project_id: string;
  choice: "improve" | "leave";
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_choice", {
    ...props,
    ...getDeviceContext(),
  });
}

/**
 * Track: the user submitted an improvement from the exit "improve" flow.
 * `detail` is the full text sent (typed or chip); `source` distinguishes a
 * one-tap preset chip from free-typed text; `result` records what the submit
 * did — today it always hands the prompt to the Build chat ("submitted_to_build").
 */
export function trackExitIntentImproveSubmitted(props: {
  project_id: string;
  detail: string;
  source: "preset" | "custom";
  is_remote_preview: boolean;
  result: "submitted_to_build";
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_improve_submitted", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user submitted a "why I'm leaving" reason. */
export function trackExitIntentReason(props: {
  project_id: string;
  reason: string;
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_reason", {
    ...props,
    ...getDeviceContext(),
  });
}

/**
 * Track: the feedback survey's THANK-YOU step was shown — the modal that now
 * carries both the founder-call/voucher offer and (for eligible free users) the
 * $1 trial recovery offer.
 *
 * A separate event from `exit_intent_shown`, which fires when the flow OPENS on
 * the "Before you go…" screen. The thank-you step is several steps and a
 * submitted answer downstream of that, so the open event cannot serve as this
 * one's denominator — most exits never reach here at all. Without its own
 * impression row, "does this entrypoint convert?" has no denominator, which is
 * the whole question this offer exists to answer.
 *
 * `trial_offer_shown` records whether the trial half rendered, so the trial's
 * own conversion rate is measured against the impressions that actually carried
 * it rather than against every thank-you screen. The paired
 * `upgrade_entrypoint_viewed` (entry_point "feedback_survey_trial") is the
 * canonical impression for the offer itself; this flag lets the two be
 * reconciled without a join.
 */
export function trackFeedbackSurveyThanksShown(props: {
  project_id: string;
  /** Whether the $1 recovery offer rendered alongside the founder call. */
  trial_offer_shown: boolean;
  /** Why the trial half was withheld, when it was (paid_user, already_used_trial…). */
  eligibility_status?: TrialEligibilityStatus | null;
  /** The "what would need to happen to launch?" answer that led here, when the
   *  user gave one. Null on the money-branch path, which skips the reason. */
  reason?: string | null;
}) {
  if (!initialized) return;
  posthog.capture("feedback_survey_thanks_shown", {
    survey_id: "exit_intent",
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user clicked "Book founder call" (money branch or post-survey). */
export function trackExitIntentFounderCall(props: {
  project_id: string;
  source: "money" | "post_survey";
  /** Whether the $1 recovery offer was on screen next to this CTA. The
   *  acceptance criterion is that the founder call stays accessible and does not
   *  end up competing with the trial — which is only checkable if we can compare
   *  its click rate with and without the trial card present. */
  trial_offer_shown?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_founder_call_clicked", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user dismissed the exit-intent flow (closed/skipped) at `step`. */
export function trackExitIntentDismissed(props: {
  project_id: string;
  step: "exit" | "improve" | "reason" | "money" | "thanks";
  /** On the "thanks" step: whether the $1 recovery offer was on screen when the
   *  user walked away from it. Separates "declined the trial" from "never saw
   *  one", which the step alone cannot. */
  trial_offer_shown?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_dismissed", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// "Before you go" explicit-exit funnel (PANDA-1290)
// ──────────────────────────────────────────────────
// These four events replace the old mouse-edge signal with the genuine exit
// signal the ticket asks for: an explicit attempt to LEAVE the current project
// (clicking Home / Projects / Back to projects, switching project, or browser
// Back). `exit_action` records which one; `device_type` from getDeviceContext
// doubles as the required device class. `exit_intent_detected` is emitted ONLY
// for these real exit actions — never for closing a modal or Chat/Preview
// switches, which no longer reach this code at all.

/** The kind of explicit exit that produced the signal. */
export type ExitActionType = "internal_nav" | "switch_project" | "browser_back";

/** Track: a genuine, eligible exit action was detected. */
export function trackExitIntentDetected(props: {
  project_id: string;
  exit_action: ExitActionType;
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_detected", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: "Before you go" was actually shown for an exit attempt. */
export function trackBeforeYouGoShown(props: {
  project_id: string;
  exit_action: ExitActionType;
}) {
  if (!initialized) return;
  posthog.capture("before_you_go_shown", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user chose to STAY (navigation cancelled). */
export function trackBeforeYouGoStay(props: {
  project_id: string;
  exit_action: ExitActionType;
}) {
  if (!initialized) return;
  posthog.capture("before_you_go_stay", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user chose to LEAVE (navigation resumed to the destination). */
export function trackBeforeYouGoLeave(props: {
  project_id: string;
  exit_action: ExitActionType;
}) {
  if (!initialized) return;
  posthog.capture("before_you_go_leave", {
    ...props,
    ...getDeviceContext(),
  });
}

/**
 * Track: an eligible "Before you go" exit was stood down because a
 * higher-priority retention surface (the publish blocker) was already shown this
 * session, so the two are never shown back-to-back (PANDA-1443). `reason` makes
 * the suppression visible in analytics.
 */
export function trackExitIntentSuppressed(props: {
  project_id: string;
  reason: "publish_blocker_recently_shown";
  exit_action: ExitActionType;
}) {
  if (!initialized) return;
  posthog.capture("exit_intent_suppressed", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// "Never built" survey (PANDA-1245)
// ──────────────────────────────────────────────────
// One of the four lifecycle surveys. Shown on the home/empty state to users who
// signed up but never started a build, asking "What stopped you from building?".
// Two triggers: exit intent (cursor leaving toward the browser chrome) and a
// next-session prompt 24h after signup with still no build. The free-text
// response is saved in PostHog and forwarded to #panda-feedback server-side via
// /api/surveys/never-built (which also attaches the session recording link); the
// shown/skipped funnel steps below are captured client-side.

export type NeverBuiltTriggerSource = "exit_intent" | "no_build_24h";

/**
 * Read the current PostHog session id and a deep-link to its recording. Used to
 * attach the never-built response to the exact replay in the Slack message and
 * the PostHog event. Both calls are guarded — they return undefined before the
 * recorder boots or when analytics is disabled — so callers can pass through
 * whatever is available without breaking the submit.
 */
export function getSessionContext(): {
  session_id?: string;
  session_replay_url?: string;
} {
  if (!initialized) return {};
  try {
    const session_id = posthog.get_session_id?.() || undefined;
    const session_replay_url = posthog.get_session_replay_url?.() || undefined;
    return { session_id, session_replay_url };
  } catch {
    return {};
  }
}

/** Track: the never-built survey was shown. */
export function trackNeverBuiltSurveyShown(props: {
  trigger_source: NeverBuiltTriggerSource;
  time_since_signup_ms?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("never_built_survey_shown", {
    survey_id: "never_built",
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user dismissed the never-built survey without sending feedback. */
export function trackNeverBuiltSurveySkipped(props: {
  trigger_source: NeverBuiltTriggerSource;
}) {
  if (!initialized) return;
  posthog.capture("never_built_survey_skipped", {
    survey_id: "never_built",
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Never-published lifecycle survey (PANDA-1246)
// ──────────────────────────────────────────────────
// "What's stopping you from publishing?" — a single open-text survey for users
// who built but haven't published. Fires from three triggers (exit intent after
// build, 60s after the publish flow is abandoned, and 72h-built-not-published on
// the next session). The response is persisted + sent to PostHog/Slack server
// side; these client events track the funnel (shown → submitted/dismissed).

type NeverPublishedTrigger = "exit_intent" | "publish_abandoned" | "built_72h";

/** Track: the never-published survey was shown. */
export function trackNeverPublishedShown(props: {
  project_id: string;
  trigger_source: NeverPublishedTrigger;
}) {
  if (!initialized) return;
  posthog.capture("never_published_survey_shown", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user submitted a free-text answer. */
export function trackNeverPublishedSubmitted(props: {
  project_id: string;
  trigger_source: NeverPublishedTrigger;
}) {
  if (!initialized) return;
  posthog.capture("never_published_survey_submitted", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user dismissed the survey without answering. */
export function trackNeverPublishedDismissed(props: {
  project_id: string;
  trigger_source: NeverPublishedTrigger;
}) {
  if (!initialized) return;
  posthog.capture("never_published_survey_dismissed", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Publish-blocker prompt — eligible unpublished exit (PANDA-1291)
// ──────────────────────────────────────────────────
// Refines the never-published survey above so "What's stopping you from
// publishing?" only surfaces when an evaluated, publishable, never-published
// build is abandoned through a genuine project-exit action (leaving for
// Dashboard/Home/projects, switching projects, or in-app Back). These five
// events trace that funnel — from the moment an exit qualifies, through the
// prompt being shown, to the user's choice. Every event carries the project,
// the build version, the device class (from getDeviceContext), how long the
// preview had been visible, and which exit action triggered it.

export interface PublishBlockerEventProps {
  project_id: string;
  /** Best-effort build version (built-phase count); null when unknown. */
  build_version: number | null;
  /** Cumulative ms the latest preview had been visible at exit time. */
  preview_visible_ms: number;
  /** Which explicit exit action surfaced the prompt (shared exit-guard type). */
  exit_action_type: ExitActionType;
}

function capturePublishBlocker(
  event: string,
  props: PublishBlockerEventProps,
): void {
  if (!initialized) return;
  posthog.capture(event, { ...props, ...getDeviceContext() });
}

/** Track: an exit attempt met every condition to show the prompt. */
export function trackUnpublishedExitEligible(props: PublishBlockerEventProps) {
  capturePublishBlocker("unpublished_exit_eligible", props);
}

/** Track: the publish-blocker prompt was shown. */
export function trackPublishBlockerShown(props: PublishBlockerEventProps) {
  capturePublishBlocker("publish_blocker_shown", props);
}

/** Track: the user submitted a reason, then continued leaving. */
export function trackPublishBlockerSubmitted(props: PublishBlockerEventProps) {
  capturePublishBlocker("publish_blocker_submitted", props);
}

/** Track: the user dismissed the prompt and continued leaving. */
export function trackPublishBlockerDismissed(props: PublishBlockerEventProps) {
  capturePublishBlocker("publish_blocker_dismissed", props);
}

/** Track: the user chose "Publish now" — exit cancelled, publish flow opened. */
export function trackPublishBlockerPublishClicked(
  props: PublishBlockerEventProps,
) {
  capturePublishBlocker("publish_blocker_publish_clicked", props);
}

/**
 * Track: an eligible publish-blocker exit was stood down because the "Before you
 * go" exit survey was already shown this session, so the two are never shown
 * back-to-back (PANDA-1443). The publish blocker still WINS when both are
 * eligible on the same exit attempt (the exit dispatcher offers it first); this
 * only fires on a later attempt after the exit survey already ran this session.
 */
export function trackPublishBlockerSuppressed(
  props: PublishBlockerEventProps & {
    suppressed_reason: "exit_intent_recently_shown";
  },
) {
  capturePublishBlocker("publish_blocker_suppressed", props);
}

// ──────────────────────────────────────────────────
// Never-upgraded lifecycle survey (PANDA-1247)
// ──────────────────────────────────────────────────
// "What would make upgrading feel like an obvious decision?" — a single
// open-text survey for users who have PUBLISHED an app but never converted to
// paid. Fires from three triggers (the upgrade modal being dismissed, hitting a
// paywall, and published-7d-ago-still-free on the next session). The response is
// persisted + sent to PostHog/Slack server side; these client events track the
// funnel (shown → submitted/dismissed). Schema mirrors the other three surveys.

type NeverUpgradedTrigger = "upgrade_dismissed" | "published_7d" | "paywall";

/** Track: the never-upgraded survey was shown. */
export function trackNeverUpgradedShown(props: {
  project_id: string;
  trigger_source: NeverUpgradedTrigger;
}) {
  if (!initialized) return;
  posthog.capture("never_upgraded_survey_shown", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user submitted a free-text answer. */
export function trackNeverUpgradedSubmitted(props: {
  project_id: string;
  trigger_source: NeverUpgradedTrigger;
}) {
  if (!initialized) return;
  posthog.capture("never_upgraded_survey_submitted", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user dismissed the survey without answering. */
export function trackNeverUpgradedDismissed(props: {
  project_id: string;
  trigger_source: NeverUpgradedTrigger;
}) {
  if (!initialized) return;
  posthog.capture("never_upgraded_survey_dismissed", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Read the current PostHog session id (for the survey's session-replay link). */
export function getPosthogSessionId(): string | null {
  if (!initialized) return null;
  try {
    return posthog.get_session_id() ?? null;
  } catch {
    return null;
  }
}

// ──────────────────────────────────────────────────
// Lifecycle-prompt suppression (return-session bug)
// ──────────────────────────────────────────────────
// The counterpart of the *_shown events above: records that a lifecycle prompt
// whose stored conditions were all satisfied stood down because no fresh
// qualifying event (lib/ui/session-qualifying-events.ts) had happened this
// session — i.e. the sessions that used to produce the stale return-session
// modals. One event per campaign+reason per page lifetime: the deciding effects
// re-run on unrelated state changes, and a per-render stream would drown the
// impression events this is meant to be read against.

const lifecycleSuppressionReported = new Set<string>();

/** Track: a lifecycle prompt was suppressed for lack of a fresh session event. */
export function trackLifecyclePromptSuppressed(props: {
  /** Which prompt stood down (its campaign identity). */
  campaign: "build_quality" | "never_upgraded" | "return_feedback";
  /** Why — the missing evidence class, not a free-text explanation. */
  reason: "no_fresh_build" | "no_fresh_qualifying_event";
  project_id: string;
}) {
  if (!initialized) return;
  const key = `${props.campaign}:${props.reason}:${props.project_id}`;
  if (lifecycleSuppressionReported.has(key)) return;
  lifecycleSuppressionReported.add(key);
  posthog.capture("lifecycle_prompt_suppressed", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────
// Activation blockers
// ──────────────────────────────────────────────────

/**
 * Track: User was blocked by the email-verification gate (the gate modal was
 * shown). Fires once per block. `credits_consumed` = lifetime credits the user
 * had already spent at the moment they were blocked. PostHog auto-captures
 * device context, so mobile-vs-desktop is available without extra props.
 */
export function trackEmailVerificationRequired(
  creditsConsumed: number,
  email: string | null,
) {
  if (!initialized) return;
  posthog.capture("email_verification_required", {
    email,
    credits_consumed: creditsConsumed,
    // The only signal that the gate opened — segmenting it by device is what
    // makes a mobile-only stranded-account regression visible.
    ...getDeviceContext(),
  });
}

/**
 * Track: a third-party marketing/referral beacon (Affonso, GTM/GA, Meta pixel)
 * couldn't be reached from the browser — blocked by an ad blocker, offline, or
 * a vendor outage.
 *
 * A product event, NOT an exception, on purpose: it is a handled outcome with a
 * name and a rate to measure, and filing it as an Error Tracking issue is the
 * exact bug this reports on — see docs/failure-monitoring.md. The gate modal's
 * fetch interceptor fires it at most once per host per page load, so a blocked
 * vendor that retries in a loop can't flood ingestion.
 */
export function trackThirdPartyBeaconFailed(props: {
  /** Hostname that failed, e.g. `api.affonso.io`. */
  host: string;
  /** The rejection's message, e.g. "Failed to fetch". Never a stack. */
  reason: string;
}) {
  if (!initialized) return;
  posthog.capture("third_party_beacon_failed", {
    beacon_host: props.host,
    failure_code: "fetch_failed",
    reason: props.reason,
  });
}

// ──────────────────────────────────────────────────
// Billing / payment funnel (Phase A) — client INTENT events
//
// The money-truth events (purchase completed / renewed / failed / cancelled)
// fire SERVER-side from the Stripe webhook (lib/posthog/billing-events.ts),
// because Checkout is hosted on Stripe and the browser never sees the payment
// succeed. These two are the client-side INTENT signals — the user clicked to
// pay and we're about to redirect them to Stripe. They fire right before the
// redirect, so abandons (user reaches Stripe but never pays) are measurable as
// checkout_initiated WITHOUT a matching billing_purchase_completed.
// ──────────────────────────────────────────────────

/**
 * Track: User initiated a NEW subscription checkout — fired the moment the
 * Stripe Checkout URL comes back from /api/credits/checkout, just before we
 * redirect the browser to Stripe. `plan` is the selected plan id (the annual
 * variant encodes its cadence in the id). `source` identifies which surface
 * launched it (e.g. "upgrade_modal"). UTM is attached for revenue attribution.
 */
export function trackCheckoutInitiated(
  plan: string,
  source: string,
  projectId?: string | null,
  blockedAction?: string | null,
) {
  if (!initialized) return;
  posthog.capture("checkout_initiated", {
    plan,
    source,
    project_id: projectId ?? null,
    blocked_action: blockedAction ?? null,
    ...getUtmProperties(),
  });
}

/**
 * Track: User initiated a one-time credit TOP-UP checkout — fired the moment
 * the Stripe Checkout URL comes back from /api/credits/topup, just before
 * redirect. Separate from checkout_initiated because top-ups are mode=payment
 * (one-time), not a subscription. `source` identifies which surface launched
 * it (e.g. "add_credits_modal", "credits_usage_card").
 */
export function trackTopupCheckoutInitiated(
  credits: number,
  priceUsd: number,
  source: string,
  projectId?: string | null,
) {
  if (!initialized) return;
  posthog.capture("topup_checkout_initiated", {
    credits,
    price_usd: priceUsd,
    project_id: projectId ?? null,
    source,
    ...getUtmProperties(),
  });
}

/**
 * Track: User initiated the card-on-file (+5 bonus) setup — fired the moment
 * the Stripe setup-card URL comes back, just before redirect. `card_intent` is
 * "add" (first card, grants the bonus) or "update" (replace default card).
 * `source` identifies the surface (e.g. "card_entry_modal", "auto_refill",
 * "billing_page", "growth_billing_panel"). The matching server truth event is
 * `card_added`, fired from the Stripe webhook.
 */
export function trackCardSetupInitiated(
  cardIntent: "add" | "update",
  source: string,
  projectId?: string | null,
) {
  if (!initialized) return;
  posthog.capture("card_setup_initiated", {
    card_intent: cardIntent,
    source,
    project_id: projectId ?? null,
    ...getUtmProperties(),
  });
}

// ──────────────────────────────────────────────────
// Autonomous Grow funnel — entry → visible progress
//
// The funnel the "Autonomous Grow with revenue activity feed and mission
// progress" spec asks us to track end-to-end: did the user enter Grow, did
// Panda visibly start work, did it make mission progress, and where did credit
// gates appear. (A "limit set" step sat between the first two; there is no Grow
// limit to set — migration 216.) mission_type is one of first_lead /
// first_customer / first_revenue; grow_step_type is a plan step key
// (find_prospects, prepare_outreach, launch_campaign, …).
// ──────────────────────────────────────────────────

export type GrowMissionType = "first_lead" | "first_customer" | "first_revenue";

/** Track: the user opened/entered the Grow canvas for a project. */
export function trackGrowEntered(projectId: string, missionType?: GrowMissionType) {
  if (!initialized) return;
  posthog.capture("grow_entered", {
    project_id: projectId,
    mission_type: missionType ?? null,
  });
}

/** Track: a plan step reached "done". */
export function trackGrowProgressStepCompleted(projectId: string, stepType: string, missionType: GrowMissionType) {
  if (!initialized) return;
  posthog.capture("grow_progress_step_completed", {
    project_id: projectId,
    grow_step_type: stepType,
    mission_type: missionType,
  });
}

/** Track: the revenue activity feed produced/holds outcome metrics. */
export function trackRevenueActivityGenerated(projectId: string, properties: Record<string, number>) {
  if (!initialized) return;
  posthog.capture("revenue_activity_generated", {
    project_id: projectId,
    ...properties,
  });
}

/** Track: the user viewed the revenue activity feed. */
export function trackRevenueActivityViewed(projectId: string, missionType: GrowMissionType) {
  if (!initialized) return;
  posthog.capture("revenue_activity_viewed", {
    project_id: projectId,
    mission_type: missionType,
  });
}

/** Track: the active mission advanced to the next rung of the ladder. */
export function trackMissionProgressed(projectId: string, fromMission: GrowMissionType, toMission: GrowMissionType) {
  if (!initialized) return;
  posthog.capture("mission_progressed", {
    project_id: projectId,
    from_mission: fromMission,
    mission_type: toMission,
  });
}

/** Track: a credit was consumed against a visible Grow task. */
export function trackGrowCreditConsumed(
  projectId: string,
  activeTask: string | null,
  creditsBefore: number,
  creditsAfter: number,
) {
  if (!initialized) return;
  posthog.capture("grow_credit_consumed", {
    project_id: projectId,
    active_task: activeTask,
    credits_before: creditsBefore,
    credits_after: creditsAfter,
  });
}

/** Track: Grow ran low/out of credits while working on a task. */
export function trackGrowNeedsCredits(projectId: string, activeTask: string | null) {
  if (!initialized) return;
  posthog.capture("grow_needs_credits", {
    project_id: projectId,
    active_task: activeTask,
  });
}

/** Track: a credit gate was shown, in the context of the active task. */
export function trackCreditGateShown(projectId: string, activeTask: string | null) {
  if (!initialized) return;
  posthog.capture("credit_gate_shown", {
    project_id: projectId,
    active_task: activeTask,
  });
}

// ──────────────────────────────────────────────────
// Unfinished-build recovery emails (PANDA-1020)
//
// The cron emits the server side of the funnel (recovery_email_eligible /
// _queued / _sent) via lib/posthog/server. These three are the client side —
// fired from the project page when a user arrives on a recovery deep link
// (utm_campaign=recovery_*) and then continues building. Together they make the
// ticket's "click -> return -> action completion" funnel measurable.
//   email_type is the recovery type (first_build_recovery, build_not_published,
//   published_no_continuation, follow_up_24_48), parsed from the campaign.
// ──────────────────────────────────────────────────

interface RecoveryClickProps {
  project_id: string;
  email_type: string;
  campaign: string;
}

/** Track: user clicked a recovery email CTA (deep-link landed). */
export function trackRecoveryEmailClicked(props: RecoveryClickProps) {
  if (!initialized) return;
  posthog.capture("recovery_email_clicked", {
    ...props,
    ...getDeviceContext(),
    ...getUtmProperties(),
  });
}

/** Track: user returned to the project from a recovery email. Distinct from
 *  the click so a re-open of an already-loaded tab still counts as a return. */
export function trackUserReturnedFromEmail(props: RecoveryClickProps) {
  if (!initialized) return;
  posthog.capture("user_returned_from_email", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: user took a Build action (CTA completed) after a recovery return. */
export function trackEmailCtaActionCompleted(props: RecoveryClickProps) {
  if (!initialized) return;
  posthog.capture("email_cta_action_completed", {
    ...props,
    ...getDeviceContext(),
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Bad-experience recovery FIX — client verdict funnel.
//
// The server owns detection + offer events (lib/posthog/recovery-events.ts). But
// whether a free fix actually worked can only be judged on the client: it is the
// browser that sees the app compile and the preview come up. These four make the
// ticket's "did recovery really fix it?" measurable, keyed on the same event_id
// as the server funnel so the whole chain joins:
//   recovery_started         — the user redeemed the free fix (structured repair)
//   recovery_build_validated  — the repaired app compiled + the preview rendered
//   recovery_succeeded        — the fix turn ended with a working preview
//   recovery_failed           — it never compiled; we rolled back and said so
// ──────────────────────────────────────────────────────────────────────────

interface RecoveryFixProps {
  projectId: string;
  eventId: string;
  kind: string;
}

function recoveryFixProps(p: RecoveryFixProps) {
  return {
    event_id: p.eventId,
    project_id: p.projectId,
    bad_experience_kind: p.kind,
    ...getDeviceContext(),
  };
}

/** Track: the user redeemed the free fix; the structured repair turn is starting. */
export function trackRecoveryStarted(props: RecoveryFixProps) {
  if (!initialized) return;
  posthog.capture("recovery_started", recoveryFixProps(props));
}

/**
 * Track: the client withheld an offer the server had already served.
 *
 * READ THIS BEFORE READING THE RECOVERY FUNNEL. `recovery_offer_shown` fires
 * SERVER-side, inside the GET that builds the offer, so it counts offers that
 * the client then vetoed on the evidence in front of the user (a successful edit
 * had already landed). Every veto therefore inflates the impression denominator,
 * and click-through will appear to drop the day this ships even though the
 * numerator is untouched. The true impression count is
 *
 *     recovery_offer_shown − recovery_offer_suppressed{surface:"render"}
 *
 * `surface` separates the two vetoes, and the split is worth having: "render"
 * means the card never became actionable, while "click" means the user actually
 * reached for a fix we then declined — a strictly worse experience and the one to
 * watch. `reason` carries the RecoveryOfferSilence value, or "turn_running".
 */
export function trackRecoveryOfferSuppressed(
  props: RecoveryFixProps & { reason: string; surface: "render" | "click" },
) {
  if (!initialized) return;
  posthog.capture("recovery_offer_suppressed", {
    ...recoveryFixProps(props),
    reason: props.reason,
    surface: props.surface,
  });
}

/** Track: the recovered app compiled and the preview rendered — validated before
 *  any "it's fixed" is shown to the user. */
export function trackRecoveryBuildValidated(props: RecoveryFixProps) {
  if (!initialized) return;
  posthog.capture("recovery_build_validated", recoveryFixProps(props));
}

/** Track: the recovery fix turn ended with a working preview. */
export function trackRecoverySucceeded(props: RecoveryFixProps) {
  if (!initialized) return;
  posthog.capture("recovery_succeeded", recoveryFixProps(props));
}

/** Track: the recovery fix could not compile the app; it was rolled back to the
 *  last working version and the user was told the specific blocker. */
export function trackRecoveryFailed(props: RecoveryFixProps & { reason?: string }) {
  if (!initialized) return;
  posthog.capture("recovery_failed", {
    ...recoveryFixProps(props),
    reason: props.reason ?? null,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Continuation system — post-build "recommended next step" funnel.
// Server fires readiness_check_completed + ai_next_step_generated (the truth
// that an assessment ran). These client events measure the UI funnel on top:
// the CTA was shown, clicked, and — once the build it triggered finishes —
// completed. prompt_depth_updated marks each step of build progression so #2,
// #3, #5 are measurable from this surface.
// ──────────────────────────────────────────────────────────────────────────

/** Everything the next-step events carry. Ergonomic camelCase in; the events
 *  emit snake_case to match the rest of the analytics surface. */
export interface NextStepContext {
  projectId: string;
  projectCategory: string;
  /** The readiness item id being closed, e.g. "conversion_path". */
  missingItem: string;
  recommendationType: string;
  recommendationGoal: string;
  readinessScore: number;
  promptDepth: number;
}

/**
 * The clicked CTA's context, remembered until the build it kicks off completes.
 * The click and the completion render on two different messages, so a ref local
 * to one wouldn't survive — module scope is the natural home for this one-user,
 * fire-and-forget link. Cleared the moment it's consumed.
 */
let pendingNextStep: NextStepContext | null = null;

function nextStepProps(ctx: NextStepContext): Record<string, unknown> {
  return {
    project_id: ctx.projectId,
    project_category: ctx.projectCategory,
    missing_item: ctx.missingItem,
    recommendation_type: ctx.recommendationType,
    recommendation_goal: ctx.recommendationGoal,
    readiness_score: ctx.readinessScore,
    prompt_depth: ctx.promptDepth,
    source_surface: "chat",
    device_type: getDeviceContext().device_type,
  };
}

/** The "recommended next step" CTA rendered below the last assistant message. */
export function trackNextStepShown(ctx: NextStepContext) {
  if (!initialized) return;
  posthog.capture("next_step_shown", nextStepProps(ctx));
}

/** The user clicked the CTA to run the recommended next Build action. */
export function trackNextStepClicked(ctx: NextStepContext) {
  if (!initialized) return;
  pendingNextStep = ctx;
  posthog.capture("next_step_clicked", nextStepProps(ctx));
}

/**
 * Call when a build/edit finishes. If it was started from a next-step CTA on the
 * same project, fire next_step_completed (recommendation accepted) and, when the
 * score improved, readiness_progressed. No-ops when no CTA click is pending.
 */
export function trackNextStepCompleted(args: {
  projectId: string;
  readinessScore: number;
  promptDepth: number;
}) {
  if (!initialized) return;
  const pending = pendingNextStep;
  if (!pending || pending.projectId !== args.projectId) return;
  pendingNextStep = null;
  posthog.capture("next_step_completed", {
    project_id: args.projectId,
    project_category: pending.projectCategory,
    missing_item: pending.missingItem,
    recommendation_type: pending.recommendationType,
    recommendation_goal: pending.recommendationGoal,
    prompt_depth: args.promptDepth,
    was_recommendation_accepted: true,
    readiness_score_before: pending.readinessScore,
    readiness_score_after: args.readinessScore,
    device_type: getDeviceContext().device_type,
  });
  if (args.readinessScore > pending.readinessScore) {
    posthog.capture("readiness_progressed", {
      project_id: args.projectId,
      project_category: pending.projectCategory,
      readiness_score_before: pending.readinessScore,
      readiness_score_after: args.readinessScore,
      prompt_depth: args.promptDepth,
    });
  }
}

/** Build progression advanced — a new build/edit completed at this depth. */
export function trackPromptDepthUpdated(args: {
  projectId: string;
  promptDepth: number;
  readinessScore?: number | null;
}) {
  if (!initialized) return;
  posthog.capture("prompt_depth_updated", {
    project_id: args.projectId,
    prompt_depth: args.promptDepth,
    readiness_score: args.readinessScore ?? null,
  });
}

// ── Stripe setup guidance ──────────────────────────────────────────────────
//
// Stripe setup is the highest-intent monetization moment in the product and,
// until now, the least instrumented: we could see an Express account get
// created and (server-side) whether it ever went live, but nothing in between.
// So "users reach the modal and then feel lost" was a support observation with
// no number attached.
//
// These four events make the guidance itself measurable, and — critically —
// make COMPLETION sliceable by it. Every started/completed event carries
// `help_viewed` and `help_topics`, so the question the work exists to answer
// ("does explaining this actually get more merchants through Stripe?") is a
// breakdown, not a new experiment: completion rate where help_viewed is true
// vs false, on the same funnel.
//
// `surface` separates the desktop modal from the mobile sheet because they are
// different flows with different drop-off (mobile redirects the whole tab),
// and pooling them would hide whichever is worse.

/**
 * A user opened an in-modal help affordance — the "which one do I pick?" tip,
 * the country explainer, or the "what happens next" step list.
 *
 * Fires on OPEN only, not on close: the signal is that the user needed the
 * answer, and a close is as likely to mean "got it" as "useless".
 */
export function trackStripeSetupHelpOpened(props: {
  projectId: string | null;
  surface: StripeSetupSurface;
  topic: StripeHelpTopic;
  /** Where in the flow the user was — not_connected, onboarding_pending, … */
  flowState: string;
}) {
  if (!initialized) return;
  posthog.capture("stripe_setup_help_opened", {
    project_id: props.projectId,
    surface: props.surface,
    help_topic: props.topic,
    flow_state: props.flowState,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

/**
 * The video walkthrough was opened. Its own event rather than a `help_topic`
 * value on the one above, because it is the only help affordance that takes the
 * user OUT of the modal — its drop-off risk is real and must not be averaged
 * into the inline tips, which cost nothing to open.
 */
export function trackStripeSetupWalkthroughOpened(props: {
  projectId: string | null;
  surface: StripeSetupSurface;
  flowState: string;
}) {
  if (!initialized) return;
  posthog.capture("stripe_setup_walkthrough_opened", {
    project_id: props.projectId,
    surface: props.surface,
    help_topic: "walkthrough" satisfies StripeHelpTopic,
    flow_state: props.flowState,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

/** Help exposure carried on the funnel events, so completion is sliceable. */
interface StripeHelpExposure {
  /** Did this user open ANY help/walkthrough content before acting? */
  helpViewed: boolean;
  /** Which topics — deduped, so re-opening one tip doesn't inflate it. */
  helpTopics: readonly StripeHelpTopic[];
}

function stripeHelpProps(exposure: StripeHelpExposure) {
  return {
    help_viewed: exposure.helpViewed,
    help_topics: [...exposure.helpTopics],
    help_topic_count: exposure.helpTopics.length,
  };
}

/**
 * The user committed to a setup path — pressed "Set Up Stripe" (new account) or
 * "Connect Existing Stripe Account" (OAuth). The denominator for completion.
 *
 * `path` matters because the two are not comparable: creating an account means
 * a full Stripe onboarding form, connecting an existing one is a sign-in.
 */
export function trackStripeSetupStarted(
  props: {
    projectId: string | null;
    surface: StripeSetupSurface;
    path: StripeSetupPath;
    /** Chosen country for a new account; null when connecting an existing one. */
    country?: string | null;
  } & StripeHelpExposure,
) {
  if (!initialized) return;
  posthog.capture("stripe_setup_started", {
    project_id: props.projectId,
    surface: props.surface,
    setup_path: props.path,
    country: props.country ?? null,
    ...stripeHelpProps(props),
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

/**
 * Setup reached a connected account IN THIS SESSION — the numerator.
 *
 * Only fired on a transition observed while the modal was open (a user who
 * opens an already-connected modal is not a completion), so the rate this
 * supports is honest. Server-side connection truth still lives in
 * lib/posthog/*; this is the client half that knows about help exposure.
 */
export function trackStripeSetupCompleted(
  props: {
    projectId: string | null;
    surface: StripeSetupSurface;
    /** Null when the user returned to a completed account without our seeing
     *  which button started it (e.g. an OAuth round-trip that reloaded). */
    path?: StripeSetupPath | null;
    /** Stripe has actually enabled charges, vs connected-but-still-verifying. */
    chargesEnabled: boolean;
  } & StripeHelpExposure,
) {
  if (!initialized) return;
  posthog.capture("stripe_setup_completed", {
    project_id: props.projectId,
    surface: props.surface,
    setup_path: props.path ?? null,
    charges_enabled: props.chargesEnabled,
    ...stripeHelpProps(props),
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

// ── Stripe disconnect / replace (PANDA-1946) ───────────────────────────────
//
// The client half of the disconnect story. The server event
// (`payments_disconnected`) is the truth about what happened to the account;
// these two say what the USER did on the way there, which is the part that
// answers the questions this ticket came from: how many merchants abandon setup
// and then look for a way out, from which state, and on which surface.

/**
 * The user opened the disconnect confirmation. Fired on the trigger, before the
 * request — a large gap between this and the completion below means the
 * consequences copy is scaring people off, which is worth knowing.
 */
export function trackStripeDisconnectStarted(props: {
  projectId: string | null;
  surface: StripeSetupSurface;
  intent: "remove" | "replace";
  /** Flow state the trigger was pressed in — connected / onboarding_pending /
   *  disabled. Answers "where do merchants actually get stuck?". */
  flowState: string;
  linkedToApp: boolean;
}) {
  if (!initialized) return;
  posthog.capture("stripe_disconnect_started", {
    project_id: props.projectId,
    surface: props.surface,
    intent: props.intent,
    flow_state: props.flowState,
    linked_to_app: props.linkedToApp,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

/** The disconnect request came back. `succeeded` false means the user is still
 *  connected and looking at an error. */
export function trackStripeDisconnectCompleted(props: {
  projectId: string | null;
  surface: StripeSetupSurface;
  intent: "remove" | "replace";
  flowState: string;
  succeeded: boolean;
  /** Server's report of what happened to the account, when we got one. */
  outcome?: string | null;
  accountRetained?: boolean | null;
}) {
  if (!initialized) return;
  posthog.capture("stripe_disconnect_completed", {
    project_id: props.projectId,
    surface: props.surface,
    intent: props.intent,
    flow_state: props.flowState,
    succeeded: props.succeeded,
    outcome: props.outcome ?? null,
    account_retained: props.accountRetained ?? null,
    ...getDeviceContext(),
    environment: analyticsEnvironment(),
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Core in-product actions ("track key in-product actions in PostHog")
//
// Broad "user did X" signals for the central build/grow loop. They sit
// alongside the funnel-specific events above — those answer "did this funnel
// convert?", these answer "is the product being used at all?". The matching
// server-truth events live in lib/posthog/* (build/deploy pipeline, billing,
// connections). Keep every property snake_case to match the rest of the
// surface, and append getDeviceContext() so each action is segmentable by
// device without a join.
// ──────────────────────────────────────────────────────────────────────────

/**
 * Track: the user sent a message to the AI — the Build chat or the Grow chat.
 * Fires exactly once per committed send: after the online / credit / duplicate
 * guards and (in Build) gated on `!isResume` so the post-verification
 * auto-replay of an earlier turn doesn't double-count. `is_first_message`
 * marks the very first prompt of a project (the initial build that "build
 * started" tracks server-side). `from_suggestion` is true when the send came
 * from a one-tap suggestion / next-step chip rather than typed text.
 */
/**
 * PANDA-1164: the user asked Build chat to add real payments and we
 * short-circuited into the conversion flow (upgrade / connect Stripe / link
 * Stripe) instead of building a fake checkout. `plan` + the two Stripe flags
 * say which CTA state the user saw, so the funnel from "payment requested" to
 * "payments unlocked" is measurable.
 */
export function trackPaymentIntentGated(props: {
  projectId: string | null;
  plan: string;
  stripeConnected: boolean;
  stripeLinked: boolean;
}) {
  if (!initialized) return;
  posthog.capture("payment_intent_gated", {
    project_id: props.projectId,
    plan: props.plan,
    stripe_connected: props.stripeConnected,
    stripe_linked: props.stripeLinked,
    ...getDeviceContext(),
  });
}

/**
 * The user asked Build chat to cancel their subscription and we short-circuited
 * into the Settings → Billing routing reply instead of discussing it (Panda
 * cannot cancel a plan from chat). `variant` says which copy they saw — a Free
 * user is told there is nothing to cancel, a trial gets trial wording — so each
 * one's follow-through is measurable separately.
 *
 * Pairs with {@link trackSubscriptionCancelBillingOpened}: shown → opened is
 * the funnel that answers "did the routing actually route anyone?".
 */
export function trackSubscriptionCancelGuidanceShown(props: {
  projectId: string | null;
  plan: string;
  trialActive: boolean;
  variant: "trial" | "paid" | "free";
}) {
  if (!initialized) return;
  posthog.capture("subscription_cancel_guidance_shown", {
    project_id: props.projectId,
    plan: props.plan,
    trial_active: props.trialActive,
    variant: props.variant,
    surface: "build_chat",
    ...getDeviceContext(),
  });
}

/**
 * The user clicked the Billing link in that guidance — the second half of the
 * funnel above. Only the cancellation reply emits `panda://billing`, so this
 * event is unambiguous about where the click came from.
 */
export function trackSubscriptionCancelBillingOpened(props: {
  projectId: string | null;
}) {
  if (!initialized) return;
  posthog.capture("subscription_cancel_billing_opened", {
    project_id: props.projectId,
    surface: "build_chat",
    ...getDeviceContext(),
  });
}

export function trackAiMessageSent(props: {
  projectId: string | null;
  mode: "build" | "growth";
  /** Length of the user-VISIBLE message (what they typed or the CTA label) —
   *  never of an accepted recommendation's internal prompt, which is
   *  identified separately by the two recommendation_* properties. */
  messageLength: number;
  isFirstMessage: boolean;
  hasAttachment?: boolean;
  fromSuggestion?: boolean;
  templateId?: string | null;
  /** Id of the assistant message whose offered next step this send accepted
   *  (null for a normal send). */
  recommendationId?: string | null;
  /** How the recommendation was accepted: "typed_affirmation" ("sure"),
   *  "recommendation_cta" (the CTA label sent from the composer),
   *  "assistant_proposal" (a typed yes bound to the proposal in Panda's own
   *  last message), or "failed_turn_retry" (a typed push bound to the request
   *  a fresh failure told the user to send again). */
  recommendationSource?:
    | "typed_affirmation"
    | "recommendation_cta"
    | "assistant_proposal"
    | "failed_turn_retry"
    | null;
}) {
  if (!initialized) return;
  posthog.capture("ai_message_sent", {
    project_id: props.projectId,
    mode: props.mode,
    message_length: props.messageLength,
    is_first_message: props.isFirstMessage,
    has_attachment: props.hasAttachment ?? false,
    from_suggestion: props.fromSuggestion ?? false,
    template_id: props.templateId ?? null,
    recommendation_id: props.recommendationId ?? null,
    recommendation_source: props.recommendationSource ?? null,
    ...getDeviceContext(),
  });
}

/**
 * Track: the user clicked a suggestion chip — a one-tap prompt shortcut.
 * `chip_type` distinguishes the surface:
 *   "empty_state" — the starter prompts shown before the first build (these
 *                   only fill the composer; the send is tracked separately by
 *                   ai_message_sent once the user submits).
 *   "next_action" — the readiness-derived "what to build next" chips under an
 *                   assistant reply (the grey chips beside the next-step CTA).
 *                   These run the prompt immediately, so a click also produces
 *                   an ai_message_sent with from_suggestion: true.
 *   "follow_up"   — the server-generated chips rendered under an assistant
 *                   reply (Gemini Flash suggestions; the fallback when there
 *                   are no next_action chips). These only fill the composer.
 * The richer readiness next-step CTA (the gradient "what's next" button) keeps
 * its own next_step_clicked event and is intentionally NOT folded in here, so
 * the primary recommendation and the secondary chips stay independently
 * analysable.
 */
/**
 * TASK DRIFT — Panda was about to propose new work while the user still had an
 * unresolved bug open, and we suppressed it. Fires once per suppressed surface
 * per assistant turn.
 *
 * This is the counter-metric for the drift bug (a publish failure that was still
 * open while the readiness card offered an admin dashboard and the chips offered
 * testimonials). A HIGH count is healthy — it means the guard is catching drift.
 * `assistant_turns_since` is the severity dial: the longer Panda has been talking
 * without the user confirming a fix, the worse the drift would have been.
 */
export function trackTaskDriftSuppressed(props: {
  projectId: string | null;
  /** Which proactive surface we stopped from firing. */
  surface: "suggestion_chips" | "readiness_recommendation";
  /** What the user says is broken. Imported rather than re-spelled: a copy of
   *  the union goes stale the moment a kind is added, and the compiler is the
   *  only thing that would notice the dashboard had lost a category. */
  issueKind: IssueKind;
  /** Assistant turns since the report with no user confirmation of a fix. */
  /** WHAT THE ORCHESTRATOR READ. Replaced `assistantTurnsSince`, the input to
   *  a drift threshold that no longer exists (lib/ai/active-issue.ts). */
  contradicted: boolean;
}) {
  if (!initialized) return;
  posthog.capture("task_drift_suppressed", {
    project_id: props.projectId,
    surface: props.surface,
    issue_kind: props.issueKind,
    contradicted: props.contradicted,
    ...getDeviceContext(),
  });
}

export function trackSuggestionChipClicked(props: {
  projectId: string | null;
  /** "vague_prompt_option" is a concrete next step offered after a vague-scope
   *  request ("add more stuff") — the tap half of the
   *  vague_prompt_clarification_shown funnel.
   *  "readiness_review_option" is one of the numbered additions listed in a
   *  "what else can we add?" review. Both ride the same `clarifyOptions` chip
   *  strip, so they are separated here — pooling them would quietly inflate the
   *  vague-prompt funnel with taps that never came from a vague prompt. */
  chipType:
    | "empty_state"
    | "next_action"
    | "follow_up"
    | "vague_prompt_option"
    | "readiness_review_option"
    | "restyle_continuation_option"
    /** A next step offered under an edit that did not land. Separate from
     *  `vague_prompt_option` because it answers a different question: not "what
     *  did they want?" but "did the explanation give them something to do?" */
    | "apply_failure_option";
  label: string;
  mode?: "build" | "growth";
  /** True when the chip belonged to an OLDER suggestion set the user
   *  re-expanded (collapsed "Previous suggestions"), so active vs stale
   *  interactions can be told apart in the funnel. Defaults to false. */
  stale?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("suggestion_chip_clicked", {
    project_id: props.projectId,
    chip_type: props.chipType,
    label: props.label,
    label_length: props.label.length,
    mode: props.mode ?? "build",
    stale: props.stale ?? false,
    ...getDeviceContext(),
  });
}

/**
 * SUGGESTION-CHIP CONTINUITY — how a turn whose fresh chip fetch came back
 * empty was resolved (the continuity layer in ChatPanel's suggest effect).
 * Fires at most once per assistant turn, and ONLY on the non-happy paths —
 * a fresh fetch that simply returned chips is the baseline and is already
 * observable via the route's [suggest-debug] log and the click funnel.
 *
 *   • retry_recovered — the first fetch failed transiently; the single retry
 *     produced chips. A rising count means the primary fetch is flaky.
 *   • carried_forward — both attempts came back empty, but chips from a
 *     previous turn still passed the full guard against the CURRENT app and
 *     were promoted onto the new turn. This is the "no dead end while useful
 *     next actions remain" acceptance criterion, counted.
 *   • dead_end — nothing fresh, nothing carried. The strip fell through to
 *     the readiness fallback or rendered empty. A legitimate outcome when the
 *     app genuinely has no worthwhile next step — but the number to watch if
 *     "chips stopped appearing" reports come back.
 *
 * Impact is measured by joining `suggestion_chip_clicked` on `label`: a click
 * on a label this event reported as carried is a continuation the old
 * behaviour would have buried behind "Previous suggestions".
 */
export function trackSuggestionChipsContinuity(props: {
  projectId: string | null;
  outcome: "retry_recovered" | "carried_forward" | "dead_end";
  /** How many chips the outcome put (or left) on screen. Always 0 for
   *  dead_end; 1..2 otherwise. */
  chipCount: number;
  /** The carried chips' labels (carried_forward only) — what the click-side
   *  join matches against. Bounded: at most the strip's two labels. */
  labels?: string[];
}) {
  if (!initialized) return;
  posthog.capture("suggestion_chips_continuity", {
    project_id: props.projectId,
    outcome: props.outcome,
    chip_count: props.chipCount,
    labels: props.labels ?? [],
    ...getDeviceContext(),
  });
}

// ── Building streak + leaderboard rank ──────────────────────────────────
//
// The three VISUAL events of the streak feature — impressions and the one tap.
// The authoritative "streak went up / lapsed" events (streak_incremented,
// streak_reset) are server-side in lib/posthog/streak-events.ts, because the
// count is computed in the database, not the browser.

/**
 * Track: streak_viewed — the user saw their streak (nav badge / dashboard card
 * loaded with a live streak). Fires once per surface load, only when a streak is
 * actually shown, so it measures genuine exposure to the retention hook.
 */
export function trackStreakViewed(props: {
  currentStreakDays: number;
  userRank: number;
  planType: string;
}) {
  if (!initialized) return;
  posthog.capture("streak_viewed", {
    current_streak_days: props.currentStreakDays,
    user_rank: props.userRank,
    plan_type: props.planType,
    ...getDeviceContext(),
  });
}

/**
 * Track: leaderboard_rank_viewed — the user saw their rank on the dashboard rank
 * card. `percentile` is 0–100 (higher = nearer the top).
 */
export function trackLeaderboardRankViewed(props: {
  userRank: number;
  percentile: number;
}) {
  if (!initialized) return;
  posthog.capture("leaderboard_rank_viewed", {
    user_rank: props.userRank,
    percentile: props.percentile,
    ...getDeviceContext(),
  });
}

/**
 * Track: leaderboard_opened — the user opened the full leaderboard view.
 * `source` says what they tapped ("rank_card" | "streak_badge" | "streak_card").
 */
export function trackLeaderboardOpened(props: {
  userRank: number;
  totalRankedUsers: number;
  source: "rank_card" | "streak_badge" | "streak_card";
}) {
  if (!initialized) return;
  posthog.capture("leaderboard_opened", {
    user_rank: props.userRank,
    total_ranked_users: props.totalRankedUsers,
    source: props.source,
    ...getDeviceContext(),
  });
}

// ── Visual editor (no-prompt canvas editing) ────────────────────────────
//
// Instrumentation for Panda's Visual Editor so we can measure discovery,
// activation, which edit types are used, and where edits fail — WITHOUT ever
// capturing user content. Every event carries `editor_version`
// (VISUAL_EDITOR_VERSION) so metrics can be attributed to an editor
// generation. Deliberate omissions vs. the original spec: no `viewport_width`
// (device_type via getDeviceContext is the repo-wide segmentation, numeric
// viewport is not tracked anywhere) and no `route` (PostHog auto-captures the
// URL on every event via capture_pageview) — matching existing conventions.
//
// Privacy contract (audited): these events send only structural metadata —
// element TAG names ("h1"/"button"/"img"), action-type categories, op-kind
// counts, booleans and durations. They never send edited text, link targets,
// media URLs, colour values, or any other user/app content.

/**
 * Track: visual_editor_available — the Visual Editor entry became available to
 * the user (preview tab active with a live preview on a device that offers the
 * editor). Fires once per editor-availability window; the top of the funnel
 * that measures how many users ever DISCOVER the editor. `preview_ready` tells
 * a merely-visible entry apart from a fully editable one; `remote_preview`
 * marks the deployed direct-load path where opening must boot a VM first.
 */
export function trackVisualEditorAvailable(props: {
  projectId: string | null;
  previewReady: boolean;
  remotePreview: boolean;
}) {
  if (!initialized) return;
  posthog.capture("visual_editor_available", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    preview_ready: props.previewReady,
    remote_preview: props.remotePreview,
    ...getDeviceContext(),
  });
}

/**
 * Track: visual_editor_opened — the user entered edit mode on the preview
 * (Editor button). `booted_vm` marks the deployed-preview path where opening
 * the editor had to boot a sandbox first.
 */
export function trackVisualEditorOpened(props: {
  projectId: string | null;
  bootedVm?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("visual_editor_opened", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    booted_vm: props.bootedVm ?? false,
    ...getDeviceContext(),
  });
}

/**
 * Track: visual_editor_closed — edit mode exited. `unsaved_discarded` counts
 * pending element edits thrown away on exit (friction signal — a clean exit is
 * 0). `time_in_editor_ms` is the button-open → button-close dwell (null if the
 * session ended some other way). `used_prompt_after_editor` is true when the
 * user escalated an edit to Panda chat during the session (the editor couldn't
 * do it deterministically) — the follow-up signal that tells us whether the
 * editor is offloading prompt/credit usage or adding a step.
 */
export function trackVisualEditorClosed(props: {
  projectId: string | null;
  unsavedDiscarded: number;
  timeInEditorMs?: number | null;
  usedPromptAfterEditor?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("visual_editor_closed", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    unsaved_discarded: props.unsavedDiscarded,
    time_in_editor_ms:
      props.timeInEditorMs != null ? Math.round(props.timeInEditorMs) : null,
    used_prompt_after_editor: props.usedPromptAfterEditor ?? false,
    ...getDeviceContext(),
  });
}

/**
 * Track: visual_element_selected — an element was selected on the canvas.
 * `editable` reflects the selection-time determinism probe (source tag
 * resolved to a patchable JSX element); the false rate decides whether an
 * AI-assisted tier is ever worth building. `tag` is the element's DOM tag
 * (e.g. "h1"/"button") — structural metadata only, never its content.
 */
export function trackVisualElementSelected(props: {
  projectId: string | null;
  tag: string;
  hasSourceTag: boolean;
  editable: boolean;
}) {
  if (!initialized) return;
  posthog.capture("visual_element_selected", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    tag: props.tag,
    target_type: props.tag,
    has_source_tag: props.hasSourceTag,
    editable: props.editable,
  });
}

/**
 * Track: visual_editor_action_started — the user began an edit of a given
 * `action_type` (text / style / spacing / image / link / layout / component)
 * on an element. Deduped per (element, action_type) within a session, so it
 * counts DISTINCT action engagements, not keystrokes. `target_type` is the
 * element's DOM tag. This is the event that answers "which editor actions do
 * people actually use".
 */
export function trackVisualEditorActionStarted(props: {
  projectId: string | null;
  actionType: VisualEditorActionType;
  targetType: string;
}) {
  if (!initialized) return;
  posthog.capture("visual_editor_action_started", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    action_type: props.actionType,
    target_type: props.targetType,
    ...getDeviceContext(),
  });
}

/**
 * Track: visual_editor_action_previewed — a live preview of an edit was pushed
 * into the running app (the editor previews every edit instantly; there is no
 * separate Preview button). Deduped per (element, action_type) like
 * `_action_started`; because a link edit has no visible preview, started ≥
 * previewed and the gap surfaces non-visual edits or a broken preview bridge.
 */
export function trackVisualEditorActionPreviewed(props: {
  projectId: string | null;
  actionType: VisualEditorActionType;
  targetType: string;
}) {
  if (!initialized) return;
  posthog.capture("visual_editor_action_previewed", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    action_type: props.actionType,
    target_type: props.targetType,
    ...getDeviceContext(),
  });
}

/**
 * Track: visual_edits_saved — a batch of deterministic visual edits was
 * applied (one persist+remount round). op_counts keys: text, style-class,
 * style-inline, link, remove, duplicate, moveUp, moveDown. `success` is always
 * true (this fires only on a confirmed apply); `failed_count` records how many
 * edits in the batch could not be applied deterministically.
 */
export function trackVisualEditsSaved(props: {
  projectId: string | null;
  elementCount: number;
  opCounts: Record<string, number>;
  failedCount: number;
}) {
  if (!initialized) return;
  posthog.capture("visual_edits_saved", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    element_count: props.elementCount,
    op_counts: props.opCounts,
    op_total: Object.values(props.opCounts).reduce((a, b) => a + b, 0),
    failed_count: props.failedCount,
    success: true,
    ...getDeviceContext(),
  });
}

/**
 * Track: site_colors_available — the Site colours surface is reachable for
 * this session. The mobile arm matters most: phones had no editor entry of any
 * kind before this, so `trackVisualEditorAvailable` structurally never fired
 * there and mobile discovery had no top-of-funnel number at all.
 */
export function trackSiteColorsAvailable(props: {
  projectId: string | null;
  surface: "editor" | "mobile_sheet";
}) {
  if (!initialized) return;
  posthog.capture("site_colors_available", {
    project_id: props.projectId,
    surface: props.surface,
    editor_version: VISUAL_EDITOR_VERSION,
    ...getDeviceContext(),
  });
}

/** Track: site_colors_opened — the panel or sheet was opened, and from where.
 *  `chat_handoff` is the arm that closes the loop with
 *  site_color_routed_to_editor: routed minus opened is the drop-off the
 *  hand-off copy is responsible for. */
export function trackSiteColorsOpened(props: {
  projectId: string | null;
  surface: "editor" | "mobile_sheet" | "chat_handoff";
}) {
  if (!initialized) return;
  posthog.capture("site_colors_opened", {
    project_id: props.projectId,
    surface: props.surface,
    editor_version: VISUAL_EDITOR_VERSION,
    ...getDeviceContext(),
  });
}

/**
 * Track: site_colors_applied — a whole-site colour swap landed.
 * `skipped_count` is the honesty metric: files the engine refused to rewrite
 * because it could not prove the edit safe. A rising skip rate means the
 * matchers are meeting markup they don't understand, and it should be read
 * against file_count, not on its own.
 */
export function trackSiteColorsApplied(props: {
  projectId: string | null;
  surface: "editor" | "mobile_sheet";
  colorCount: number;
  fileCount: number;
  skippedCount: number;
  /** "site" for a whole-site swap; "template" | "page" | "component" when the
   *  swap was scoped to a page group, one page, or a component family. */
  scopeKind?: "site" | "template" | "page" | "component";
}) {
  if (!initialized) return;
  posthog.capture("site_colors_applied", {
    project_id: props.projectId,
    surface: props.surface,
    color_count: props.colorCount,
    file_count: props.fileCount,
    skipped_count: props.skippedCount,
    scope_kind: props.scopeKind ?? "site",
    editor_version: VISUAL_EDITOR_VERSION,
    ...getDeviceContext(),
  });
}

/**
 * Track: site_color_routed_to_editor — chat recognised a whole-site colour
 * request and handed it to the visual editor instead of attempting a full-site
 * edit (lib/ai/site-color-request.ts).
 *
 * The pair to watch is this against visual_editor_opened: the gate is only
 * worth its interruption if the people it stops actually land in the editor.
 * A rising count here with a flat opened-rate means the hand-off copy is not
 * carrying them, not that the routing is wrong.
 */
export function trackSiteColorRoutedToEditor(props: {
  projectId: string | null;
  /** Present when the hand-off was SCOPED to a page group / page rather than
   *  the whole site (lib/ai/scoped-color-request.ts). */
  scopeKind?: "template" | "page" | "component";
}) {
  if (!initialized) return;
  posthog.capture("site_color_routed_to_editor", {
    project_id: props.projectId,
    scope_kind: props.scopeKind ?? "site",
    editor_version: VISUAL_EDITOR_VERSION,
    ...getDeviceContext(),
  });
}

/** Track: visual_edit_undone — the post-save Undo pill was used (the ticket's
 *  "save reverted" signal). */
export function trackVisualEditUndone(props: { projectId: string | null }) {
  if (!initialized) return;
  posthog.capture("visual_edit_undone", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
  });
}

/**
 * Track: visual_edit_fallback — an element or edit could not take the free
 * deterministic path (the ticket's "action failed / validation error"). stage
 * "selection" = locked at select time (untagged / unlocatable); stage "save" =
 * a save-time miss (stale tag, dynamic className, keyed list…); stage
 * "chat_handoff" = the user clicked "Ask Panda in chat" from the locked/failed
 * state; stage "render" = the edit was saved but the reloaded page, measured,
 * does not render it (reason "not_rendered" — a stylesheet rule beat it).
 * `reason`/`error_code` is a stable failure slug (never a message);
 * `action_type` is the attempted edit's category when known (save stage), else
 * null. `success` is always false.
 */
export function trackVisualEditFallback(props: {
  projectId: string | null;
  stage: "selection" | "save" | "render" | "chat_handoff";
  reason: string;
  tag: string;
  actionType?: VisualEditorActionType | null;
}) {
  if (!initialized) return;
  posthog.capture("visual_edit_fallback", {
    project_id: props.projectId,
    editor_version: VISUAL_EDITOR_VERSION,
    stage: props.stage,
    reason: props.reason,
    error_code: props.reason,
    action_type: props.actionType ?? null,
    target_type: props.tag,
    tag: props.tag,
    success: false,
  });
}

// ── Operation coordination (PANDA-2261) ──────────────────────────────────
//
// One project, one high-impact operation at a time (lib/operations/
// single-flight.ts). These four events are the outcomes of that decision, and
// together they answer the question the disabled-button version of this fix
// could not: how often does the coordination actually FIRE, and is it helping
// or getting in the way?
//
// Read them as a set, not individually:
//   • `operation_merged` rising is the fix working — a redundant resume folded
//     into a build the user was already getting.
//   • `operation_queued` rising is also fine — a click honoured a moment late.
//   • `operation_conflict_blocked` is the only one that costs the user
//     anything, because it is the only outcome that throws the intent away.
//     If it climbs, a pair in the table wants to become a queue.
//   • `duplicate_action_ignored` is impatience, and a rising rate on one
//     `operation` usually means that control gives no feedback on first click.
//
// `active_operation` and `operation` are on every row so a board can group by
// the PAIR — which is the unit the policy is actually written in, and the unit
// a single "blocked" counter could never expose.

/** The requested action cannot run now and was not remembered. */
export function trackOperationConflictBlocked(props: {
  projectId: string | null;
  /** What was asked for. */
  operation: string;
  /** What was already running. */
  activeOperation: string;
  /** The plain-language sentence the user was shown. */
  reason: string;
}) {
  if (!initialized) return;
  posthog.capture("operation_conflict_blocked", {
    project_id: props.projectId,
    operation: props.operation,
    active_operation: props.activeOperation,
    reason: props.reason,
  });
}

/** The requested action was held and will run once the active one settles. */
export function trackOperationQueued(props: {
  projectId: string | null;
  operation: string;
  activeOperation: string;
  /** How many requests are waiting, including this one. A number above 1 means
   *  the queue is absorbing more than a single early click. */
  queueDepth: number;
}) {
  if (!initialized) return;
  posthog.capture("operation_queued", {
    project_id: props.projectId,
    operation: props.operation,
    active_operation: props.activeOperation,
    queue_depth: props.queueDepth,
  });
}

/** The active operation already delivers what was asked for. */
export function trackOperationMerged(props: {
  projectId: string | null;
  operation: string;
  /** The operation it folded into. */
  activeOperation: string;
  /** Why folding was the right answer — the table's own reason string. */
  reason: string;
}) {
  if (!initialized) return;
  posthog.capture("operation_merged", {
    project_id: props.projectId,
    operation: props.operation,
    active_operation: props.activeOperation,
    reason: props.reason,
  });
}

/** The same intent, arriving twice. Nothing was started. */
export function trackDuplicateActionIgnored(props: {
  projectId: string | null;
  operation: string;
  /** The idempotency key both clicks produced. */
  key: string;
}) {
  if (!initialized) return;
  posthog.capture("duplicate_action_ignored", {
    project_id: props.projectId,
    operation: props.operation,
    idempotency_key: props.key,
  });
}

// ── First-build hidden recovery (PANDA) ──────────────────────────────────
//
// A first build can recover from an internal hiccup and still hand the user a
// working preview. That is the system doing its job — but from every dashboard
// we had, a build that recovered silently and a build that never wobbled looked
// identical, so "how often does the happy path actually need rescuing?" had no
// answer. These events give it one, WITHOUT surfacing anything to the user: the
// design note is explicit that a successful build must not advertise its own
// recovery.
//
// Read as a pair with the existing refusal telemetry: a refusal followed by
// `build_recovered` is harmless latency, a refusal with no recovery is a build
// the user actually lost. Those are the two the ticket asks to tell apart.

/**
 * The initial candidate was refused before mount, and the retry then worked.
 *
 * `reason` is the refusal's own code (`output_truncated`, `missing_modules`,
 * `missing_api_route`) rather than a generic flag, so the split-file retry's
 * value can be measured per cause — the split-file guardrails are the remedy
 * for an oversized file, and firing them at an export-contract mismatch is a
 * different problem wearing the same log line.
 */
export function trackBuildRecovered(props: {
  projectId: string | null;
  /** Why the first candidate was refused. */
  reason: string;
  /** `single_pass` or `phased` — which retry path rescued it. */
  path: "single_pass" | "phased";
  /** Wall-clock cost of the recovery, which is what the user actually pays. */
  recoveryMs: number;
  /** Files the refusal named. Bounded — this is a signal, not a manifest. */
  paths?: readonly string[] | null;
}) {
  if (!initialized) return;
  posthog.capture("build_recovered", {
    project_id: props.projectId,
    reason: props.reason,
    recovery_path: props.path,
    recovery_ms: Math.round(props.recoveryMs),
    paths: props.paths?.slice(0, 10) ?? null,
    path_count: props.paths?.length ?? 0,
  });
}

/**
 * A GitHub push that failed and then succeeded on a later attempt.
 *
 * Fired only when `attempts > 1`, so this counts rescues rather than pushes. A
 * rising rate on one `code` is the signal that a transient class has stopped
 * being transient.
 */
export function trackGitHubPushRecovered(props: {
  projectId: string | null;
  attempts: number;
  code: string | null;
}) {
  if (!initialized) return;
  posthog.capture("github_push_recovered", {
    project_id: props.projectId,
    attempts: props.attempts,
    failure_code: props.code,
  });
}

/**
 * A GitHub push the client stopped retrying.
 *
 * The push is deliberately fire-and-forget — the preview must never wait on it
 * — which is exactly why its failure needs an event: nothing downstream
 * notices, and before this the only trace was a `console.warn` in a browser
 * nobody was watching. Both reported first-build sessions failed here and the
 * only reason anyone knows is that someone read the console.
 *
 * `retryable` records whether the loop CHOSE not to retry (a permission or auth
 * state that will not change in six seconds) or ran out of attempts. Those are
 * different failures and only the second one argues for a longer ladder.
 */
export function trackGitHubPushGaveUp(props: {
  projectId: string | null;
  attempts: number;
  code: string | null;
  status: number | null;
  retryable: boolean;
}) {
  if (!initialized) return;
  posthog.capture("github_push_gave_up", {
    project_id: props.projectId,
    attempts: props.attempts,
    failure_code: props.code,
    http_status: props.status,
    was_retryable: props.retryable,
  });
}

// ── The persistence lifecycle, as one traceable thread ───────────────────────
//
// The report asks for exactly this: "Telemetry must link the edit run, local
// preview revision, persistence attempt, Retry action, retry result, durable
// project version, sync state, and any later loss on reload."
//
// It could not, because a failed save produced one `console.warn` inside the
// tab it happened in and nothing else. The user's work then died on a hard
// refresh with no event anywhere marking that it had existed — so the loss the
// ticket describes is, in the product data, indistinguishable from a user who
// simply changed their mind.
//
// `pending_id` is the thread. It is minted when the unsaved set is written to
// the device (lib/db/pending-save-store.ts) and carried through every event
// below, so one query can follow a change from the edit that made it to the
// version that finally stored it — or to the reload where it was thrown away.

/** An edit's file set was stored locally because the save did not land. */
export function trackSavePendingStored(props: {
  projectId: string;
  pendingId: string;
  code: string | null;
  fileCount: number;
  /** Distinguishes an expired session from a database fault at a glance. */
  reason: "sync" | "auth" | "oversize";
}) {
  if (!initialized) return;
  posthog.capture("save_pending_stored", {
    project_id: props.projectId,
    pending_id: props.pendingId,
    code: props.code,
    file_count: props.fileCount,
    reason: props.reason,
  });
}

/** A stored set finally reached the database, and which durable version took it. */
export function trackSavePendingResolved(props: {
  projectId: string;
  pendingId: string;
  attempts: number;
  snapshotId: string | null;
  /** Present when the flush came from opening the project, not from Retry. */
  onLoad?: boolean;
}) {
  if (!initialized) return;
  posthog.capture("save_pending_resolved", {
    project_id: props.projectId,
    pending_id: props.pendingId,
    attempts: props.attempts,
    snapshot_id: props.snapshotId,
    on_load: props.onLoad ?? false,
  });
}

/** A flush attempt did not land. `attempts` accumulates across sessions. */
export function trackSavePendingFailed(props: {
  projectId: string;
  pendingId: string;
  attempts: number;
  code: string | null;
}) {
  if (!initialized) return;
  posthog.capture("save_pending_failed", {
    project_id: props.projectId,
    pending_id: props.pendingId,
    attempts: props.attempts,
    code: props.code,
  });
}

/**
 * A project opened holding work a previous session never stored.
 *
 * The event the reported loss had no equivalent of. `diverged` says whether the
 * project was also saved somewhere else in the meantime, which is the case that
 * cannot be resolved by replaying and has to be put to the user.
 */
export function trackSavePendingFound(props: {
  projectId: string;
  pendingId: string;
  attempts: number;
  ageMs: number;
  diverged: boolean;
}) {
  if (!initialized) return;
  posthog.capture("save_pending_found", {
    project_id: props.projectId,
    pending_id: props.pendingId,
    attempts: props.attempts,
    age_ms: props.ageMs,
    diverged: props.diverged,
  });
}

/** The user chose to throw the stored set away. The only sanctioned loss. */
export function trackSavePendingDiscarded(props: {
  projectId: string;
  pendingId: string;
  ageMs: number;
}) {
  if (!initialized) return;
  posthog.capture("save_pending_discarded", {
    project_id: props.projectId,
    pending_id: props.pendingId,
    age_ms: props.ageMs,
  });
}

// ──────────────────────────────────────────────────
// Bug reporting — the floating "Report a bug" tab
//
// Four events, one funnel: the tab was opened, a report was sent, a send
// failed, a failed send was retried. What we want out of it is whether people
// who open the panel actually finish, and whether the ones who don't are
// hitting an error or simply changing their mind.
// ──────────────────────────────────────────────────

/** Track: the user opened the bug-reporting panel from the edge tab. */
export function trackBugReportOpened(props: {
  project_id: string | null;
  route: string | null;
}) {
  if (!initialized) return;
  posthog.capture("bug_report_opened", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the user closed the panel without sending. `had_text` separates a
 *  glance from an abandoned report. */
export function trackBugReportDismissed(props: {
  project_id: string | null;
  had_text: boolean;
}) {
  if (!initialized) return;
  posthog.capture("bug_report_dismissed", props);
}

/** Track: a report reached us. `duplicate` marks a retry the server deduped. */
export function trackBugReportSubmitted(props: {
  project_id: string | null;
  route: string | null;
  has_screenshot: boolean;
  has_steps: boolean;
  attempt: number;
  duplicate: boolean;
}) {
  if (!initialized) return;
  posthog.capture("bug_report_submitted", {
    ...props,
    ...getDeviceContext(),
  });
}

/** Track: the submission failed and the user was shown the retry state. */
export function trackBugReportFailed(props: {
  project_id: string | null;
  status: number | null;
  reason: string;
  attempt: number;
}) {
  if (!initialized) return;
  posthog.capture("bug_report_failed", props);
}

/**
 * Track: image_size_observed — one Panda photo the editor preview measured
 * drawn soft or in a wide slot (lib/images/image-size-diagnosis.ts), once per
 * photo and outcome per session.
 *
 * READ ONLY: nothing acts on it. It is the production half of the
 * `[image-size]` trail — the owner's console is out of reach, so this is how a
 * "the hero looks blurry" report is answered: `outcome` says whether the file
 * was below the 1920px target, `asset_hash` joins it to the server's
 * `[image-size] resolve` line for the same photo.
 */
export function trackImageSizeObserved(props: {
  project_id: string;
  route: string;
  asset_hash: string;
  outcome: string;
  kind: string;
  natural_width: number;
  natural_height: number;
  box_width: number;
  box_height: number;
  viewport_width: number;
  scale: number;
  device_pixel_ratio: number;
  /** Canvas source tag (`app/page.tsx:42:7`) — a code location, never content. */
  source: string | null;
}) {
  if (!initialized) return;
  posthog.capture("image_size_observed", props);
}
